import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { apiClient, isApiError } from '../../../lib/apiClient';
import { fetchLatestWeeklyReportPublicSettings } from '../../../api/weeklyReportPublicSettings';
import { subscribeWeeklyReportPublicScopeRefresh } from '../../../lib/weeklyReportPublicScopeRefresh';
import { shareInFlightRequest } from '../../../lib/sharedApiRequest';
import {
  buildWeeklySearchParams,
  fetchWeeklyReports,
  fetchWeeklyReportsByDepartmentScopesAdaptively,
} from '../../../api/weeklySearch';
import {
  CREATOR_SYNC_BACKGROUND_INTERVAL_MS,
} from '../../../lib/creatorOptionsIndexedDb';
import { useProgressiveCreatorOptions } from '../../../hooks/useProgressiveCreatorOptions';
import {
  CREATOR_OPTION_DISPLAY_LIMIT,
  CREATOR_SEARCH_RESULT_LIMIT,
} from '../../../constants/creatorOptions';
import { CLASSIFICATION_MARKET_TARGET_ATTR_NAME } from '../../../constants';
import {
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Tooltip,
  Box,
  Snackbar,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { ja } from 'date-fns/locale/ja';
import { startOfWeek, addDays } from 'date-fns';

import MultiSelectCheckbox, {
  Option,
} from '../../common/MultiSelectCheckbox/MultiSelectCheckbox';
import MultiSelectWithCheckboxes from '../../common/MultiSelectWithCheckboxes/MultiSelectWithCheckboxes';
import './SearchArea.css';
import {
  compareWeeklyReports,
  buildTeamSortIndexMap,
  attachKanaForWeeklyList,
} from '../../common/utils/weeklySort';

import {
  Report,
  MasterResponse,
  MastersContextValue,
  Suggestion,
} from './WeeklyMasterProvider';

import {
  CREATE_BY_NONE_PARAM,
  CRLF,
  EMAIL_BODY_COLOR,
  EMAIL_FEEDBACK_COLOR,
  EMAIL_FONT_FAMILY,
  EMAIL_HEADER_ANGLE_CLOSE,
  EMAIL_HEADER_ANGLE_OPEN,
  EMAIL_HEADER_BRACKET_CLOSE,
  EMAIL_HEADER_BRACKET_OPEN,
  EMAIL_HEADER_LABEL_AUTHOR,
  EMAIL_HEADER_LABEL_RESPONDENTS,
  EMAIL_HEADER_LABEL_TEAM,
  EMAIL_HEADER_PAREN_CLOSE,
  EMAIL_HEADER_PAREN_OPEN,
  EMAIL_HEADER_RESPONDENTS_DELIM,
  EMAIL_HEADER_RESPONDENTS_SEPARATOR,
  EMAIL_HEADER_SEPARATOR,
  EMAIL_TITLE_COLOR,
  MAIL_SUBJECT_BASE,
  APP_EMAIL_BODY_WRAP_CHARS,
  MAIL_FORMAT_PRESET_APP,
  MAIL_FORMAT_PRESET_NO_WRAP,
  SEARCH_PARAM_KEYS,
  START_DATE_PRESET_PARAM,
  WEEKLY_REPORT_UPDATED_AT_KEY,
  WEEKLY_REPORT_UPDATED_EVENT,
  WEEKLY_SEARCH_CREATOR_OPTIONS_CACHE_KEY_PREFIX,
  WEEKLY_SEARCH_CREATOR_OPTIONS_CACHE_VERSION,
  WEEKLY_SEARCH_SCREEN_CACHE_KEY_LATEST,
  WEEKLY_SEARCH_SCREEN_CACHE_KEY_PREFIX,
  WEEKLY_SEARCH_SCREEN_CACHE_VERSION,
  WEEKLY_SEARCH_MAIL_FORMAT_CACHE_KEY_LATEST,
  WEEKLY_SEARCH_SCREEN_RESULTS_CACHE_KEY_PREFIX,
  WEEKLY_SEARCH_SCREEN_RESULTS_CACHE_VERSION,
} from './SearchArea.constants';
import {
  buildWeeklyReportAccessMap,
  getWeeklyReportRegisteredDepartmentRawValues,
  isAccessAllowedByEffectiveAccess,
  resolveEffectiveAccessForReportTypeValue,
  resolveReportTypeAccessOptions,
  type EffectiveReportTypeAccess,
  type ReportTypeAccessSetting,
} from './reportTypeAccess';
import { clearCreatorSelectionWhenResolvedEmpty } from './creatorSelection';
import {
  buildWeeklyReportVisibilityRecoveryParams,
  mergeWeeklyReportRowsWithVisibilityRecovery,
} from './visibilityRecovery';

import { useUser } from '../../../context/UserContext';
import { useUserProfile } from '../../../context/UserProfileContext';
import {
  buildUserDepCandidates,
  canAccessWeeklyReportByScope,
  normalizeDepartmentId,
  normalizeScopeCode,
  normalizeTeamId,
  resolveDepartmentIdFromValue,
  type ReportTypeVisibility,
  type VisibilityDepartmentContext,
} from '../../../lib/weeklyReportOrganizationAccess';
import {
  ACCESS_CONTEXT_UPDATED_AT_KEY,
  ACCESS_CONTEXT_UPDATED_EVENT,
  getLatestWeeklyDataInvalidationMs,
  isWeeklyDataCacheStale,
} from '../../../lib/accessContextUpdate';
import { buildWeeklyViewerAccessContextKey } from '../../../lib/weeklyViewerAccessContext';

const buildUserScopedCacheKey = (prefix: string, loginId: string | null) =>
  `${prefix}::${loginId ?? 'unknown'}`;

const readFirstAvailableLocalStorage = (keys: string[]): { key: string; value: string } | null => {
  for (const key of keys) {
    try {
      const value = localStorage.getItem(key);
      if (value) return { key, value };
    } catch {
      // ignore localStorage errors
    }
  }
  return null;
};
/* =========================================================
 * 週報更新通知（入力修正 → 週報連へ反映）
 * - 同一タブ: CustomEvent
 * - 別タブ: storage event
 * ======================================================= */

type HandleSearchOptions = {
  reason?: string;
};
type DateSearchMode = 'recent' | 'range';

const RECENT_PRESET_ORDER = ['last', 'this', 'next'] as const;
type RecentPreset = (typeof RECENT_PRESET_ORDER)[number];

const isRecentPreset = (value: unknown): value is RecentPreset =>
  value === 'this' || value === 'last' || value === 'next';

const RECENT_PRESET_TONE: Record<
  RecentPreset,
  {
    surface: string;
    border: string;
    buttonSurface: string;
    buttonBorder: string;
    buttonHoverSurface: string;
    buttonHoverBorder: string;
  }
> = {
  last: {
    surface: '#f8fafc',
    border: '#dbe3ee',
    buttonSurface: '#ffffff',
    buttonBorder: '#cbd5e1',
    buttonHoverSurface: '#f1f5f9',
    buttonHoverBorder: '#94a3b8',
  },
  this: {
    surface: '#dbeafe',
    border: '#93c5fd',
    buttonSurface: '#bfdbfe',
    buttonBorder: '#60a5fa',
    buttonHoverSurface: '#93c5fd',
    buttonHoverBorder: '#3b82f6',
  },
  next: {
    surface: '#93c5fd',
    border: '#3b82f6',
    buttonSurface: '#60a5fa',
    buttonBorder: '#2563eb',
    buttonHoverSurface: '#3b82f6',
    buttonHoverBorder: '#1d4ed8',
  },
};

function isReactMouseEvent(arg: any): boolean {
  return !!arg && typeof arg === 'object' && 'nativeEvent' in arg;
}

function pickHandleSearchOptions(arg: any): HandleSearchOptions | null {
  if (!arg || typeof arg !== 'object') return null;
  if (isReactMouseEvent(arg)) return null;
  if ('reason' in arg) return arg as HandleSearchOptions;
  return null;
}

function safeParseIsoToMs(iso: string | null): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

function readWeeklyReportUpdatedAtMs(): number | null {
  try {
    return safeParseIsoToMs(localStorage.getItem(WEEKLY_REPORT_UPDATED_AT_KEY));
  } catch {
    return null;
  }
}

function readLatestWeeklyDataInvalidationMs(): number {
  return getLatestWeeklyDataInvalidationMs(readWeeklyReportUpdatedAtMs());
}

function readResultsCacheSavedAtMs(
  resultsCacheKey: string,
  accessContextKey: string,
  expectedQuery?: string | null
): number | null {
  try {
    const payload = readResultsCachePayload(resultsCacheKey);
    if (!payload) return null;

    if (expectedQuery) {
      const entry = findResultsCacheEntryByQuery(
        payload,
        expectedQuery,
        accessContextKey
      );
      return safeParseIsoToMs(entry?.savedAt ?? null);
    }

    const savedTimes = getResultsCacheEntries(payload)
      .filter((entry) => entry.accessContextKey === accessContextKey)
      .map((entry) => safeParseIsoToMs(entry.savedAt))
      .filter((ms): ms is number => ms != null);

    if (savedTimes.length === 0) return null;
    return Math.max(...savedTimes);
  } catch {
    return null;
  }
}

const MAIL_FORMAT_PRESETS = [
  MAIL_FORMAT_PRESET_APP,
  MAIL_FORMAT_PRESET_NO_WRAP,
] as const;

const LEGACY_MAIL_FORMAT_PRESETS = ['default', 'xd4', 'xq4'] as const;

type MailFormatPreset = (typeof MAIL_FORMAT_PRESETS)[number];

type MailFormatState = {
  includeFeedback: boolean;
  preset: MailFormatPreset;
};

const DEFAULT_MAIL_FORMAT_STATE: MailFormatState = {
  includeFeedback: true,
  preset: MAIL_FORMAT_PRESET_APP,
};

type MailFormatPresetOption = {
  value: MailFormatPreset;
  label: string;
  helper: string;
};

const MAIL_FORMAT_PRESET_OPTIONS: MailFormatPresetOption[] = [
  {
    value: MAIL_FORMAT_PRESET_APP,
    label: 'APPを元に改行',
    helper: '現在の画面幅に合わせてタイトル・本文・FBを折り返します',
  },
  {
    value: MAIL_FORMAT_PRESET_NO_WRAP,
    label: '改行なし',
    helper: '入力済み改行も1行にまとめて横長に出力します',
  },
];

function isMailFormatPreset(raw: unknown): raw is MailFormatPreset {
  return MAIL_FORMAT_PRESETS.some((preset) => preset === raw);
}

function mapLegacyMailFormatPreset(raw: unknown): MailFormatPreset | null {
  if (!LEGACY_MAIL_FORMAT_PRESETS.some((preset) => preset === raw)) return null;
  if (raw === 'xd4') return MAIL_FORMAT_PRESET_NO_WRAP;
  return MAIL_FORMAT_PRESET_APP;
}

function normalizeMailFormatState(raw: unknown): MailFormatState {
  if (!raw || typeof raw !== 'object') {
    return DEFAULT_MAIL_FORMAT_STATE;
  }

  const obj = raw as Record<string, unknown>;
  const includeFeedbackRaw = obj.includeFeedback;
  const includeFeedbackFromNumber =
    typeof includeFeedbackRaw === 'number'
      ? includeFeedbackRaw !== 0
      : null;
  const includeFeedback =
    includeFeedbackFromNumber ??
    parseBooleanLike(includeFeedbackRaw) ??
    DEFAULT_MAIL_FORMAT_STATE.includeFeedback;
  const preset =
    (isMailFormatPreset(obj.preset) ? obj.preset : null) ??
    mapLegacyMailFormatPreset(obj.preset) ??
    DEFAULT_MAIL_FORMAT_STATE.preset;

  return {
    includeFeedback,
    preset,
  };
}

const MAIL_FORMAT_CACHE_VERSION = 1;

type WeeklySearchMailFormatCache = {
  version: number;
  savedAt: string;
  mailFormatState: MailFormatState;
};

function readMailFormatStateFromLocalStorage(): MailFormatState | null {
  try {
    const raw = localStorage.getItem(WEEKLY_SEARCH_MAIL_FORMAT_CACHE_KEY_LATEST);
    if (!raw) return null;
    const parsed = safeJsonParse<WeeklySearchMailFormatCache>(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.version !== 'number') return null;
    if (parsed.version > MAIL_FORMAT_CACHE_VERSION) return null;
    return normalizeMailFormatState((parsed as any).mailFormatState);
  } catch {
    return null;
  }
}

type WeeklySearchScreenCache = {
  version: number;
  savedAt: string;
  startDate: string | null; // yyyy-mm-dd
  endDate: string | null; // yyyy-mm-dd
  freeWord: string;
  searchTitleBody?: boolean;
  searchTags?: boolean;
  selectedReportTypes: string[];
  selectedTeams: string[];
  selectedCreateBy: string[];
  selectedDeptIds: string[];
  deptTouched: boolean;

  // 最後に検索した情報（戻ったときの自動検索/復元に使う）
  lastSearchAt: string | null;
  lastSearchQuery: string | null;
  // 記入者フィルタをユーザーが触ったか（条件なし含む）
  createByTouched: boolean;
  // 開始日付プリセット（今週/先週/来週）のチェック状態
  startDatePreset?: 'this' | 'last' | 'next' | null;
  // wr_id 指定検索（戻り時に再検索するため）
  lastWrId?: string | null;
  // メール整形設定（FB有無 + 課別プリセット）
  mailFormatState?: MailFormatState;

};

//  ：検索結果キャッシュ
type WeeklySearchScreenResultsCacheEntry = {
  savedAt: string;
  query: string; // URLSearchParams.toString()
  accessContextKey: string;
  results: Report[];
};

type WeeklySearchScreenResultsCache = WeeklySearchScreenResultsCacheEntry & {
  version: number;
  entries?: WeeklySearchScreenResultsCacheEntry[];
};

const WEEKLY_SEARCH_RESULTS_CACHE_MAX_ENTRIES = 3;
const WEEKLY_SEARCH_RESULTS_CACHE_MAX_ROWS = 300;
const WEEKLY_SEARCH_RESULTS_CACHE_MAX_CHARS = 1_500_000;
// Access-controlled report bodies are revalidated by the API before display.
// Search conditions may be cached, but persisted result rows must not be used
// as the source of truth after reload/navigation.
const ENABLE_PERSISTED_WEEKLY_RESULT_CACHE = false;

type WeeklySearchCreatorOptionsCache = {
  version: number;
  savedAt: string;
  options: Array<{
    id: string;
    value: string;
    department_id?: string;
    user_id?: string | null;
    legacy?: boolean;
  }>;
};

function safeJsonParse<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function normalizeQueryString(raw: string): string {
  try {
    const sp = new URLSearchParams(raw);
    const pairs: Array<[string, string]> = [];
    sp.forEach((v, k) => {
      pairs.push([k, v]);
    });

    pairs.sort((a, b) => {
      if (a[0] !== b[0]) return a[0].localeCompare(b[0]);
      return a[1].localeCompare(b[1]);
    });

    const out = new URLSearchParams();
    pairs.forEach(([k, v]) => out.append(k, v));
    return out.toString();
  } catch {
    return raw;
  }
}

function parseResultsCachePayload(raw: string | null): WeeklySearchScreenResultsCache | null {
  if (!raw) return null;

  const parsed = safeJsonParse<WeeklySearchScreenResultsCache>(raw);
  if (
    !parsed ||
    typeof parsed !== 'object' ||
    parsed.version !== WEEKLY_SEARCH_SCREEN_RESULTS_CACHE_VERSION
  ) {
    return null;
  }

  return parsed;
}

function normalizeResultsCacheEntry(raw: unknown): WeeklySearchScreenResultsCacheEntry | null {
  if (!raw || typeof raw !== 'object') return null;

  const entry = raw as Partial<WeeklySearchScreenResultsCacheEntry>;
  if (
    typeof entry.savedAt !== 'string' ||
    typeof entry.query !== 'string' ||
    typeof entry.accessContextKey !== 'string' ||
    entry.accessContextKey.length === 0 ||
    !Array.isArray(entry.results)
  ) {
    return null;
  }

  return {
    savedAt: entry.savedAt,
    query: entry.query,
    accessContextKey: entry.accessContextKey,
    results: entry.results,
  };
}

function getResultsCacheEntries(
  payload: WeeklySearchScreenResultsCache | null
): WeeklySearchScreenResultsCacheEntry[] {
  if (!payload) return [];

  const candidates: WeeklySearchScreenResultsCacheEntry[] = [];
  const latestEntry = normalizeResultsCacheEntry(payload);
  if (latestEntry) candidates.push(latestEntry);

  if (Array.isArray(payload.entries)) {
    payload.entries.forEach((entry) => {
      const normalized = normalizeResultsCacheEntry(entry);
      if (normalized) candidates.push(normalized);
    });
  }

  const byQuery = new Map<string, WeeklySearchScreenResultsCacheEntry>();
  candidates.forEach((entry) => {
    const key = `${entry.accessContextKey}::${normalizeQueryString(entry.query)}`;
    const existing = byQuery.get(key);
    const entrySavedAt = safeParseIsoToMs(entry.savedAt) ?? 0;
    const existingSavedAt = safeParseIsoToMs(existing?.savedAt ?? null) ?? 0;
    if (!existing || entrySavedAt >= existingSavedAt) {
      byQuery.set(key, entry);
    }
  });

  return Array.from(byQuery.values()).sort(
    (a, b) => (safeParseIsoToMs(b.savedAt) ?? 0) - (safeParseIsoToMs(a.savedAt) ?? 0)
  );
}

function readResultsCachePayload(cacheKey: string): WeeklySearchScreenResultsCache | null {
  try {
    return parseResultsCachePayload(localStorage.getItem(cacheKey));
  } catch {
    return null;
  }
}

function findResultsCacheEntryByQuery(
  payload: WeeklySearchScreenResultsCache | null,
  query: string,
  accessContextKey: string
): WeeklySearchScreenResultsCacheEntry | null {
  const expected = normalizeQueryString(query);
  return (
    getResultsCacheEntries(payload).find(
      (entry) =>
        entry.accessContextKey === accessContextKey &&
        normalizeQueryString(entry.query) === expected
    ) ?? null
  );
}

function isResultsCacheEntryStale(entry: WeeklySearchScreenResultsCacheEntry): boolean {
  return isWeeklyDataCacheStale(
    entry.savedAt,
    readWeeklyReportUpdatedAtMs()
  );
}

function isResultsCacheEntryCacheable(entry: WeeklySearchScreenResultsCacheEntry): boolean {
  if (entry.results.length > WEEKLY_SEARCH_RESULTS_CACHE_MAX_ROWS) return false;

  try {
    return JSON.stringify(entry).length <= WEEKLY_SEARCH_RESULTS_CACHE_MAX_CHARS;
  } catch {
    return false;
  }
}

function writeResultsCacheEntries(
  cacheKey: string,
  entries: WeeklySearchScreenResultsCacheEntry[]
): void {
  const normalizedEntries = entries
    .map((entry) => normalizeResultsCacheEntry(entry))
    .filter((entry): entry is WeeklySearchScreenResultsCacheEntry => Boolean(entry))
    .slice(0, WEEKLY_SEARCH_RESULTS_CACHE_MAX_ENTRIES);

  if (normalizedEntries.length === 0) {
    localStorage.removeItem(cacheKey);
    return;
  }

  const latest = normalizedEntries[0];
  const payload: WeeklySearchScreenResultsCache = {
    version: WEEKLY_SEARCH_SCREEN_RESULTS_CACHE_VERSION,
    savedAt: latest.savedAt,
    query: latest.query,
    accessContextKey: latest.accessContextKey,
    results: latest.results,
    entries: normalizedEntries,
  };

  try {
    localStorage.setItem(cacheKey, JSON.stringify(payload));
  } catch {
    const fallbackPayload: WeeklySearchScreenResultsCache = {
      version: WEEKLY_SEARCH_SCREEN_RESULTS_CACHE_VERSION,
      savedAt: latest.savedAt,
      query: latest.query,
      accessContextKey: latest.accessContextKey,
      results: latest.results,
      entries: [latest],
    };
    localStorage.setItem(cacheKey, JSON.stringify(fallbackPayload));
  }
}

function writeResultsCacheEntry(
  cacheKey: string,
  nextEntry: WeeklySearchScreenResultsCacheEntry
): void {
  const currentEntries = getResultsCacheEntries(readResultsCachePayload(cacheKey));
  const nextQuery = normalizeQueryString(nextEntry.query);
  const withoutSameQuery = currentEntries.filter(
    (entry) =>
      entry.accessContextKey !== nextEntry.accessContextKey ||
      normalizeQueryString(entry.query) !== nextQuery
  );

  if (!isResultsCacheEntryCacheable(nextEntry)) {
    writeResultsCacheEntries(cacheKey, withoutSameQuery);
    return;
  }

  writeResultsCacheEntries(cacheKey, [nextEntry, ...withoutSameQuery]);
}

function isWrIdOnlySearchParams(searchParams: URLSearchParams): boolean {
  const keys = Array.from(searchParams.keys());
  return Boolean(searchParams.get('wr_id')) && keys.length === 1 && keys[0] === 'wr_id';
}

function mergeSearchConditionsIntoCurrentRouteParams(
  currentRouteParams: URLSearchParams,
  nextSearchParams: URLSearchParams
): URLSearchParams {
  const merged = new URLSearchParams(currentRouteParams);

  SEARCH_PARAM_KEYS.forEach((key) => merged.delete(key));

  nextSearchParams.forEach((value, key) => {
    merged.append(key, value);
  });

  return merged;
}

function buildNonSearchParamsFingerprint(params: URLSearchParams): string {
  const nonSearchParams = new URLSearchParams(params);

  SEARCH_PARAM_KEYS.forEach((key) => nonSearchParams.delete(key));

  return normalizeQueryString(nonSearchParams.toString());
}

function throwIfAborted(signal: AbortSignal): void {
  if (!signal.aborted) return;

  const err = new Error('Search aborted');
  err.name = 'AbortError';
  throw err;
}

function normalizeStringArray(v: any): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((x) => (typeof x === 'string' ? x.trim() : ''))
    .filter((s) => s.length > 0);
}

function sanitizeSelectedIdsByOptions(selectedIds: string[], options: Option[]): string[] {
  const allowed = new Set((options ?? []).map((o: any) => String(o?.id)));
  return (selectedIds ?? []).filter((id) => allowed.has(String(id)));
}

function isSameStringArray(a: string[], b: string[]): boolean {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function parseYmdToLocalDate(ymd: string): Date | null {
  if (typeof ymd !== 'string') return null;
  const m = ymd.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mm = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mm) || !Number.isFinite(d)) return null;
  return new Date(y, mm - 1, d);
}

function normalizeLoginIdCandidate(v: any): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim().toLowerCase();
  return s.length ? s : null;
}

function parseBooleanLike(raw: unknown): boolean | null {
  if (typeof raw === 'boolean') return raw;
  if (typeof raw !== 'string') return null;
  const s = raw.trim().toLowerCase();
  if (!s) return null;
  if (s === '1' || s === 'true' || s === 'yes') return true;
  if (s === '0' || s === 'false' || s === 'no') return false;
  return null;
}

function parseTeamFiscalYear(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw !== 'string') return null;

  const s = raw.trim().toUpperCase();
  if (!s) return null;

  const fyMatch = s.match(/^FY(\d{2}|\d{4})$/);
  if (fyMatch) {
    const n = Number(fyMatch[1]);
    if (!Number.isFinite(n)) return null;
    return fyMatch[1].length === 2 ? 2000 + n : n;
  }

  if (/^\d{4}$/.test(s)) {
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }

  return null;
}

function isPastTeam(teamFiscalYear: unknown): boolean {
  const fy = parseTeamFiscalYear(teamFiscalYear);
  return fy != null;
}

function getTeamEndFiscalYear(team: any): unknown {
  return (team as any)?.end_fiscal_year ?? (team as any)?.team_fiscal_year;
}


type ApiReportType = {
  id: number;
  label: string;
  value: string;
  color?: string | null;
  attr_id: number;
  department_id: number;
  team_id: number;
  order_number: number;
};

const buildReportTypeAccessMapFingerprint = (
  accessMap: Record<string, ReportTypeAccessSetting> | null
): string => {
  if (!accessMap) return '';

  const normalized = Object.keys(accessMap)
    .sort()
    .map((id) => {
      const setting = accessMap[id];
      return [
        id,
        setting?.visibility ?? 'private',
        Array.from(new Set(setting?.departments ?? [])).sort(),
        Array.from(new Set(setting?.teams ?? [])).sort(),
      ];
    });

  return JSON.stringify(normalized);
};

const getDepartmentMasterId = (row: any): string | null => {
  const candidates = [
    row?.id,
    row?.department_id,
    row?.departmentId,
    row?.dept_id,
    row?.deptId,
  ];

  for (const candidate of candidates) {
    const id = normalizeDepartmentId(candidate);
    if (id) return id;
  }

  return null;
};

const getDepartmentMasterLabels = (row: any): string[] => {
  const labels = [
    row?.label,
    row?.value,
    row?.department,
    row?.department_name,
    row?.departmentName,
    row?.dept,
    row?.division,
    row?.name,
  ]
    .map((value) => String(value ?? '').trim())
    .filter((value) => value.length > 0);

  return Array.from(new Set(labels));
};

const getRowDepartmentRawValues = (row: Report): unknown[] => {
  return getWeeklyReportRegisteredDepartmentRawValues(row as any);
};

const buildRowDeptCandidates = (
  row: Report,
  deptIdToLabelMap?: Record<string, string>
): string[] => {
  const rawCandidates = getRowDepartmentRawValues(row);

  const normalized = new Set<string>();
  rawCandidates.forEach((raw) => {
    const s = String(raw ?? '').trim();
    if (!s) return;

    const mappedById = String(deptIdToLabelMap?.[s] ?? '').trim();
    if (mappedById) {
      const code = normalizeScopeCode(mappedById);
      if (code) normalized.add(code);
    }

    if (/^\d+$/.test(s)) {
      const mappedByNumId = String(
        deptIdToLabelMap?.[String(Number(s))] ?? ''
      ).trim();
      if (mappedByNumId) {
        const code = normalizeScopeCode(mappedByNumId);
        if (code) normalized.add(code);
      }
    }

    const selfCode = normalizeScopeCode(s);
    if (selfCode) normalized.add(selfCode);

    const embeddedCodes =
      s
        .normalize('NFKC')
        .toUpperCase()
        .match(/[A-Z]{1,5}\d{1,4}/g)
        ?.map((v) => normalizeScopeCode(v))
        .filter((v) => v.length > 0) ?? [];
    embeddedCodes.forEach((code) => normalized.add(code));
  });

  return Array.from(normalized);
};

const resolveRowDepartmentId = (
  row: Report,
  deptLabelToIdMap?: Record<string, string>
): string | null => {
  const rawCandidates = getRowDepartmentRawValues(row);
  for (const raw of rawCandidates) {
    const resolved = resolveDepartmentIdFromValue(raw, deptLabelToIdMap);
    if (resolved) return resolved;
  }
  return null;
};

const getRowTeamRawValues = (row: Report): unknown[] => {
  const anyRow = row as any;
  return [
    anyRow?.team,
    anyRow?.team_id,
    anyRow?.teamId,
    anyRow?.team_code,
    anyRow?.teamCode,
  ];
};

const resolveRowTeamId = (row: Report): string | null => {
  const rawCandidates = getRowTeamRawValues(row);
  for (const raw of rawCandidates) {
    const resolved = normalizeTeamId(raw);
    if (resolved) return resolved;
  }
  return null;
};

const canAccessPrivateRowByDepartment = (
  row: Report,
  userDepRaw: string,
  context?: VisibilityDepartmentContext
): boolean => {
  const userDepIdFromRaw = resolveDepartmentIdFromValue(
    userDepRaw,
    context?.deptLabelToIdMap
  );
  const userDepIdFromFallback =
    userDepIdFromRaw == null
      ? normalizeDepartmentId(context?.userDeptIdRaw)
      : null;
  const userDepId = userDepIdFromRaw ?? userDepIdFromFallback;

  const rowDepId = resolveRowDepartmentId(row, context?.deptLabelToIdMap);
  if (userDepId && rowDepId) {
    return userDepId === rowDepId;
  }

  const userDepCandidates = buildUserDepCandidates(userDepRaw, {
    deptLabelToIdMap: context?.deptLabelToIdMap,
    deptIdToLabelMap: context?.deptIdToLabelMap,
    userDeptIdRaw: context?.userDeptIdRaw,
  });
  if (userDepCandidates.length === 0) return false;

  const rowDeptCandidates = buildRowDeptCandidates(row, context?.deptIdToLabelMap);
  if (rowDeptCandidates.length === 0) return false;

  return userDepCandidates.some((dep) => rowDeptCandidates.includes(dep));
};

const canAccessRowByUserDep = (
  row: Report,
  setting: ReportTypeAccessSetting,
  userDepRaw: string,
  context?: VisibilityDepartmentContext
): boolean => {
  return canAccessWeeklyReportByScope(
    setting,
    userDepRaw,
    context,
    canAccessPrivateRowByDepartment(row, userDepRaw, context),
  );
};
type TeamOptionWithDept = Option & {
  department_id?: string;
};

type CreatorOptionWithDept = Option & {
  department_id?: string;
  user_id?: string | null;
  legacy?: boolean;
};

type ReportTypeOptionWithDept = Option & {
  color?: string | null;
  attr_id: number;
  department_id?: string;
  team_id: number;
  order_number: number;
};

type WeekRange = {
  start: Date;
  end: Date;
};

// 時刻を 00:00:00.000 にそろえる（比較用）
function stripTime(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function formatDateYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getRecentPresetDateRange(
  preset: RecentPreset,
  baseDate: Date = new Date()
): { startDate: Date; endDate: Date } {
  const thisWeekMon = startOfWeek(baseDate, { weekStartsOn: 1 });
  const startDate =
    preset === 'this'
      ? thisWeekMon
      : preset === 'last'
        ? addDays(thisWeekMon, -7)
        : addDays(thisWeekMon, 7);

  return {
    startDate,
    endDate: addDays(startDate, 4),
  };
}

function isSameLocalDate(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false;
  return formatDateYmd(a) === formatDateYmd(b);
}

function datesMatchRecentPreset(
  preset: RecentPreset,
  startDate: Date | null,
  endDate: Date | null
): boolean {
  if (!startDate || !endDate) return true;
  const presetRange = getRecentPresetDateRange(preset);
  return (
    isSameLocalDate(startDate, presetRange.startDate) &&
    isSameLocalDate(endDate, presetRange.endDate)
  );
}

function getValidRecentPresetFromParams(params: URLSearchParams): RecentPreset | null {
  const rawPreset = params.get(START_DATE_PRESET_PARAM);
  if (!isRecentPreset(rawPreset)) return null;

  const startDateParam = params.get('start_date');
  const endDateParam = params.get('end_date');
  const startDate = startDateParam ? parseYmdToLocalDate(startDateParam) : null;
  const endDate = endDateParam ? parseYmdToLocalDate(endDateParam) : null;

  return datesMatchRecentPreset(rawPreset, startDate, endDate)
    ? rawPreset
    : null;
}

// 検索開始日〜終了日に「かすっている週」をすべて返す
function buildHighlightedWeeksForRange(
  startDate: Date | null,
  endDate: Date | null
): WeekRange[] {
  if (!startDate || !endDate) return [];

  const from = stripTime(startDate);
  const to = stripTime(endDate);

  const [min, max] = from <= to ? [from, to] : [to, from];

  // 週の開始日は「月曜」想定
  let currentStart = startOfWeek(min, { weekStartsOn: 1 });
  const weeks: WeekRange[] = [];

  while (currentStart <= max) {
    const currentEnd = addDays(currentStart, 6);
    weeks.push({
      start: stripTime(currentStart),
      end: stripTime(currentEnd),
    });
    currentStart = addDays(currentStart, 7);
  }

  return weeks;
}

type WeeklyHighlightDayProps = PickersDayProps<Date> & {
  highlightedWeeks?: WeekRange[];
  rangeStartDate?: Date | null;
  rangeEndDate?: Date | null;
  showRangeStartMarker?: boolean;
  showRangeEndMarker?: boolean;
};

// 週単位で帯がハイライトされるように PickersDay を拡張
const WeeklyHighlightDay: React.FC<WeeklyHighlightDayProps> = (props) => {
  const {
    highlightedWeeks = [],
    rangeStartDate,
    rangeEndDate,
    showRangeStartMarker = false,
    showRangeEndMarker = false,
    day,
    outsideCurrentMonth,
    sx,
    selected,
    ...other
  } = props;

  const current = stripTime(day as Date);

  const matchedWeek = highlightedWeeks.find((week) => {
    const start = stripTime(week.start);
    const end = stripTime(week.end);
    return current >= start && current <= end;
  });

  const weekday = current.getDay();
  const isWeekday = weekday >= 1 && weekday <= 5; // 月〜金だけ帯表示

  const rangeStart = rangeStartDate ? stripTime(rangeStartDate) : null;
  const rangeEnd = rangeEndDate ? stripTime(rangeEndDate) : null;
  const isInExplicitDateRange =
    (!rangeStart || current >= rangeStart) && (!rangeEnd || current <= rangeEnd);
  const isInHighlightedWeekAndWeekday = !!matchedWeek && isWeekday && isInExplicitDateRange;
  const isSameRangeBoundary =
    !!rangeStart && !!rangeEnd && rangeStart.getTime() === rangeEnd.getTime();
  const isRangeStartMarker =
    showRangeStartMarker &&
    !!rangeStart &&
    !outsideCurrentMonth &&
    !isSameRangeBoundary &&
    rangeStart.getTime() === current.getTime();
  const isRangeEndMarker =
    showRangeEndMarker &&
    !!rangeEnd &&
    !outsideCurrentMonth &&
    !isSameRangeBoundary &&
    rangeEnd.getTime() === current.getTime();
  const effectiveSelected = Boolean(selected && !isRangeStartMarker && !isRangeEndMarker);

  let tooltipTitle: string | undefined;
  if (isInHighlightedWeekAndWeekday) {
    // 週報の期間は「月〜金」で固定
    const weekStart = startOfWeek(matchedWeek.start, { weekStartsOn: 1 });
    const weekEnd = addDays(weekStart, 4); // 月〜金

    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}/${m}/${dd}`;
    };

    tooltipTitle = `${fmt(weekStart)}〜${fmt(weekEnd)} の週報です`;
  }

  // 「本日の検索日付は青」という MUI デフォルトの選択色を尊重するため、
  // selected === true のときは背景色を上書きしない。
  const dayNode = (
    <PickersDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      selected={effectiveSelected}
      sx={{
        ...sx,
        ...(isInHighlightedWeekAndWeekday && !effectiveSelected
          ? {
              // 週の帯が分かるように背景色を付与（平日のみ）
              backgroundColor: '#d0f0c0',
              color: '#0f172a',
              borderRadius: 0,
              '&:hover, &:focus': {
                backgroundColor: '#b2dfdb',
                color: '#0f172a',
              },
            }
          : {}),
        ...(isRangeStartMarker
          ? {
              backgroundColor: '#dbeafe',
              borderRadius: '50%',
              boxShadow: 'inset 0 0 0 1px #60a5fa',
              color: '#0f172a',
              '&:hover, &:focus': {
                backgroundColor: '#bfdbfe',
              },
            }
          : {}),
        ...(isRangeEndMarker
          ? {
              backgroundColor: '#2563eb',
              borderRadius: '50%',
              color: '#fff',
              '&:hover, &:focus': {
                backgroundColor: '#1d4ed8',
              },
            }
          : {}),
      }}
    />
  );

  if (tooltipTitle) {
    // Tooltip で囲むときは、Button 内に block 要素を入れないよう span でラップ
    return (
      <Tooltip
        title={tooltipTitle}
        arrow
        // Tooltip 自体が hover 判定を持たないようにする（マウスが外れたら素直に閉じる）
        disableInteractive
        // 下の週をクリック/ホバーできるようにする（イベントを邪魔しない）
        slotProps={{
          popper: {
            sx: {
              pointerEvents: 'none',
            },
          },
        }}
      >
        <span>{dayNode}</span>
      </Tooltip>
    );
  }

  return dayNode;
};

// メール関連の整形ユーティリティ
function countCodePoints(str: string): number {
  return Array.from(str ?? '').length;
}


const isAbortError = (err: unknown): boolean => {
  if (err instanceof DOMException) return err.name === 'AbortError';
  return (
    typeof err === 'object' &&
    err !== null &&
    'name' in err &&
    (err as { name?: string }).name === 'AbortError'
  );
};

const isSecurityError = (err: unknown): boolean => {
  if (err instanceof DOMException) return err.name === 'SecurityError';
  return (
    typeof err === 'object' &&
    err !== null &&
    'name' in err &&
    (err as { name?: string }).name === 'SecurityError'
  );
};

function encodeMimeHeaderUtf8(value: string): string {
  // ASCII onlyならそのまま
  if (/^[\x00-\x7F]*$/.test(value)) return value;

  // TextEncoder が使える環境ならそれを優先
  if (typeof TextEncoder !== 'undefined') {
    const bytes = new TextEncoder().encode(value);
    let bin = '';
    for (let i = 0; i < bytes.length; i += 1) {
      bin += String.fromCharCode(bytes[i]);
    }
    const b64 = btoa(bin);
    return `=?UTF-8?B?${b64}?=`;
  }

  // フォールバック（古い環境向け）
  // encodeURIComponent でUTF-8の%xx列 → バイナリ文字列化 → btoa
  const utf8Bin = unescape(encodeURIComponent(value));
  const b64 = btoa(utf8Bin);
  return `=?UTF-8?B?${b64}?=`;
}

function normalizeLineBreaks(raw: string): string {
  return raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function normalizeNoWrapEmailText(raw: string): string {
  return normalizeLineBreaks(raw).replace(/[ \t]*\n+[ \t]*/g, ' ');
}

function escapeHtml(raw: string): string {
  return raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function toPlainTextWithCrlf(raw: string): string {
  return normalizeLineBreaks(raw).replace(/\n/g, CRLF);
}

function toHtmlWithBr(raw: string): string {
  return escapeHtml(normalizeLineBreaks(raw)).replace(/\n/g, '<br>');
}

function wrapTextByCodepoints(raw: string, maxChars: number): string {
  if (maxChars <= 0) return raw;
  const lines = normalizeLineBreaks(raw).split('\n');
  const wrappedLines = lines.map((line) => {
    const chars = Array.from(line);
    if (chars.length <= maxChars) return line;
    const chunks: string[] = [];
    for (let i = 0; i < chars.length; i += maxChars) {
      chunks.push(chars.slice(i, i + maxChars).join(''));
    }
    return chunks.join('\n');
  });
  return wrappedLines.join('\n');
}

type TextWrapConfig = {
  maxWidthPx: number;
  font: string;
  renderedSelectors?: string[];
};

const MAIL_WRAP_REFERENCE_VIEWPORT_WIDTH_PX = 1920;
const MAIL_WRAP_REFERENCE_VIEWPORT_WIDTHS_PX = [1280, 1366, 1536, 1920] as const;
const MAIL_WRAP_SCREEN_MAX_WIDTH_PX = 1920;
const MAIL_WRAP_SCREEN_HORIZONTAL_PADDING_PX = 32;
const MAIL_WRAP_LEFT_COLUMN_RATIO = 2 / 3;
const MAIL_WRAP_RIGHT_COLUMN_RATIO = 1 / 3;
const MAIL_WRAP_TITLE_PADDING_X_PX = 13;
const MAIL_WRAP_BODY_PADDING_X_PX_WIDE = 16;
const MAIL_WRAP_BODY_PADDING_X_PX_COMPACT = 12;
const MAIL_WRAP_FEEDBACK_COLUMN_PADDING_X_PX = 16;
const MAIL_WRAP_FEEDBACK_INPUT_PADDING_X_PX = 66;

type MailTextWrapConfig = {
  title?: TextWrapConfig | null;
  body?: TextWrapConfig | null;
  feedback?: TextWrapConfig | null;
};

type ResolvedMailFormat = {
  preset: MailFormatPreset;
  includeFeedback: boolean;
  wrapMode: 'pixel' | 'codepoint' | 'none';
  wrapChars: number;
};

type ResolvedMailReportText = {
  title: string;
  body: string;
  feedback: string;
  includeFeedbackSection: boolean;
};

function resolveMailFormat(state: MailFormatState): ResolvedMailFormat {
  const safeState = normalizeMailFormatState(state);

  switch (safeState.preset) {
    case MAIL_FORMAT_PRESET_NO_WRAP:
      return {
        preset: safeState.preset,
        includeFeedback: safeState.includeFeedback,
        wrapMode: 'none',
        wrapChars: 0,
      };
    case MAIL_FORMAT_PRESET_APP:
    default:
      return {
        preset: MAIL_FORMAT_PRESET_APP,
        includeFeedback: safeState.includeFeedback,
        wrapMode: 'pixel',
        wrapChars: APP_EMAIL_BODY_WRAP_CHARS,
      };
  }
}

function getMailPresetLabel(preset: MailFormatPreset): string {
  const option = MAIL_FORMAT_PRESET_OPTIONS.find((v) => v.value === preset);
  return option?.label ?? '標準';
}

function resolveMailWrapReferenceViewportWidth(): number {
  if (typeof window === 'undefined') return MAIL_WRAP_REFERENCE_VIEWPORT_WIDTH_PX;

  const rawWidth =
    window.innerWidth ||
    document?.documentElement?.clientWidth ||
    MAIL_WRAP_REFERENCE_VIEWPORT_WIDTH_PX;
  if (!Number.isFinite(rawWidth) || rawWidth <= 0) {
    return MAIL_WRAP_REFERENCE_VIEWPORT_WIDTH_PX;
  }

  for (const width of MAIL_WRAP_REFERENCE_VIEWPORT_WIDTHS_PX) {
    if (rawWidth <= width) return width;
  }
  return MAIL_WRAP_REFERENCE_VIEWPORT_WIDTH_PX;
}

function buildReferenceMailTextWrapConfig(viewportWidthPx: number): MailTextWrapConfig {
  const contentWidthPx =
    Math.min(viewportWidthPx, MAIL_WRAP_SCREEN_MAX_WIDTH_PX) -
    MAIL_WRAP_SCREEN_HORIZONTAL_PADDING_PX;
  const leftColumnWidthPx = contentWidthPx * MAIL_WRAP_LEFT_COLUMN_RATIO;
  const rightColumnWidthPx = contentWidthPx * MAIL_WRAP_RIGHT_COLUMN_RATIO;
  const isCompactReference = viewportWidthPx <= 1600;

  const titleFontSizePx = isCompactReference ? 20 : 22.5;
  const bodyFontSizePx = isCompactReference ? 20 : 22.2;
  const feedbackFontSizePx = isCompactReference ? 15 : 16;

  return {
    title: {
      maxWidthPx: leftColumnWidthPx - MAIL_WRAP_TITLE_PADDING_X_PX,
      font: `700 ${titleFontSizePx}px monospace`,
    },
    body: {
      maxWidthPx:
        leftColumnWidthPx -
        (isCompactReference
          ? MAIL_WRAP_BODY_PADDING_X_PX_COMPACT
          : MAIL_WRAP_BODY_PADDING_X_PX_WIDE),
      font: `400 ${bodyFontSizePx}px monospace`,
    },
    feedback: {
      maxWidthPx:
        rightColumnWidthPx -
        MAIL_WRAP_FEEDBACK_COLUMN_PADDING_X_PX -
        MAIL_WRAP_FEEDBACK_INPUT_PADDING_X_PX,
      font: `400 ${feedbackFontSizePx}px monospace`,
    },
  };
}

function resolveWrapConfigFromSelectors(
  selectors: string[],
  fallback: TextWrapConfig | null
): TextWrapConfig | null {
  const fallbackWithSelectors = fallback
    ? { ...fallback, renderedSelectors: selectors }
    : null;
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return fallbackWithSelectors;
  }

  for (const selector of selectors) {
    const el = document.querySelector<HTMLElement>(selector);
    if (!el) continue;

    const rect = el.getBoundingClientRect();
    const style = window.getComputedStyle(el);
    const paddingLeft = Number.parseFloat(style.paddingLeft || '0') || 0;
    const paddingRight = Number.parseFloat(style.paddingRight || '0') || 0;
    const maxWidthPx = rect.width - paddingLeft - paddingRight;

    if (!Number.isFinite(maxWidthPx) || maxWidthPx <= 0) continue;

    const font =
      style.font && style.font.trim().length > 0
        ? style.font
        : `${style.fontWeight || '400'} ${style.fontSize || '16px'} ${style.fontFamily || 'sans-serif'}`;

    return { maxWidthPx, font, renderedSelectors: selectors };
  }

  return fallbackWithSelectors;
}

function resolveDefaultMailTextWrapConfig(): MailTextWrapConfig {
  const referenceConfig = buildReferenceMailTextWrapConfig(
    resolveMailWrapReferenceViewportWidth()
  );

  return {
    title: resolveWrapConfigFromSelectors(
      [
        '.wr-title-display .MuiInputBase-input',
        '.wr-title-display',
        '.wr-title-textfield textarea.MuiInputBase-input',
        '.titleField textarea.MuiInputBase-input',
        '.titleField .MuiInputBase-input',
      ],
      referenceConfig.title ?? null
    ),
    body: resolveWrapConfigFromSelectors(
      [
        '.wr-body-display-root',
        '.wr-body-textfield textarea.MuiInputBase-inputMultiline',
        '.bodyField textarea.MuiInputBase-inputMultiline',
        '.bodyField .MuiInputBase-input',
      ],
      referenceConfig.body ?? null
    ),
    feedback: resolveWrapConfigFromSelectors(
      [
        '.wr-fb-display .MuiInputBase-input',
        '.wr-fb-text textarea.MuiInputBase-inputMultiline:not([aria-hidden="true"])',
        '.wr-fb-text .FeedbackInputRoot textarea.MuiInputBase-inputMultiline',
        '.wr-fb-display',
      ],
      referenceConfig.feedback ?? null
    ),
  };
}

function findRenderedElementForText(
  raw: string,
  selectors: string[] | undefined
): HTMLElement | null {
  if (typeof document === 'undefined' || !selectors?.length) return null;

  const normalizedRaw = normalizeLineBreaks(raw);
  const normalizedRawTrimmed = normalizedRaw.trim();
  if (!normalizedRawTrimmed) return null;

  for (const selector of selectors) {
    const elements = Array.from(document.querySelectorAll<HTMLElement>(selector));
    for (const el of elements) {
      const text = normalizeLineBreaks(getRenderedElementText(el));
      if (text === normalizedRaw || text.trim() === normalizedRawTrimmed) {
        return el;
      }
    }
  }

  return null;
}

function isTextControlElement(
  el: HTMLElement
): el is HTMLTextAreaElement | HTMLInputElement {
  return (
    (typeof HTMLTextAreaElement !== 'undefined' && el instanceof HTMLTextAreaElement) ||
    (typeof HTMLInputElement !== 'undefined' && el instanceof HTMLInputElement)
  );
}

function getRenderedElementText(el: HTMLElement): string {
  if (isTextControlElement(el)) {
    return el.value ?? '';
  }
  return el.textContent ?? '';
}

function createTextControlMeasurementElement(
  source: HTMLElement,
  text: string
): { el: HTMLElement; cleanup: () => void } | null {
  if (typeof document === 'undefined' || !document.body) return null;

  const rect = source.getBoundingClientRect();
  if (!Number.isFinite(rect.width) || rect.width <= 0) return null;

  const style = window.getComputedStyle(source);
  const mirror = document.createElement('div');
  mirror.textContent = text;

  const mirrorStyle = mirror.style;
  mirrorStyle.position = 'fixed';
  mirrorStyle.left = '-10000px';
  mirrorStyle.top = '0';
  mirrorStyle.visibility = 'hidden';
  mirrorStyle.pointerEvents = 'none';
  mirrorStyle.width = `${rect.width}px`;
  mirrorStyle.boxSizing = style.boxSizing || 'border-box';
  mirrorStyle.padding = style.padding;
  mirrorStyle.border = style.border;
  mirrorStyle.font = style.font;
  mirrorStyle.letterSpacing = style.letterSpacing;
  mirrorStyle.lineHeight = style.lineHeight;
  mirrorStyle.whiteSpace = 'pre-wrap';
  mirrorStyle.wordBreak = style.wordBreak || 'break-word';
  mirrorStyle.overflowWrap = style.overflowWrap || 'break-word';

  document.body.appendChild(mirror);
  return {
    el: mirror,
    cleanup: () => {
      mirror.remove();
    },
  };
}

function collectTextNodes(root: HTMLElement): Text[] {
  const out: Text[] = [];
  if (typeof document === 'undefined') return out;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    out.push(node as Text);
    node = walker.nextNode();
  }
  return out;
}

function findTextPosition(
  nodes: Text[],
  codeUnitOffset: number
): { node: Text; offset: number } | null {
  let remaining = codeUnitOffset;
  for (const node of nodes) {
    const len = node.nodeValue?.length ?? 0;
    if (remaining <= len) {
      return { node, offset: remaining };
    }
    remaining -= len;
  }
  return null;
}

function wrapTextByRenderedElement(
  raw: string,
  selectors: string[] | undefined
): string | null {
  if (typeof document === 'undefined') return null;

  const normalized = normalizeLineBreaks(raw);
  const el = findRenderedElementForText(normalized, selectors);
  if (!el) return null;

  const measurement = isTextControlElement(el)
    ? createTextControlMeasurementElement(el, normalized)
    : null;
  const measureEl = measurement?.el ?? el;

  const nodes = collectTextNodes(measureEl);
  if (nodes.length === 0) {
    measurement?.cleanup();
    return null;
  }

  const domText = normalizeLineBreaks(getRenderedElementText(el));
  if (domText !== normalized && domText.trim() !== normalized.trim()) {
    measurement?.cleanup();
    return null;
  }

  const range = document.createRange();
  const chars = Array.from(normalized);
  const out: string[] = [];
  let prevTop: number | null = null;
  let codeUnitOffset = 0;

  try {
    for (const ch of chars) {
      const charCodeUnitLength = ch.length;
      if (ch === '\n') {
        out.push('\n');
        prevTop = null;
        codeUnitOffset += charCodeUnitLength;
        continue;
      }

      const start = findTextPosition(nodes, codeUnitOffset);
      const end = findTextPosition(nodes, codeUnitOffset + charCodeUnitLength);
      codeUnitOffset += charCodeUnitLength;

      if (!start || !end) {
        out.push(ch);
        continue;
      }

      range.setStart(start.node, start.offset);
      range.setEnd(end.node, end.offset);
      const rect = range.getClientRects()[0];
      if (rect) {
        const top = Math.round(rect.top * 2) / 2;
        if (
          prevTop !== null &&
          Math.abs(top - prevTop) > 2 &&
          out[out.length - 1] !== '\n'
        ) {
          out.push('\n');
        }
        prevTop = top;
      }

      out.push(ch);
    }
  } catch {
    return null;
  } finally {
    range.detach();
    measurement?.cleanup();
  }

  return out.join('');
}

function wrapTextByPixelWidth(raw: string, maxWidthPx: number, font: string): string {
  if (maxWidthPx <= 0) return raw;
  if (typeof document === 'undefined') return raw;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return raw;
  ctx.font = font;

  const lines = normalizeLineBreaks(raw).split('\n');
  const wrappedLines = lines.map((line) => {
    const chars = Array.from(line);
    if (chars.length === 0) return '';
    const chunks: string[] = [];
    let current = '';
    let currentWidth = 0;

    chars.forEach((ch) => {
      const w = ctx.measureText(ch).width;
      if (current && currentWidth + w > maxWidthPx) {
        chunks.push(current);
        current = ch;
        currentWidth = w;
      } else {
        current += ch;
        currentWidth += w;
      }
    });

    if (current) chunks.push(current);
    return chunks.join('\n');
  });

  return wrappedLines.join('\n');
}

function wrapTextForEmail(
  raw: string,
  config: TextWrapConfig | null,
  resolvedFormat: ResolvedMailFormat
): string {
  const normalized = normalizeLineBreaks(raw);

  if (resolvedFormat.wrapMode === 'none') {
    return normalizeNoWrapEmailText(normalized);
  }

  if (resolvedFormat.wrapMode === 'codepoint') {
    return wrapTextByCodepoints(normalized, resolvedFormat.wrapChars);
  }

  const renderedWrapped = wrapTextByRenderedElement(
    normalized,
    config?.renderedSelectors
  );
  if (renderedWrapped != null) {
    return renderedWrapped;
  }

  if (config?.maxWidthPx && config.font) {
    return wrapTextByPixelWidth(normalized, config.maxWidthPx, config.font);
  }

  return wrapTextByCodepoints(normalized, resolvedFormat.wrapChars);
}

function estimateEmailWrapCharWidthPx(font: string): number {
  if (typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.font = font;
      const measured = ctx.measureText('あ').width;
      if (Number.isFinite(measured) && measured > 0) {
        return measured;
      }
    }
  }

  const fontSize = Number.parseFloat(font.match(/(\d+(?:\.\d+)?)px/)?.[1] ?? '');
  return Number.isFinite(fontSize) && fontSize > 0 ? fontSize : 16;
}

function resolveFeedbackMailWrapConfig(
  wrapConfig: MailTextWrapConfig,
  resolvedFormat: ResolvedMailFormat
): TextWrapConfig | null {
  const feedbackConfig = wrapConfig.feedback ?? null;
  const bodyConfig = wrapConfig.body ?? null;

  if (
    resolvedFormat.wrapMode !== 'pixel' ||
    !bodyConfig?.maxWidthPx ||
    !bodyConfig.font
  ) {
    return feedbackConfig;
  }

  // APP改行時のFBは、画面右ペインの狭い表示幅ではなく本文と同じ幅・同じフォント基準で折り返す。
  // renderedSelectors は渡さず、右側FB欄のDOM幅で再計測されないようにする。
  const twoCharsWidthPx = estimateEmailWrapCharWidthPx(bodyConfig.font) * 2;
  return {
    maxWidthPx: bodyConfig.maxWidthPx + twoCharsWidthPx,
    font: bodyConfig.font,
  };
}

function buildResolvedMailReportText(
  r: Report,
  options: {
    wrapConfig?: MailTextWrapConfig;
    resolvedFormat: ResolvedMailFormat;
  }
): ResolvedMailReportText {
  const wrapConfig = options.wrapConfig ?? {};
  const resolvedFormat = options.resolvedFormat;

  const title = wrapTextForEmail(
    (r?.title ?? '').toString(),
    wrapConfig.title ?? null,
    resolvedFormat
  );
  const body = wrapTextForEmail(
    (r?.body ?? '').toString(),
    wrapConfig.body ?? null,
    resolvedFormat
  );
  const feedback = resolvedFormat.includeFeedback
    ? wrapTextForEmail(
        (r?.feedback_body ?? '').toString(),
        resolveFeedbackMailWrapConfig(wrapConfig, resolvedFormat),
        resolvedFormat
      )
    : '';
  const includeFeedbackSection = feedback.trim().length > 0;

  return {
    title,
    body,
    feedback,
    includeFeedbackSection,
  };
}

function extractFamilyNameForHeader(name: string): string {
  const raw = String(name ?? '').trim();
  if (!raw) return '';
  const tokens = raw.split(/[ \u3000]+/).filter(Boolean);
  if (tokens.length >= 2) return tokens[0];

  const chars = Array.from(raw);
  if (chars.includes('\u30FB') && chars.length >= 3) {
    return chars.slice(0, 3).join('');
  }
  if (chars.length >= 4) {
    return chars.slice(0, 2).join('');
  }
  return raw;
}

function buildRespondentHeaderNames(respondents: string[]): string[] {
  const normalized = (respondents ?? [])
    .map((v) => String(v ?? '').replace(/\s+/g, ' ').trim())
    .filter((v) => v.length > 0);

  if (normalized.length === 0) return [];

  const rows = normalized.map((full) => {
    const family = extractFamilyNameForHeader(full) || full;
    const plain = full.replace(/[ \u3000]+/g, '');
    const isPlain = !/[ \u3000]/.test(full) && !plain.includes('\u30FB');
    const prefix2 = isPlain && plain.length >= 2 ? Array.from(plain).slice(0, 2).join('') : '';
    return {
      full,
      family,
      isPlain,
      prefix2,
    };
  });

  const prefix2Counts = new Map<string, number>();
  rows.forEach((row) => {
    const key = String(row.prefix2 ?? '').trim();
    if (!key) return;
    prefix2Counts.set(key, (prefix2Counts.get(key) ?? 0) + 1);
  });

  const familyKeys = rows.map((row) => {
    const family = String(row.family ?? '').trim();
    if (
      row.isPlain &&
      family.length >= 3 &&
      row.prefix2 &&
      (prefix2Counts.get(row.prefix2) ?? 0) > 1
    ) {
      return row.prefix2;
    }
    return family;
  });

  const counts = new Map<string, number>();
  familyKeys.forEach((family) => {
    const key = String(family ?? '').trim();
    if (!key) return;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });

  const labels = rows.map((row, idx) => {
    const familyKey = String(familyKeys[idx] ?? '').trim();
    if (!familyKey) return row.full;
    if ((counts.get(familyKey) ?? 0) <= 1) return familyKey;
    return row.full;
  });

  return dedupePreserveOrder(labels);
}

function dedupePreserveOrder(list: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  list.forEach((v) => {
    const s = String(v ?? '').trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  });
  return out;
}

function normalizeRespondents(raw: unknown): string[] {
  const acc: string[] = [];

  const pushValue = (value: unknown) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(pushValue);
      return;
    }
    if (typeof value === 'object') {
      Object.values(value as Record<string, unknown>).forEach(pushValue);
      return;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return;
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object') {
          pushValue(parsed);
          return;
        }
      } catch {
        // ignore JSON parse errors
      }
      trimmed
        .split(/[,\u3001\n]/)
        .map((v) => v.replace(/\s+/g, ' ').trim())
        .filter((v) => v.length > 0)
        .forEach((v) => acc.push(v));
      return;
    }
    const asString = String(value).replace(/\s+/g, ' ').trim();
    if (asString) acc.push(asString);
  };

  pushValue(raw);

  const seen = new Set<string>();
  const deduped: string[] = [];
  acc.forEach((name) => {
    const normalized = name.replace(/\s+/g, ' ').trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    deduped.push(normalized);
  });

  return deduped;
}

type EmailHeaderLabelMaps = {
  deptLabelMap?: Record<string, string>;
  teamLabelMap?: Record<string, string>;
};

function buildEmailHeaderBlock(
  r: Report,
  options?: EmailHeaderLabelMaps
): string {
  const deptKey = r?.department != null ? String(r.department) : '';
  const teamKey = r?.team != null ? String(r.team) : '';
  const deptLabel =
    deptKey && options?.deptLabelMap?.[deptKey]
      ? String(options.deptLabelMap[deptKey])
      : deptKey;
  const teamLabel =
    teamKey && options?.teamLabelMap?.[teamKey]
      ? String(options.teamLabelMap[teamKey])
      : teamKey;
  const deptTeamLabel = [deptLabel, teamLabel]
    .map((v) => String(v ?? '').trim())
    .filter((v) => v.length > 0)
    .join(' ');

  const reporterFull = (r?.create_by ?? '').toString().trim();
  const reporter = extractFamilyNameForHeader(reporterFull) || reporterFull;
  const jobTitle = (r?.approver ?? '').toString().trim();
  const respondents = normalizeRespondents(r?.respondents);
  const respondentNames = buildRespondentHeaderNames(respondents);
  const respondentsText = respondentNames.join(EMAIL_HEADER_RESPONDENTS_DELIM);
  const respondentsInline = respondentNames.length > 0;

  const headerParts: string[] = [];

  if (deptTeamLabel) {
    headerParts.push(
      `<span style="font-size:10.5pt;font-weight:700;color:#000;">` +
        `${EMAIL_HEADER_BRACKET_OPEN}${escapeHtml(deptTeamLabel)}${EMAIL_HEADER_BRACKET_CLOSE}` +
      `</span>`
    );
  }

  const jobTitleText = jobTitle
    ? `${EMAIL_HEADER_ANGLE_OPEN}${EMAIL_HEADER_LABEL_TEAM}${EMAIL_HEADER_SEPARATOR}${jobTitle}${EMAIL_HEADER_ANGLE_CLOSE}`
    : '';
  const reporterText = reporter
    ? `${EMAIL_HEADER_LABEL_AUTHOR}${EMAIL_HEADER_SEPARATOR}${reporter}`
    : '';
  const middleText = [jobTitleText, reporterText].filter(Boolean).join('');
  if (middleText) {
    headerParts.push(
      `<span style="font-size:10pt;color:#000;">${escapeHtml(middleText)}</span>`
    );
  }

  if (respondentsInline && respondentsText) {
    const inlineText =
      ` ${EMAIL_HEADER_PAREN_OPEN}` +
      `${EMAIL_HEADER_LABEL_RESPONDENTS}${EMAIL_HEADER_RESPONDENTS_SEPARATOR}${respondentsText}` +
      `${EMAIL_HEADER_PAREN_CLOSE}`;
    headerParts.push(
      `<span style="font-size:9pt;color:#000;">${escapeHtml(inlineText)}</span>`
    );
  }

  if (headerParts.length === 0) return '';

  const headerLine = headerParts.join('');
  const headerLineHtml = headerParts.length > 0
    ? `<p style="margin:0 0 1px 0; mso-line-height-rule:exactly; line-height:1.25;">${headerLine}</p>`
    : '';
  return `${headerLineHtml}`;
}

function buildEmailHtmlBlock(
  r: Report,
  options?: {
    addTrailingBlankLine?: boolean;
    deptLabelMap?: Record<string, string>;
    teamLabelMap?: Record<string, string>;
    wrapConfig?: MailTextWrapConfig;
    resolvedFormat?: ResolvedMailFormat;
  }
): string {
  const resolvedFormat =
    options?.resolvedFormat ?? resolveMailFormat(DEFAULT_MAIL_FORMAT_STATE);
  const headerHtml = buildEmailHeaderBlock(r, {
    deptLabelMap: options?.deptLabelMap,
    teamLabelMap: options?.teamLabelMap,
  });
  const addTrailingBlankLine = options?.addTrailingBlankLine === true;
  const mailText = buildResolvedMailReportText(r, {
    wrapConfig: options?.wrapConfig,
    resolvedFormat,
  });
  const titleHtml = toHtmlWithBr(mailText.title);
  const bodyHtml = toHtmlWithBr(mailText.body);
  const feedbackHtml = toHtmlWithBr(mailText.feedback);

  return [
    `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0"`,
    `  bgcolor="#ffffff" style="background-color:#ffffff;`,
    `  background-image:linear-gradient(#ffffff,#ffffff);`,
    `  margin:0 0 10px 0;">`,
    `<tr><td style="padding:0; mso-line-height-rule:exactly;`,
    `  font-family:${EMAIL_FONT_FAMILY}; font-size:10.5pt; line-height:1.25;`,
    `  color:${EMAIL_BODY_COLOR};">`,
    headerHtml,

    // タイトル
    `<p style="margin:0 0 1px 0; mso-line-height-rule:exactly; line-height:1.25; font-size:10.5pt;">` +
      `<font color="${EMAIL_TITLE_COLOR}">` +
        `<b><u>` +
          `<span style="color:${EMAIL_TITLE_COLOR} !important;">` +
            `${titleHtml}` +
          `</span>` +
        `</u></b>` +
      `</font>` +
    `</p>`,

    // 本文
    `<p style="margin:0; mso-line-height-rule:exactly; line-height:1.25; font-size:10.5pt;">` +
      `<font color="${EMAIL_BODY_COLOR}">` +
        `<span style="color:${EMAIL_BODY_COLOR} !important;">` +
          `${bodyHtml}` +
        `</span>` +
      `</font>` +
    `</p>`,
    ...(mailText.includeFeedbackSection
      ? [
          `<p style="display:block; margin:0; padding:0; mso-line-height-rule:exactly; line-height:1.15; font-size:10.5pt;">` +
            `<font color="${EMAIL_FEEDBACK_COLOR}">` +
              `<span style="color:${EMAIL_FEEDBACK_COLOR} !important;">` +
                `${feedbackHtml}` +
              `</span>` +
            `</font>` +
          `</p>`,
        ]
      : []),
    ...(addTrailingBlankLine
      ? [`<div style="height:10px; line-height:10px;">&nbsp;</div>`]
      : []),

    `</td></tr></table>`,
  ].join('');
}

function formatReportBlockForPreview(
  r: Report,
  options?: {
    wrapConfig?: MailTextWrapConfig;
    resolvedFormat?: ResolvedMailFormat;
  }
): string {
  const resolvedFormat =
    options?.resolvedFormat ?? resolveMailFormat(DEFAULT_MAIL_FORMAT_STATE);
  const mailText = buildResolvedMailReportText(r, {
    wrapConfig: options?.wrapConfig,
    resolvedFormat,
  });
  const lines = [
    toPlainTextWithCrlf(mailText.title),
    toPlainTextWithCrlf(mailText.body),
  ];
  if (mailText.includeFeedbackSection) {
    lines.push(`FB：${toPlainTextWithCrlf(mailText.feedback)}`);
  }
  return lines.join(CRLF);
}

type PerItemEml = {
  index1based: number;
  preview: string;
  rawLength: number;
};

function buildEmlPerItem(
  reports: Report[],
  options?: {
    wrapConfig?: MailTextWrapConfig;
    resolvedFormat?: ResolvedMailFormat;
  }
): { items: PerItemEml[] } {
  const resolvedFormat =
    options?.resolvedFormat ?? resolveMailFormat(DEFAULT_MAIL_FORMAT_STATE);
  const items = reports.map((r, i) => {
    const index1based = i + 1;

    const titleRaw = (r?.title ?? '').toString();
    const bodyRaw = (r?.body ?? '').toString();
    const feedbackRaw = (r?.feedback_body ?? '').toString();

    const rawLength =
      countCodePoints(titleRaw) +
      countCodePoints(bodyRaw) +
      (resolvedFormat.includeFeedback ? countCodePoints(feedbackRaw) : 0);
    const preview = formatReportBlockForPreview(r, {
      wrapConfig: options?.wrapConfig,
      resolvedFormat,
    }).replace(/\r\n/g, ' / ');

    return {
      index1based,
      preview,
      rawLength,
    };
  });
  return { items };
}

function base64Utf8(str: string): string {
  if (typeof TextEncoder !== 'undefined') {
    const bytes = new TextEncoder().encode(str);
    let bin = '';
    for (let i = 0; i < bytes.length; i += 1) {
      bin += String.fromCharCode(bytes[i]);
    }
    return btoa(bin);
  }
  return btoa(unescape(encodeURIComponent(str)));
}

function wrapBase64(b64: string, width = 76): string {
  const chunks: string[] = [];
  for (let i = 0; i < b64.length; i += width) {
    chunks.push(b64.slice(i, i + width));
  }
  return chunks.join('\r\n');
}

function buildEmlText(
  allReports: Report[],
  options?: EmailHeaderLabelMaps & {
    mailFormat?: ResolvedMailFormat;
    wrapConfig?: MailTextWrapConfig;
  }
): string {
  const subject = encodeMimeHeaderUtf8(MAIL_SUBJECT_BASE);
  const resolvedFormat =
    options?.mailFormat ?? resolveMailFormat(DEFAULT_MAIL_FORMAT_STATE);
  const wrapConfig = options?.wrapConfig ?? resolveDefaultMailTextWrapConfig();

  const isMulti = allReports.length > 1;
  const blocksHtml = allReports
    .map((r) =>
      buildEmailHtmlBlock(r, {
        addTrailingBlankLine: isMulti,
        deptLabelMap: options?.deptLabelMap,
        teamLabelMap: options?.teamLabelMap,
        wrapConfig,
        resolvedFormat,
      })
    )
    .join('');

  const htmlBody =
    `<!DOCTYPE html><html><head>` +
    `<meta charset="UTF-8">` +
    `<meta http-equiv="x-ua-compatible" content="IE=edge">` +

    // “ライト固定”を宣言（Outlookデスクトップは無視することがあるが入れて損なし）
    `<meta name="color-scheme" content="light">` +
    `<meta name="supported-color-schemes" content="light">` +

    // Outlook.com / 一部クライアントのdark mode対策
    `<style>` +
    `  :root { color-scheme: light; supported-color-schemes: light; }` +
    `  body, table, td, p, div, span { color: ${EMAIL_BODY_COLOR} !important; }` +
    `  a { color: ${EMAIL_TITLE_COLOR} !important; }` +
    `  [data-ogsc] body, [data-ogsc] table, [data-ogsc] td, [data-ogsc] p, [data-ogsc] div, [data-ogsc] span {` +
    `    background-color: #ffffff !important;` +
    `    background-image: linear-gradient(#ffffff,#ffffff) !important;` +
    `    color: ${EMAIL_BODY_COLOR} !important;` +
    `  }` +
    `  [data-ogsc] a { color: ${EMAIL_TITLE_COLOR} !important; }` +
    `</style>` +

    // mso(Outlook)専用：Office設定（見た目安定用）
    `<!--[if mso]>` +
    `<noscript><xml><o:OfficeDocumentSettings>` +
    `<o:AllowPNG/>` +
    `<o:PixelsPerInch>96</o:PixelsPerInch>` +
    `</o:OfficeDocumentSettings></xml></noscript>` +
    `<style>` +
    `  body, table, td, p, div, span { font-family: ${EMAIL_FONT_FAMILY} !important; }` +
    `</style>` +
    `<![endif]-->` +
    `</head>` +

    `<body bgcolor="#ffffff" ` +
    `  style="margin:0; padding:0;` +
    `  background-color:#ffffff !important;` +
    `  background-image:linear-gradient(#ffffff,#ffffff) !important;` +
    `  color:${EMAIL_BODY_COLOR} !important;` +
    `  font-family:${EMAIL_FONT_FAMILY}; font-size:10.5pt; line-height:1.3;">` +

    `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" bgcolor="#ffffff" ` +
    `  style="background-color:#ffffff; background-image:linear-gradient(#ffffff,#ffffff);">` +
    `<tr><td style="padding:16px; color:${EMAIL_BODY_COLOR};">` +
    blocksHtml +
    `</td></tr></table>` +

    `</body></html>`;

  const htmlB64 = wrapBase64(base64Utf8(htmlBody));

  const lines: string[] = [
    `Subject: ${subject}`,
    `MIME-Version: 1.0`,
    `X-Unsent: 1`,
    `Content-Type: text/html; charset=UTF-8`,
    `Content-Transfer-Encoding: base64`,
    ``,
    htmlB64,
  ];

  return lines.join(CRLF) + CRLF;
}



function buildEmlFilename(suffix?: string): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const base = `weekly-report_${y}${m}${dd}-${hh}${mm}`;
  if (suffix && suffix.trim()) return `${base}_${suffix.trim()}.eml`;
  return `${base}.eml`;
}

// ==============================
// .eml を「1回だけ」開く/ダウンロードするためのガード（強化版）
// - ブラウザ実装差で「1操作が2回保存」になるのを抑止
// - HMR / 画面二重マウント等で module-scope が効かないケースも想定し globalThis に保持
// ==============================

const EML_DEDUP_WINDOW_MS = 3000;

// globalThis に dedup 状態を保持（モジュールが再評価されても共有される）
const GLOBAL_DEDUP_MAP_KEY = '__weekly_eml_dedup_v1__';

type GlobalDedupEntry = { sig: string; at: number };
type GlobalDedupMap = Record<string, GlobalDedupEntry>;

function getGlobalDedupMap(): GlobalDedupMap {
  const g = globalThis as any;
  if (!g[GLOBAL_DEDUP_MAP_KEY]) g[GLOBAL_DEDUP_MAP_KEY] = {};

  const map = g[GLOBAL_DEDUP_MAP_KEY] as GlobalDedupMap;

  // 例: 60秒より古いものを掃除
  const now = Date.now();
  for (const [k, v] of Object.entries(map)) {
    if (!v?.at || now - v.at > 60_000) {
      delete map[k];
    }
  }
  return map;
}

// async 版（失敗したらロックを戻して再試行できるようにする）
async function runOnceGlobalAsync(
  key: string,
  sig: string,
  fn: () => Promise<void>
): Promise<boolean> {
  const map = getGlobalDedupMap();
  const now = Date.now();
  const last = map[key];

  if (last && last.sig === sig && now - last.at < EML_DEDUP_WINDOW_MS) {
    return false;
  }

  map[key] = { sig, at: now };

  try {
    await fn();
    return true;
  } catch (e) {
    // 失敗したらロック解除（ユーザーがすぐ再実行できるように）
    const cur = map[key];
    if (cur && cur.sig === sig) {
      delete map[key];
    }
    throw e;
  }
}

function buildEmlActionSig(emlText: string): string {
  // filename 依存にすると微妙な差で dedup できないので本文ベースで指紋化
  const head = emlText.slice(0, 256);
  const tail = emlText.slice(-256);
  return `${emlText.length}::${head}::${tail}`;
}

function normalizeEmlFilename(filename: string): string {
  return filename.endsWith('.eml') ? filename : `${filename}.eml`;
}

function revokeLater(url: string, ms = 20000): void {
  window.setTimeout(() => {
    try {
      URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }, ms);
}

async function downloadEml(
  filename: string,
  emlText: string,
  options?: { preferFilePicker?: boolean }
): Promise<void> {
  const fileNameWithExt = normalizeEmlFilename(filename);
  const sig = buildEmlActionSig(emlText);
  const preferFilePicker = options?.preferFilePicker !== false;

  // download 専用の key（open と分離）
  const key = `eml-download::${sig}`;

  await runOnceGlobalAsync(key, sig, async () => {
    const blob = new Blob([emlText], { type: 'message/rfc822' });

    const navAny = navigator as any;
    if (typeof navAny?.msSaveOrOpenBlob === 'function') {
      navAny.msSaveOrOpenBlob(blob, fileNameWithExt);
      return;
    }

    const wAny = window as any;
    const canUsePicker =
      preferFilePicker &&
      typeof wAny?.showSaveFilePicker === 'function' &&
      Boolean(navAny?.userActivation?.isActive);

    if (canUsePicker) {
      try {
        // showSaveFilePicker は secure context 必須 & user activation が必要 :contentReference[oaicite:5]{index=5}
        const handle = await wAny.showSaveFilePicker({
          suggestedName: fileNameWithExt,
          types: [
            {
              description: 'E-mail (.eml)',
              accept: { 'message/rfc822': ['.eml'] },
            },
          ],
        });

        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
      } catch (e) {
        // ユーザーがキャンセルした場合 AbortError になり得る :contentReference[oaicite:6]{index=6}
        if (isAbortError(e)) return;
        // ユーザー操作扱いでない場合などはフォールバックへ
        if (isSecurityError(e)) {
          // fall through to anchor download
        } else {
          throw e;
        }
      }
    }

    // フォールバック（a.download + blobURL）
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = fileNameWithExt;
      a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      revokeLater(url, 60_000);
    }
  });
}

interface SearchAreaProps {
  mailExportReports: Report[];
  onResults: (results: Report[]) => void;
  onSearchPerformed: (reason?: string) => void; // reason を渡す
  onBusyChange?: (busy: boolean) => void;
}

const SearchArea: React.FC<SearchAreaProps> = ({
  mailExportReports,
  onResults,
  onSearchPerformed,
  onBusyChange,
}) => {
  // ----- 状態管理 -----
  const [state, setState] = useState<MastersContextValue>({
    loading: true,
    error: null,
    data: null,
  });

  const [teamsMaster, setTeamsMaster] = useState<any[]>([]);
  const [teamClassificationsMaster, setTeamClassificationsMaster] = useState<any[]>([]);
  const [usersMaster, setUsersMaster] = useState<any[]>([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);

  // ログイン中のユーザー情報（Cognito 等）
  const currentUser = useUser();
  const { profile, loaded: profileLoaded } = useUserProfile();
  const viewerAccessContextKey = useMemo(
    () =>
      buildWeeklyViewerAccessContextKey({
        profileLoaded,
        department: profile?.department,
        teamId: profile?.preferredTeamId,
        updatedAt: profile?.preferredTeamUpdatedAt,
      }),
    [
      profileLoaded,
      profile?.department,
      profile?.preferredTeamId,
      profile?.preferredTeamUpdatedAt,
    ]
  );
  const latestViewerAccessContextKeyRef = useRef<string | null>(
    viewerAccessContextKey
  );

  useEffect(() => {
    latestViewerAccessContextKeyRef.current = viewerAccessContextKey;
  }, [viewerAccessContextKey]);

  // label の有無チェック
  const hasLabel = <T extends { label?: string | null }>(
    x: T | null | undefined
  ): x is T & { label: string } =>
    !!x && typeof x.label === 'string' && x.label.trim().length > 0;

  // 課ラベル "XQ4" → 課ID "1" といったマップ
  const deptLabelToIdMap = useMemo(() => {
    const map: Record<string, string> = {};
    const list = state.data?.departments ?? [];

    list.forEach((d: any) => {
      const id = getDepartmentMasterId(d);
      if (!id) return;

      getDepartmentMasterLabels(d).forEach((label) => {
        map[label] = id;

        const normalizedLabel = normalizeScopeCode(label);
        if (normalizedLabel) {
          map[normalizedLabel] = id;
        }
      });
    });

    return map;
  }, [state.data?.departments]);

  // 課ID "1" → 課ラベル "XQ4" といったマップ（USER_DEP のフォールバック用）
  const deptIdToLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    const list = state.data?.departments ?? [];

    list.forEach((d: any) => {
      const id = getDepartmentMasterId(d);
      if (!id) return;

      const label = getDepartmentMasterLabels(d)[0] ?? '';
      if (!label) return;

      map[id] = label;
    });

    return map;
  }, [state.data?.departments]);

// 「課」の候補 (表示名/値)
const deptOptions: Suggestion[] = useMemo(() => {
  const collator = new Intl.Collator('en', {
    numeric: true,
    sensitivity: 'base',
  });

  // マスタの order_number 昇順（未設定は最後尾）。同値はラベルの自然順。
  const toOrder = (v: unknown): number => {
    if (v === null || v === undefined || v === '') return Number.MAX_SAFE_INTEGER;
    const n = Number(v);
    return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
  };

  return (state.data?.departments ?? [])
    .map((d: any): (Suggestion & { order: number }) | null => {
      const idRaw = getDepartmentMasterId(d);
      const id = Number(idRaw);
      const label = getDepartmentMasterLabels(d)[0] ?? '';

      if (!Number.isFinite(id) || id <= 0) {
        return null;
      }

      if (!label) {
        return null;
      }

      return {
        id,
        label,
        order: toOrder(d?.order_number),
      };
    })
    .filter((d): d is Suggestion & { order: number } => d !== null)
    .sort((a, b) =>
      a.order !== b.order ? a.order - b.order : collator.compare(a.label, b.label)
    )
    .map(({ id, label }): Suggestion => ({ id, label }));
}, [state.data?.departments]);


  // 週報分類マスタ（課ID付き・重複除去）
  const reportOptionsRaw: ReportTypeOptionWithDept[] = useMemo(() => {
    const list = (state.data?.reportTypes as ApiReportType[] | undefined) ?? [];

    const withLabel = list.filter(hasLabel).map((item) => ({
      id: String(item.id),
      value: item.value ?? item.label,
      label: item.label,
      color: item.color ?? null,
      attr_id: item.attr_id,
      department_id: String(item.department_id),
      team_id: item.team_id,
      order_number: item.order_number,
    }));

    const seen = new Set<string>();

    return withLabel.filter((opt) => {
      // department_id も含めて重複判定する
      const key = `${opt.attr_id}::${opt.department_id ?? ''}::${opt.label}`;

      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [state.data?.reportTypes]);

  // buildWeeklySearchParams() に渡す用：生の週報分類マスタ（report_types の label 解決に使う）
  const rawReportTypes: ApiReportType[] = useMemo(() => {
    return (state.data?.reportTypes as ApiReportType[] | undefined) ?? [];
  }, [state.data?.reportTypes]);
  const rawReportTypeIdSet = useMemo(
    () => new Set(rawReportTypes.map((rt) => String(rt.id))),
    [rawReportTypes]
  );

  const teamClassNameByIdMap = useMemo(() => {
    const map: Record<string, string> = {};
    const list = Array.isArray(teamClassificationsMaster)
      ? teamClassificationsMaster
      : [];
    list.forEach((row: any) => {
      const id = String((row as any)?.class_id ?? (row as any)?.id ?? '').trim();
      const name = String(
        (row as any)?.class_name ??
          (row as any)?.classification_name ??
          (row as any)?.name ??
          ''
      ).trim();
      if (!id || !name) return;
      map[id] = name;
    });
    return map;
  }, [teamClassificationsMaster]);

  // チームマスタ (/get_all_teams)
  const allTeamOptionsRaw: TeamOptionWithDept[] = useMemo(() => {
    return teamsMaster
      .filter(
        (t) =>
          t.status === 'Y' && // 有効なチームだけに絞る
          typeof t.label === 'string' &&
          t.label.trim().length > 0
      )
      .map((t) => ({
        id: String(t.team_id),
        value: String(t.label ?? '').trim(),
        groupLabel: (() => {
          if (isPastTeam(getTeamEndFiscalYear(t))) {
            return '過去チーム';
          }
          const classId = String((t as any)?.class_id ?? (t as any)?.classId ?? '').trim();
          const className = classId ? String(teamClassNameByIdMap[classId] ?? '').trim() : '';
          return className || '分類なし';
        })(),
        department_id: String(t.department_id),
      }));
  }, [teamsMaster, teamClassNameByIdMap]);

  const teamIdToLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    (teamsMaster ?? []).forEach((t: any) => {
      const key = t?.team_id != null ? String(t.team_id) : '';
      const label = t?.label != null ? String(t.label).trim() : '';
      if (key && label) {
        map[key] = label;
      }
    });
    return map;
  }, [teamsMaster]);

  // 週報連／入力修正で共通の並び順にするためのチーム並び順マップ
  const teamSortIndexMap = useMemo(
    () => buildTeamSortIndexMap(teamsMaster),
    [teamsMaster]
  );

  const userDepartmentByName = useMemo(() => {
    const map = new Map<string, string>();
    usersMaster.forEach((user: any) => {
      const fullName = `${String(user?.last_name ?? '').trim()} ${String(
        user?.first_name ?? ''
      ).trim()}`.trim();
      const department = String(user?.department ?? '').trim();
      if (fullName && department && !map.has(fullName)) {
        map.set(fullName, department);
      }
    });
    return map;
  }, [usersMaster]);

  // 記入者候補
  // creators（=週報で実際に記入された実績のある人）と
  // usersMaster（ユーザ一覧）を統合して、氏名ごとに1件だけにする。
  const creatorOptionsRaw: CreatorOptionWithDept[] = useMemo(() => {
    const mapByName: Record<string, CreatorOptionWithDept> = {};

    // 1) state.data.creators 側（週報作成済みユーザ）
    const creators = (state.data as any)?.creators ?? [];
    creators.forEach((c: any) => {
      const fullName = c.label; // "山田 太郎" など

      const deptLabelOfCreator = userDepartmentByName.get(fullName);
      const deptIdOfCreator = deptLabelOfCreator
        ? deptLabelToIdMap[deptLabelOfCreator]
        : undefined;

      // ID は wr_id があればそれ、なければ名前をそのまま使う
      const opt: CreatorOptionWithDept = {
        id: String(c.wr_id ?? fullName),
        value: fullName,
        department_id: deptIdOfCreator,
      };

      mapByName[fullName] = opt;
    });

    // 2) usersMaster 側（全ユーザ）
    usersMaster.forEach((u: any) => {
      const fullName = `${u.last_name} ${u.first_name}`;
      const deptLabel = u.department;
      const deptId = deptLabel ? deptLabelToIdMap[deptLabel] : undefined;

      if (!mapByName[fullName]) {
        // まだ登録されていない人は追加する
        mapByName[fullName] = {
          id: fullName, // wr_id が無い人は氏名で
          value: fullName,
          department_id: deptId,
        };
      } else {
        // department_id がまだ無い場合は補完する
        if (!mapByName[fullName].department_id && deptId) {
          mapByName[fullName].department_id = deptId;
        }
      }
    });

    // mapByName に溜めたものを配列化して返す
    return Object.values(mapByName);
  }, [state.data, usersMaster, deptLabelToIdMap, userDepartmentByName]);

  // ==== DB の読み仮名マップ（氏名 → 氏名かな） ====
  // USER_INFO_MANAGE の last_name_kana / first_name_kana が入っている人だけを対象に、
  // 「山田 次郎」 → 「やまだ じろう」のような map を作る。
  // さらに、create_by_user_id（メールアドレス）でも引けるようにキーを追加する。
  const userKanaMap: Record<string, string> = useMemo(() => {
    const map: Record<string, string> = {};

    const normalize = (v: any): string | null => {
      if (typeof v !== 'string') return null;
      const s = v.trim();
      return s.length ? s : null;
    };

    const normalizeMail = (v: any): string | null => {
      if (typeof v !== 'string') return null;
      const s = v.trim().toLowerCase();
      return s.length ? s : null;
    };

    usersMaster.forEach((u: any) => {
      const lastName = normalize(u.last_name) ?? '';
      const firstName = normalize(u.first_name) ?? '';
      const lastKana = normalize(u.last_name_kana) ?? '';
      const firstKana = normalize(u.first_name_kana) ?? '';

      // 氏名が空ならスキップ
      if (!lastName && !firstName) return;

      // かなが 1 つも入っていなければスキップ（一般的な読み仮名に任せる）
      if (!lastKana && !firstKana) return;

      const fullName = `${lastName} ${firstName}`.trim();
      const fullKana = `${lastKana} ${firstKana}`.trim();

      if (!fullName || !fullKana) return;

      // 1) 氏名キー（"山田 次郎" → "やまだ じろう"）
      //    同じ「姓・名・フリガナ」のユーザーが複数いても、
      //    最初に出てきたレコードを優先するため、上書きしない。
      if (!map[fullName]) {
        map[fullName] = fullKana;
      }

      // 2) create_by_user_id で join するためのメールアドレスキー
      const mailCandidates = [
        u.mail_address,
        u.email,
        u.mail,
        u.email_address,
        u.loginId,
        u.login_id,
        u.user_email,
        u.user_mail,
      ];

      mailCandidates.forEach((raw) => {
        const key = normalizeMail(raw);
        if (!key) return;
        if (!map[key]) {
          map[key] = fullKana;
        }
      });
    });

    return map;
  }, [usersMaster]);

  // ==== USER_INFO_MANAGE の取得順（若いレコードほど小さい index） ====
  // 「姓・名・フリガナ」が完全一致したときの tie-break 用に、
  // create_by_user_id（メールアドレス）ごとのソートインデックスを持たせる。
  const userSortIndexMap: Record<string, number> = useMemo(() => {
    const map: Record<string, number> = {};

    const normalizeMail = (v: any): string | null => {
      if (typeof v !== 'string') return null;
      const s = v.trim().toLowerCase();
      return s.length ? s : null;
    };

    usersMaster.forEach((u: any, index: number) => {
      const mailCandidates = [
        u.mail_address,
        u.email,
        u.mail,
        u.email_address,
        u.loginId,
        u.login_id,
        u.user_email,
        u.user_mail,
      ];

      mailCandidates.forEach((raw) => {
        const key = normalizeMail(raw);
        if (!key) return;
        // 最初に見つかったメールアドレスの順位を優先する
        if (!(key in map)) {
          map[key] = index;
        }
      });
    });

    return map;
  }, [usersMaster]);

  // 日付初期値（1週間前の月曜イメージ）
  const getDefaultStartDate = (): Date => {
    const today = new Date();
    const thisWeekMon = startOfWeek(today, { weekStartsOn: 1 });
    const lastWeekMon = addDays(thisWeekMon, -7);
    return lastWeekMon;
  };


  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isWeeklyReportRoute = useMemo(() => {
    const normalizePath = (path: string) => path.replace(/\/+$/, '') || '/';
    const hash = String(location.hash ?? '');

    if (hash.startsWith('#/')) {
      const hashPath = normalizePath(hash.slice(1).split('?')[0] ?? '/');
      return hashPath === '/weekly-report' || hashPath.startsWith('/weekly-report/');
    }

    const pathname = normalizePath(String(location.pathname ?? '/'));
    return pathname === '/weekly-report' || pathname.startsWith('/weekly-report/');
  }, [location.hash, location.pathname]);
  const setWeeklySearchParams = useCallback(
    (nextParams: URLSearchParams, options?: { replace?: boolean }) => {
      if (!isWeeklyReportRoute) return;
      setSearchParams(nextParams, options);
    },
    [isWeeklyReportRoute, setSearchParams]
  );
  const hasInitialCreateByParam = useRef(
    searchParams.getAll('create_by').length > 0 ||
    searchParams.getAll('create_by_user_ids').length > 0 ||
    searchParams.getAll('legacy_create_by').length > 0
  );
  const initialCreateByUserIdsRef = useRef(
    searchParams.getAll('create_by_user_ids').map(String)
  );
  const initialCreateByNamesRef = useRef(
    searchParams.getAll('create_by').map(String)
  );
  const initialLegacyCreateByNamesRef = useRef(
    searchParams.getAll('legacy_create_by').map(String)
  );
  const hasInitialReportTypesParam = useRef(
    searchParams.getAll('report_types').length > 0
  );

  // URL 直叩き（初回レンダー時点でクエリがある）かどうかを固定で保持する
  // - URL 経由では profile 由来で反映するのは USER_DEP のみにする（departments は自動付与しない）
  const didInitialAutoSearch = useRef(false);
  const [initialParamsReady, setInitialParamsReady] = useState(false);
  const isInitialSetupDone = useRef(false);
  const lastAutoSearchKeyRef = useRef<string | null>(null);
  const lastUrlSearchKeyRef = useRef<string | null>(null);

  // 初期セット完了を state でも持つ（自動検索のトリガに使う）
  const [initialSetupDone, setInitialSetupDone] = useState(false);

  // 検索フォーム state 群
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const preset = getValidRecentPresetFromParams(searchParams);
    if (preset) {
      return getRecentPresetDateRange(preset).startDate;
    }

    const param = searchParams.get('start_date');
    return (param ? parseYmdToLocalDate(param) : null) ?? getDefaultStartDate();
  });

  const [endDate, setEndDate] = useState<Date | null>(() => {
    const preset = getValidRecentPresetFromParams(searchParams);
    if (preset) {
      return getRecentPresetDateRange(preset).endDate;
    }

    const param = searchParams.get('end_date');
    const parsed = param ? parseYmdToLocalDate(param) : null;
    if (parsed) return parsed;

    return new Date();
  });
  const [freeWord, setFreeWord] = useState<string>(() => searchParams.get('free_word') || '');
  const [searchTitleBody, setSearchTitleBody] = useState<boolean>(() => {
    const parsed = parseBooleanLike(searchParams.get('search_title_body'));
    return parsed ?? true;
  });
  const [searchTags, setSearchTags] = useState<boolean>(() => {
    const parsed = parseBooleanLike(searchParams.get('search_tags'));
    return parsed ?? false;
  });
  const [mailFormatState, setMailFormatState] = useState<MailFormatState>(() => {
    const cached = readMailFormatStateFromLocalStorage();
    return cached ?? DEFAULT_MAIL_FORMAT_STATE;
  });

  const [selectedReportTypes, setSelectedReportTypes] = useState<string[]>(() => {
    const vals = searchParams.getAll('report_types');
    return vals.length ? vals : [];
  });
  const [selectedTeams, setSelectedTeams] = useState<string[]>(() => {
    const vals = searchParams.getAll('teams');
    return vals.length ? vals : [];
  });
  const [selectedCreateBy, setSelectedCreateBy] = useState<string[]>(() => {
    const vals = [
      ...searchParams.getAll('create_by_user_ids'),
      ...searchParams.getAll('create_by'),
      ...searchParams.getAll('legacy_create_by'),
    ];
    return vals.length ? vals : [];
  });

// ===== 開始日付プリセット（今週 / 先週 / 来週）=====
const [startDatePreset, setStartDatePreset] = useState<'this' | 'last' | 'next' | null>(() => {
  return getValidRecentPresetFromParams(searchParams);
});
const [dateSearchMode, setDateSearchMode] = useState<DateSearchMode>(() => {
  return getValidRecentPresetFromParams(searchParams)
    ? 'recent'
    : 'range';
});
const recentPresetRef = useRef<RecentPreset>(
  getValidRecentPresetFromParams(searchParams) ?? 'this'
);
const rangeDateSnapshotRef = useRef<{ startDate: Date | null; endDate: Date | null } | null>(
  null
);



const applyStartDatePreset = useCallback((preset: 'this' | 'last' | 'next') => {
  const nextRange = getRecentPresetDateRange(preset);
  setStartDate(nextRange.startDate);
  setEndDate(nextRange.endDate);
}, []);
  useEffect(() => {
    if (startDatePreset) {
      recentPresetRef.current = startDatePreset;
    }
  }, [startDatePreset]);

  const activateRecentMode = useCallback(() => {
    setEndDatePickerOpen(false);
    rangeDateSnapshotRef.current = {
      startDate: startDate ? new Date(startDate) : null,
      endDate: endDate ? new Date(endDate) : null,
    };

    const nextPreset = startDatePreset ?? recentPresetRef.current ?? 'this';
    setDateSearchMode('recent');
    setStartDatePreset(nextPreset);
    applyStartDatePreset(nextPreset);
  }, [startDate, endDate, startDatePreset, applyStartDatePreset]);

  const activateRangeMode = useCallback(() => {
    setDateSearchMode('range');
    setEndDatePickerOpen(false);
    setStartDatePreset(null);

    const snapshot = rangeDateSnapshotRef.current;
    if (!snapshot) return;

    if (snapshot.startDate) setStartDate(new Date(snapshot.startDate));
    if (snapshot.endDate) setEndDate(new Date(snapshot.endDate));
  }, []);

  const handleDateSearchModeChange =
    (mode: DateSearchMode) => (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.checked) return;
      if (mode === 'recent') {
        activateRecentMode();
        return;
      }
      activateRangeMode();
    };

  const muiTheme = useTheme();
  const currentRecentPreset: RecentPreset = (
    startDatePreset ??
    recentPresetRef.current ??
    'this'
  ) as RecentPreset;
  const recentPresetTone = RECENT_PRESET_TONE[currentRecentPreset];
  const recentPresetButtonSx = {
    minWidth: 70,
    whiteSpace: 'nowrap',
    px: 0.9,
    borderRadius: '10px',
    fontSize: 12.5,
    fontWeight: 700,
    textTransform: 'none',
    color: muiTheme.palette.mode === 'dark' ? '#38bdf8' : '#2563eb',
    backgroundColor: muiTheme.palette.mode === 'dark' ? '#162235' : recentPresetTone.buttonSurface,
    borderColor: muiTheme.palette.mode === 'dark' ? '#26384f' : recentPresetTone.buttonBorder,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: muiTheme.palette.mode === 'dark' ? '#1c2b42' : recentPresetTone.buttonHoverSurface,
      borderColor: muiTheme.palette.mode === 'dark' ? '#35516f' : recentPresetTone.buttonHoverBorder,
    },
    '&.Mui-disabled': {
      color: '#94a3b8',
      backgroundColor: muiTheme.palette.mode === 'dark' ? '#111a2b' : '#f1f5f9',
      borderColor: muiTheme.palette.mode === 'dark' ? '#26384f' : '#e2e8f0',
    },
    '@media (min-width:1200px)': {
      minWidth: 64,
      px: 0.75,
      '& .MuiButton-startIcon': {
        ml: -0.25,
        mr: 0.35,
      },
      '& .MuiButton-endIcon': {
        ml: 0.35,
        mr: -0.25,
      },
      '& .MuiSvgIcon-root': {
        fontSize: 17,
      },
    },
  };
  const shiftRecentPreset = useCallback(
    (direction: -1 | 1) => {
      const currentIdx = RECENT_PRESET_ORDER.indexOf(currentRecentPreset);
      const nextIdx = Math.max(
        0,
        Math.min(RECENT_PRESET_ORDER.length - 1, currentIdx + direction)
      );
      const nextPreset = RECENT_PRESET_ORDER[nextIdx];
      if (!nextPreset || nextPreset === currentRecentPreset) return;
      setStartDatePreset(nextPreset);
      applyStartDatePreset(nextPreset);
    },
    [currentRecentPreset, applyStartDatePreset]
  );

  // ===== 日付のバリデーション（終了日付 < 開始日付を防止） =====
  const [startDatePickerError, setStartDatePickerError] = useState<any>(null);
  const [endDatePickerError, setEndDatePickerError] = useState<any>(null);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

  const dateRangeErrorMsg = useMemo(() => {
    if (!startDate || !endDate) return '';
    if (stripTime(endDate) < stripTime(startDate)) {
      return '終了日付は開始日付以降を指定してください';
    }
    return '';
  }, [startDate, endDate]);

  const startDateHelperText = useMemo(() => {
    if (!startDatePickerError) return '';
    const err = String(startDatePickerError);
    if (err === 'maxDate') return '開始日付は終了日付以前を指定してください';
    if (err === 'invalidDate') return '開始日付が不正です';
    return '開始日付が不正です';
  }, [startDatePickerError]);

  const endDateHelperText = useMemo(() => {
    if (dateRangeErrorMsg) return dateRangeErrorMsg;
    if (!endDatePickerError) return '';
    const err = String(endDatePickerError);
    if (err === 'minDate') return '終了日付は開始日付以降を指定してください';
    if (err === 'invalidDate') return '終了日付が不正です';
    return '終了日付が不正です';
  }, [endDatePickerError, dateRangeErrorMsg]);

  const hasDateError =
    Boolean(startDatePickerError) ||
    Boolean(endDatePickerError) ||
    Boolean(dateRangeErrorMsg);
  const isDateRangeIncomplete = !startDate || !endDate;
  const recentPresetLabel = useMemo(() => {
    if (currentRecentPreset === 'last') return '先週 Last week';
    if (currentRecentPreset === 'next') return '来週 Next week';
    return '今週 This week';
  }, [currentRecentPreset]);
  const recentRangeLabel = useMemo(() => {
    if (!startDate || !endDate) return '-';
    const toSlash = (d: Date) =>
      `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(
        d.getDate()
      ).padStart(2, '0')}`;
    return `${toSlash(startDate)}～${toSlash(endDate)}`;
  }, [startDate, endDate]);

  // 週報一覧と 1:1 に対応する「ハイライト対象の週」
  const highlightedWeeks = useMemo(
    () => buildHighlightedWeeksForRange(startDate, endDate),
    [startDate, endDate]
  );

  // 「課」の選択状態（チップで表示するやつ）
  const [selectedDepts, setSelectedDepts] = useState<Suggestion[]>([]);
  // ユーザーが課のフィルタを触ったことがあるか
  const [deptTouched, setDeptTouched] = useState(false);
  // ユーザーが記入者フィルタを触ったことがあるか（条件なし含む）
  const [createByTouched, setCreateByTouched] = useState(false);

  // 検索結果など
  const [, setLocalResults] = useState<Report[]>([]);
  const [creatorOptionsByDate, setCreatorOptionsByDate] =
    useState<CreatorOptionWithDept[] | null>(null);
  const [creatorOptionsLoadingRemaining, setCreatorOptionsLoadingRemaining] =
    useState(false);
  const [creatorOptionsResolvedKey, setCreatorOptionsResolvedKey] = useState<string | null>(null);
  const [creatorSelectionNormalizedKey, setCreatorSelectionNormalizedKey] = useState<string | null>(null);
  const creatorOptionHistoryRef = useRef<Map<string, CreatorOptionWithDept>>(new Map());
  const [creatorSyncTick, setCreatorSyncTick] = useState(0);
  const creatorSyncForceRef = useRef(false);

  useEffect(() => {
    const timer = window.setInterval(
      () => setCreatorSyncTick((value) => value + 1),
      CREATOR_SYNC_BACKGROUND_INTERVAL_MS
    );
    return () => window.clearInterval(timer);
  }, []);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('URLをコピーしました');
  const [dlgOpen, setDlgOpen] = useState(false);

  // ====== localStorage 用：ユーザーキー（画面用） ======
  // 発行時に create_by_user_id へ保存する認証ユーザーのログインIDを使用する。
  const authenticatedUserId = currentUser?.signInDetails?.loginId;
  const normalizedLoginId = useMemo(() => {
    const u: any = currentUser;
    const p: any = profile;

    const candidates = [
      p?.email,
      p?.mail_address,
      p?.mail,
      p?.email_address,
      p?.loginId,
      p?.login_id,
      u?.signInDetails?.loginId,
      u?.loginId,
      u?.email,
      u?.mail,
      u?.attributes?.email,
      u?.signInUserSession?.idToken?.payload?.email,
      // メール系が取れない環境（federated ログイン等）でも検索条件を保存できるよう、
      // 最後の手段として Cognito の username / userId を使う。
      u?.username,
      u?.userId,
    ];

    for (const c of candidates) {
      const hit = normalizeLoginIdCandidate(c);
      if (hit) return hit;
    }
    return null;
  }, [currentUser, profile]);

  const localStorageKey = useMemo(
    () => buildUserScopedCacheKey(WEEKLY_SEARCH_SCREEN_CACHE_KEY_PREFIX, normalizedLoginId),
    [normalizedLoginId]
  );

  // 検索結果用 localStorage key
  const resultsLocalStorageKey = useMemo(
    () => buildUserScopedCacheKey(WEEKLY_SEARCH_SCREEN_RESULTS_CACHE_KEY_PREFIX, normalizedLoginId),
    [normalizedLoginId]
  );

  const creatorOptionsLocalStorageBaseKey = useMemo(
    () => buildUserScopedCacheKey(WEEKLY_SEARCH_CREATOR_OPTIONS_CACHE_KEY_PREFIX, normalizedLoginId),
    [normalizedLoginId]
  );

  // URL に検索条件が付いているか（URL優先にするため）
  const urlHasSearchCondition = useMemo(() => {
    try {
      const keys = [
        'wr_id',
        'start_date',
        'end_date',
        'free_word',
        'search_title_body',
        'search_tags',
        'report_types',
        'teams',
        'create_by',
        'create_by_user_ids',
        'legacy_create_by',
        'departments',
        'temporary_flg',
        CREATE_BY_NONE_PARAM,
        START_DATE_PRESET_PARAM,
      ];
      return keys.some((k) => searchParams.getAll(k).length > 0);
    } catch {
      return false;
    }
  }, [searchParams]);

  // localStorage から復元した課ID（deptOptions が揃った後に反映するため）
  const pendingDeptIdsRef = useRef<string[] | null>(null);
  // 課マスタの取得前でも、保存済みの課IDで発行者200件を先行取得する。
  // selectedDepts はマスタから表示名を解決するまで空のため、別stateで保持する。
  const [restoredDeptIdsForCreator, setRestoredDeptIdsForCreator] =
    useState<string[]>([]);

  // localStorage の復元が完了したか（保存の開始ガード）
  const [didHydrateFromLocalStorage, setDidHydrateFromLocalStorage] =
    useState(false);

  // dept 復元の完了フラグ（ref だけだと自動検索が再実行されないため）
  const [pendingDeptHydrationDone, setPendingDeptHydrationDone] = useState(true);

  // この画面で「戻ったら1回自動検索したい」か
  const [autoSearchRequested, setAutoSearchRequested] = useState(false);

  // 最後の検索情報（cache にも保存する）
  const [lastSearchMeta, setLastSearchMeta] = useState<{
    at: string;
    query: string;
  } | null>(null);
  const [lastWrId, setLastWrId] = useState<string | null>(null);

  // state 反映待ちで expectedQuery が null になるのを防ぐ
  const lastSearchMetaRef = useRef<{ at: string; query: string } | null>(null);

  // 検索結果を復元したか
  const [didHydrateResultsFromLocalStorage, setDidHydrateResultsFromLocalStorage] =
    useState(false);

  // 復元で結果を適用したか（auto search が 0 件で上書きするのを防ぐ）
  const didRestoreResultsFromLocalStorageRef = useRef(false);
  // results-cache を復元した直後、1回だけ revalidate するためのフラグ
  const swrRevalidatePendingRef = useRef(false);

  // loginId が確定するまで results 復元を待つためのタイマー
  const hydrateResultsWaitTimerRef = useRef<number | null>(null);
  // loginId が確定するまで screen cache 復元を待つためのタイマー（latest が無い場合の保険）
  const hydrateScreenCacheWaitTimerRef = useRef<number | null>(null);


  // 検索の並走（race）で 2→0 のように上書きされるのを防ぐ
  const searchRequestSeqRef = useRef(0);
  const searchAbortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const [searchLoading, setSearchLoading] = useState(false);

  const isCurrentSearchRequest = useCallback(
    (seq: number, controller: AbortController) =>
      mountedRef.current &&
      searchRequestSeqRef.current === seq &&
      searchAbortRef.current === controller &&
      !controller.signal.aborted,
    []
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      searchRequestSeqRef.current += 1;
      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
        searchAbortRef.current = null;
      }
    };
  }, []);

  const latestSearchParamsRef = useRef(searchParams);
  const activeSearchAbortRef = useRef<AbortController | null>(null);
  const previousNonSearchParamsKeyRef = useRef(
    buildNonSearchParamsFingerprint(searchParams)
  );

  const abortActiveSearch = useCallback((reason: string) => {
    searchRequestSeqRef.current += 1;

    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
      searchAbortRef.current = null;
    }

    if (activeSearchAbortRef.current) {
      activeSearchAbortRef.current.abort();
      activeSearchAbortRef.current = null;
    }

    console.debug('[SearchArea] active search aborted:', reason);
  }, []);

  const previousViewerAccessContextKeyRef = useRef<string | null>(
    viewerAccessContextKey
  );

  useEffect(() => {
    const previousKey = previousViewerAccessContextKeyRef.current;
    if (previousKey === viewerAccessContextKey) return;

    previousViewerAccessContextKeyRef.current = viewerAccessContextKey;
    abortActiveSearch('viewer-access-context-changed');
    setSearchLoading(false);
    setLocalResults([]);
    onResults([]);
    lastAutoSearchKeyRef.current = null;
    creatorSyncForceRef.current = true;
    setCreatorSyncTick((value) => value + 1);
    setAutoSearchRequested(true);
  }, [abortActiveSearch, onResults, viewerAccessContextKey]);

  useEffect(() => {
    latestSearchParamsRef.current = searchParams;
  }, [searchParams]);

  useEffect(() => {
    const nextKey = buildNonSearchParamsFingerprint(searchParams);
    const prevKey = previousNonSearchParamsKeyRef.current;

    previousNonSearchParamsKeyRef.current = nextKey;

    if (prevKey !== nextKey) {
      abortActiveSearch('non-search-route-param-changed');
    }
  }, [abortActiveSearch, searchParams]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      abortActiveSearch('unmount');

    };
  }, [abortActiveSearch]);

  // ===== 週報更新通知の “最後に処理した時刻(ms)”（同じ更新で何度も叩かない） =====
  const lastHandledUpdatedAtMsRef = useRef<number | null>(null);

  const computeNeedsRevalidateByUpdateSignal = useCallback(() => {
    const updatedAtMs = readLatestWeeklyDataInvalidationMs() || null;

    const lastSearchAtIso =
      lastSearchMetaRef.current?.at ?? lastSearchMeta?.at ?? null;
    const lastSearchQuery =
      lastSearchMetaRef.current?.query ?? lastSearchMeta?.query ?? null;
    const lastSearchAtMs = safeParseIsoToMs(lastSearchAtIso);

    const resultsSavedAtMs = readResultsCacheSavedAtMs(
      resultsLocalStorageKey,
      viewerAccessContextKey ?? '',
      lastSearchQuery
    );

    // いま画面が持っている “基準時刻” （この時刻より新しい更新通知なら再フェッチしたい）
    const baseMs = Math.max(lastSearchAtMs ?? 0, resultsSavedAtMs ?? 0);

    const alreadyHandledMs = lastHandledUpdatedAtMsRef.current;

    const isNewSignal =
      updatedAtMs != null &&
      (alreadyHandledMs == null || updatedAtMs > alreadyHandledMs);

    const needs = updatedAtMs != null && updatedAtMs > baseMs && isNewSignal;

    return { needs, updatedAtMs, baseMs, lastSearchAtMs, resultsSavedAtMs };
  }, [lastSearchMeta, resultsLocalStorageKey, viewerAccessContextKey]);


  // ログインユーザーが属している課ID（初期のデフォルト絞り込み用）
  // currentUser の直接の情報 or usersMaster から loginId を突合して決める
  const myDeptId: string | null = useMemo(() => {
    const u: any = currentUser;
    if (!u) return null;
    const resolveDeptIdByLabel = (raw: unknown): string | null => {
      const label = String(raw ?? '').trim();
      if (!label) return null;

      const direct = deptLabelToIdMap[label];
      if (direct) return String(direct);

      const normalized = normalizeScopeCode(label);
      if (!normalized) return null;

      const hit = Object.entries(deptLabelToIdMap).find(
        ([deptLabel]) => normalizeScopeCode(deptLabel) === normalized
      );
      return hit?.[1] ? String(hit[1]) : null;
    };

    // A. currentUser が自分の課を持ってる場合
    const directDeptLabel = String(u.department || u.department_name || '').trim() || null;
    const resolvedFromDirect = resolveDeptIdByLabel(directDeptLabel);
    if (resolvedFromDirect) {
      return resolvedFromDirect;
    }
    if (u.department_id != null) {
      return String(u.department_id);
    }

    // B. email(loginId)から usersMaster を逆引き
    const loginEmail = u?.signInDetails?.loginId || u?.loginId || null;
    if (!loginEmail) return null;

    const matched = usersMaster.find((rec: any) => {
      const candidates = [
        rec.email,
        rec.mail,
        rec.mail_address,
        rec.email_address,
        rec.loginId,
        rec.login_id,
        rec.user_email,
        rec.user_mail,
      ]
        .filter(Boolean)
        .map((s: any) => String(s).trim().toLowerCase());

      return candidates.includes(String(loginEmail).trim().toLowerCase());
    });

    if (!matched) return null;

    const labelFromMatched = matched.department || matched.department_name || null;
    const resolvedFromMatched = resolveDeptIdByLabel(labelFromMatched);
    if (resolvedFromMatched) {
      return resolvedFromMatched;
    }
    if (matched.department_id != null) {
      return String(matched.department_id);
    }

    return null;
  }, [currentUser, usersMaster, deptLabelToIdMap]);

  // USER_DEP の解決：
  // - URL 直叩きなどで profile がまだ空でも付与できるように、currentUser / myDeptId でフォールバックする
  const defaultUserDepForSearch = useMemo((): string => {
    const fromProfile = String(
      (profile as any)?.division ??
        (profile as any)?.department ??
        profile?.department ??
        ''
    ).trim();
    if (fromProfile) return fromProfile;

    const u: any = currentUser;
    const fromCurrentUser = String(
      u?.division ?? u?.department ?? u?.department_name ?? ''
    ).trim();
    if (fromCurrentUser) return fromCurrentUser;

    const deptId =
      u?.department_id != null
        ? String(u.department_id)
        : myDeptId
        ? String(myDeptId)
        : '';
    const departmentRow = deptId
      ? (state.data?.departments ?? []).find(
          (department: any) => getDepartmentMasterId(department) === deptId
        )
      : null;
    const fromDeptId = String(
      departmentRow?.value ?? departmentRow?.label ?? ''
    ).trim();
    if (fromDeptId) return fromDeptId;

    return '';
  }, [profile, currentUser, myDeptId, state.data?.departments]);

  const userDepForSearch = useMemo(
    () => String(defaultUserDepForSearch ?? '').trim(),
    [defaultUserDepForSearch]
  );

  // ログインユーザーの課がマスタ課IDへ解決できたかを明示的に判定する。
  // 解決できない場合は USER_DEP を付けず、クライアント側の厳格な公開範囲フィルタも適用しない。
  const resolvedLoginUserDeptId = useMemo((): string | null => {
    const resolvedFromUserDep = resolveDepartmentIdFromValue(
      userDepForSearch,
      deptLabelToIdMap
    );
    if (resolvedFromUserDep) {
      return resolvedFromUserDep;
    }

    const resolvedFromMyDeptId = normalizeDepartmentId(myDeptId);
    if (resolvedFromMyDeptId) {
      return resolvedFromMyDeptId;
    }

    return null;
  }, [userDepForSearch, deptLabelToIdMap, myDeptId]);

  // 課フィルターの実効値
  // - ユーザーが何か課を選んでればそれ
  // - 課をまだ触ってなければ自分の課 (myDeptId) または URL 指定
  // - 課を触って外した後は [] = 全課
  const urlDeptIds = useMemo(() => {
    try {
      const vals = searchParams.getAll('departments');
      return vals.map((v) => String(v));
    } catch {
      return [];
    }
  }, [searchParams]);

  const effectiveDeptIds = useMemo(() => {
    // 1) 課フィルタ未操作の場合のみ URL の条件を最優先
    if (!deptTouched && urlDeptIds.length > 0) {
      return urlDeptIds;
    }

    // 2) UIで選択されている課
    const picked = selectedDepts.map((d) => String(d.id));
    if (picked.length > 0) {
      return picked;
    }

    // 3) 絞りなし（全課）
    return [];
  }, [urlDeptIds, selectedDepts, deptTouched]);

  const effectiveUserDepForVisibility = useMemo(() => {
    const preferred = String(userDepForSearch ?? '').trim();
    if (preferred) return preferred;

    if (myDeptId) {
      const row = (state.data?.departments ?? []).find(
        (department: any) => getDepartmentMasterId(department) === String(myDeptId)
      );
      const byMyDept = String(row?.value ?? row?.label ?? '').trim();
      if (byMyDept) return byMyDept;
    }

    return '';
  }, [userDepForSearch, myDeptId, state.data?.departments]);

  const userDepForScopedSearch = useMemo(() => {
    const raw = String(effectiveUserDepForVisibility ?? '').trim();
    const departmentRow = resolvedLoginUserDeptId
      ? (state.data?.departments ?? []).find(
          (department: any) =>
            getDepartmentMasterId(department) === String(resolvedLoginUserDeptId)
        )
      : null;
    return String(departmentRow?.value ?? raw).trim();
  }, [effectiveUserDepForVisibility, resolvedLoginUserDeptId, state.data?.departments]);

  const isViewerAccessContextReady = useMemo(
    () =>
      Boolean(
        viewerAccessContextKey &&
          resolvedLoginUserDeptId &&
          userDepForScopedSearch
      ),
    [viewerAccessContextKey, resolvedLoginUserDeptId, userDepForScopedSearch]
  );
  const shouldApplyVisibilityScope = isViewerAccessContextReady;

  const resolveEffectiveAccessForRow = (row: Report, accessMap = reportTypeAccessById ?? {}): EffectiveReportTypeAccess => {
  const attrs = Array.isArray((row as any)?.attributes) ? (row as any).attributes : [];
  const reportTypeAttr = attrs.find((a: any) => {
    if (Number((a as any)?.attr_id) === 1) return true;
    const name = String((a as any)?.attr_name ?? '').trim();
    return name === CLASSIFICATION_MARKET_TARGET_ATTR_NAME;
  });

  // 同名の分類はチームごとに別設定。無効化された option の設定も過去週報に対して
  // 維持するため、有効な分類マスタにアクセスマップ由来の option を合成して解決する。
  return resolveEffectiveAccessForReportTypeValue({
    raw: (reportTypeAttr as any)?.attr_value,
    options: resolveReportTypeAccessOptions(rawReportTypes, accessMap),
    departmentId: resolveRowDepartmentId(row, deptLabelToIdMap),
    teamId: resolveRowTeamId(row),
    accessMap,
  });
};

  // 発行者候補同期専用の課条件
  // - selectedDepts ベースで判定し、未選択は「課=条件なし（全課）」として扱う
  const creatorSyncDeptIds = useMemo(() => {
    const selectedIds = (selectedDepts ?? []).map((d) => String(d.id));
    if (selectedIds.length > 0) {
      return Array.from(new Set(selectedIds)).sort();
    }

    // selectedDepts は課マスタ取得後に復元されるため、それまでは保存済みIDを使う。
    if (!pendingDeptHydrationDone && restoredDeptIdsForCreator.length > 0) {
      return Array.from(new Set(restoredDeptIdsForCreator.map(String))).sort();
    }

    // URLの課IDはマスタがなくても利用できる。
    return Array.from(new Set((effectiveDeptIds ?? []).map(String))).sort();
  }, [
    selectedDepts,
    pendingDeptHydrationDone,
    restoredDeptIdsForCreator,
    effectiveDeptIds,
  ]);

  const creatorSyncTeamIds = useMemo(() => {
    return Array.from(new Set((selectedTeams ?? []).map(String))).sort();
  }, [selectedTeams]);

  // profile/currentUser の課コードはAPI側で課IDへ解決する。
  // 課マスタを待たずに公開範囲付きの200件取得を開始できる。
  const creatorUserDepForScope = useMemo(
    () => String(userDepForSearch ?? '').trim(),
    [userDepForSearch]
  );
  const shouldApplyCreatorVisibilityScope =
    isViewerAccessContextReady && Boolean(creatorUserDepForScope);

  const creatorOptionsCriteriaKey = useMemo(() => {
    if (!startDate || !endDate) return null;
    const deptIds = Array.from(new Set(creatorSyncDeptIds)).sort();
    const teamIds = Array.from(new Set(creatorSyncTeamIds)).sort();
    const deptMode = deptIds.length === 0 ? 'dept_none' : 'dept_filtered';
    const teamMode = teamIds.length === 0 ? 'team_none' : 'team_filtered';
    const visibilityScope = shouldApplyCreatorVisibilityScope
      ? creatorUserDepForScope.toLocaleLowerCase()
      : 'scope_none';
    return `${formatDateYmd(startDate)}|${formatDateYmd(endDate)}|${deptIds.join(',')}|${deptMode}|${teamIds.join(',')}|${teamMode}|${visibilityScope}`;
  }, [
    startDate,
    endDate,
    creatorSyncDeptIds,
    creatorSyncTeamIds,
    shouldApplyCreatorVisibilityScope,
    creatorUserDepForScope,
  ]);

  // チーム候補
  const teamOptionsFiltered: Option[] = useMemo(() => {
    if (effectiveDeptIds.length === 0) {
      // 全課なので全チーム
      return allTeamOptionsRaw;
    }
    return allTeamOptionsRaw.filter((t) => {
      return t.department_id && effectiveDeptIds.includes(String(t.department_id));
    });
  }, [allTeamOptionsRaw, effectiveDeptIds]);

  // 記入者候補
  const createByOptionsFiltered: Option[] = useMemo(() => {
    // 専用APIが期間・課・公開範囲をすべて適用済み。旧マスタ候補や
    // department_idによる再絞り込みは、条件外候補の露出/欠落を招くため使わない。
    const source = creatorOptionsByDate ?? [];
    const nameCounts = new Map<string, number>();
    source.forEach((option) => {
      const name = String(option.value ?? '').trim();
      nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
    });
    return source.map((option) => {
      const name = String(option.value ?? '').trim();
      if ((nameCounts.get(name) ?? 0) < 2) return option as Option;
      const discriminator = String(option.user_id ?? option.id).trim();
      return {
        ...option,
        displayValue: `${name}（${discriminator}）`,
      } as Option;
    });
  }, [creatorOptionsByDate]);

  // 選択済みuser_idが期間変更で候補外になっても氏名条件へ誤変換しないよう、
  // 現在/過去の専用API候補とユーザーマスタのID種別を検索用に保持する。
  const createByOptionsForSearch: CreatorOptionWithDept[] = useMemo(() => {
    const byId = new Map<string, CreatorOptionWithDept>();
    creatorOptionsRaw.forEach((option) => byId.set(String(option.id), option));
    creatorOptionHistoryRef.current.forEach((option) =>
      byId.set(String(option.id), option)
    );
    (creatorOptionsByDate ?? []).forEach((option) => {
      byId.set(String(option.id), option);
    });
    (usersMaster ?? []).forEach((user: any) => {
      const userId = String(
        user?.mail_address ?? user?.email ?? user?.mail ?? user?.loginId ?? ''
      ).trim();
      if (!userId || byId.has(userId)) return;
      byId.set(userId, {
        id: userId,
        value: userId,
        user_id: userId,
        legacy: false,
      });
    });
    return Array.from(byId.values());
  }, [creatorOptionsByDate, creatorOptionsRaw, usersMaster]);

  useEffect(() => {
    (creatorOptionsByDate ?? []).forEach((option) => {
      creatorOptionHistoryRef.current.set(String(option.id), option);
    });
  }, [creatorOptionsByDate]);

  // 旧キャッシュでは週報IDを発行者の選択IDとして保存していた。
  // 専用APIの user_id / legacy ID が届いた時点で、表示名を介して新IDへ移行する。
  useEffect(() => {
    if (creatorOptionsByDate == null) return;
    if (creatorOptionsLoadingRemaining) return;
    if (!creatorOptionsCriteriaKey) return;
    if (creatorOptionsResolvedKey !== creatorOptionsCriteriaKey) return;

    const normalizeLabel = (value: unknown) =>
      String(value ?? '').trim().replace(/\s+/g, ' ');
    const currentIds = new Set(
      creatorOptionsByDate.map((option) => String(option.id))
    );
    const nextIdsByLabel = new Map<string, string[]>();
    creatorOptionsByDate.forEach((option) => {
      const label = normalizeLabel(option.value);
      if (!label) return;
      const ids = nextIdsByLabel.get(label) ?? [];
      ids.push(String(option.id));
      nextIdsByLabel.set(label, ids);
    });
    const oldLabelById = new Map<string, string>();
    creatorOptionsRaw.forEach((option) => {
      oldLabelById.set(String(option.id), String(option.value ?? '').trim());
    });
    const knownDedicatedIds = new Set(
      createByOptionsForSearch
        .filter((option) => Boolean(option.user_id) || Boolean(option.legacy))
        .map((option) => String(option.id))
    );

    setSelectedCreateBy((prev) => {
      const migrated = (prev ?? []).map((rawId) => {
        const id = String(rawId);
        if (currentIds.has(id)) return id;
        if (knownDedicatedIds.has(id)) return id;
        if (initialCreateByNamesRef.current.includes(id)) return id;
        if (initialLegacyCreateByNamesRef.current.includes(id)) {
          const legacyMatches = creatorOptionsByDate.filter(
            (option) => option.legacy && normalizeLabel(option.value) === normalizeLabel(id)
          );
          return legacyMatches.length === 1 ? String(legacyMatches[0].id) : id;
        }

        // 氏名を確実に復元できる旧wr_idだけを移行対象にする。
        const rawLabel = oldLabelById.get(id);
        if (!rawLabel) return id;
        const matches = nextIdsByLabel.get(normalizeLabel(rawLabel)) ?? [];
        if (matches.length === 1) return matches[0];

        // 同名が複数なら任意のuser_idへ狭めず、従来の氏名条件を維持する。
        return rawLabel;
      });
      return isSameStringArray(prev ?? [], migrated) ? prev : migrated;
    });
    setCreatorSelectionNormalizedKey(creatorOptionsCriteriaKey);
  }, [
    creatorOptionsByDate,
    creatorOptionsRaw,
    createByOptionsForSearch,
    creatorOptionsCriteriaKey,
    creatorOptionsResolvedKey,
    creatorOptionsLoadingRemaining,
  ]);

  // ＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
  // 課の切替で、子フィルタの“選択値”を options と整合させる
  // - localStorage 復元直後の dept 反映では、保存済みのチーム/発行者を消さない
  // - チーム/ユーザーの別 API が未完了の間は、候補0件を「本当に0件」と判定しない
  // - マスタ取得完了後だけ、表示候補に存在しない選択値を sanitize する
  // ＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
  const prevEffectiveDeptIdsRef = useRef<string[]>(effectiveDeptIds);
  const skipNextDeptChildSyncRef = useRef(false);

  useEffect(() => {
    const prevDeptIds = prevEffectiveDeptIdsRef.current;
    prevEffectiveDeptIdsRef.current = effectiveDeptIds;

    // 初期復元・URL正規化の途中で触ると事故るので gate
    if (state.loading) return;
    if (!didHydrateFromLocalStorage) return;
    if (!initialSetupDone) return;
    if (!pendingDeptHydrationDone) return;

    const normalizeIds = (ids: string[]) =>
      Array.from(new Set((ids ?? []).map(String))).sort();

    const prevKey = normalizeIds(prevDeptIds).join(',');
    const nextKey = normalizeIds(effectiveDeptIds).join(',');

    // 課が変わっていないなら何もしない
    if (prevKey === nextKey) return;

    // localStorage の課復元で selectedDepts を反映した直後は、
    // 保存済みのチーム/発行者をそのまま残す。
    if (skipNextDeptChildSyncRef.current) {
      skipNextDeptChildSyncRef.current = false;
      return;
    }

    const canSyncTeamOptions = teamsLoaded;
    const canSyncReportTypeOptions = !state.loading;

    // 1) 課が「条件なし（空）」になった瞬間は、子を全部クリア
    if (!nextKey) {
      setSelectedTeams([]);
      setSelectedCreateBy([]);
      setSelectedReportTypes([]);

      // 記入者は「空=条件なし」を明示したいので touched も立てる
      setCreateByTouched(true);
      return;
    }

    // 2) 課は残っている場合、各マスタが取得済みのものだけ sanitize する

    // ---- チーム ----
    if (canSyncTeamOptions) {
      if (!Array.isArray(teamOptionsFiltered) || teamOptionsFiltered.length === 0) {
        setSelectedTeams([]);
      } else {
        setSelectedTeams((prev) => {
          const next = sanitizeSelectedIdsByOptions(prev ?? [], teamOptionsFiltered);
          if (isSameStringArray(prev ?? [], next)) return prev;
          return next;
        });
      }
    }

    // 記入者は次の課条件に対する専用APIが非同期で到着するまで保持する。

    // ---- 週報分類（この effect の位置的に reportOptionsFiltered が未定義でも動くよう、raw から算出）----
    const reportOptionsFilteredNow: Option[] =
      effectiveDeptIds.length === 0
        ? (reportOptionsRaw as unknown as Option[])
        : (reportOptionsRaw as unknown as Option[]).filter((r: any) => {
            return r.department_id && effectiveDeptIds.includes(String(r.department_id));
          });

    if (canSyncReportTypeOptions) {
      if (!Array.isArray(reportOptionsFilteredNow) || reportOptionsFilteredNow.length === 0) {
        setSelectedReportTypes([]);
      } else {
        setSelectedReportTypes((prev) => {
          const next = sanitizeSelectedIdsByOptions(prev ?? [], reportOptionsFilteredNow);
          if (isSameStringArray(prev ?? [], next)) return prev;
          return next;
        });
      }
    }
  }, [
    state.loading,
    didHydrateFromLocalStorage,
    initialSetupDone,
    pendingDeptHydrationDone,
    effectiveDeptIds,
    teamOptionsFiltered,
    reportOptionsRaw,
    teamsLoaded,
  ]);

// ==============================
// 週報分類候補（課単位絞り込み + label で重複を除去）
// ==============================
const reportOptionsFiltered: Option[] = useMemo(() => {

  // 1) 課フィルタ無しなら全 raw
  const rawList: Option[] =
    effectiveDeptIds.length === 0
      ? (reportOptionsRaw as unknown as Option[])
      : (reportOptionsRaw as unknown as Option[]).filter((r: any) => {
          return (
            r.department_id &&
            effectiveDeptIds.includes(String(r.department_id))
          );
        });

  // 2) label 重複を除去（最初に出た label を採用）
  const seen = new Set<string>();
  const uniqueByLabel: Option[] = [];

  rawList.forEach((opt) => {
    const label = String(opt.value ?? '').trim();
    if (!label) return; // 空ラベルは除外
    if (seen.has(label)) return;
    seen.add(label);
    uniqueByLabel.push(opt);
  });

  return uniqueByLabel;
}, [reportOptionsRaw, effectiveDeptIds]);

  const [, setReportTypeVisibilityById] = useState<
    Record<string, ReportTypeVisibility> | null
  >(null);
  const [reportTypeAccessById, setReportTypeAccessById] = useState<
    Record<string, ReportTypeAccessSetting> | null
  >(null);
  const reportTypeAccessByIdRef = useRef<
    Record<string, ReportTypeAccessSetting> | null
  >(null);
  const softPublicScopeRefreshRef = useRef(false);
  const lastPassivePublicScopeRefreshAtRef = useRef(0);
  const [reportTypeAccessLoadFailed, setReportTypeAccessLoadFailed] =
    useState(false);
  const [publicScopeRevision, setPublicScopeRevision] = useState(0);
  const isReportTypeAccessReady = reportTypeAccessById !== null;
  const weeklySearchBusy =
    state.loading ||
    searchLoading ||
    !isViewerAccessContextReady ||
    !didHydrateFromLocalStorage ||
    !didHydrateResultsFromLocalStorage ||
    !pendingDeptHydrationDone ||
    !isReportTypeAccessReady;

  useEffect(() => {
    onBusyChange?.(weeklySearchBusy);
  }, [onBusyChange, weeklySearchBusy]);

  useEffect(() => {
    return () => {
      onBusyChange?.(false);
    };
  }, [onBusyChange]);

  useEffect(() => {
    let cancelled = false;
    const isSoftRefresh =
      softPublicScopeRefreshRef.current &&
      reportTypeAccessByIdRef.current !== null;
    softPublicScopeRefreshRef.current = false;

    const load = async () => {
      setReportTypeAccessLoadFailed(false);

      // 初回表示・明示的な公開範囲変更時は従来どおり fail-close で再取得する。
      // focus / visibilitychange による再検証時だけは、既存一覧を表示したまま
      // バックグラウンドで最新の公開範囲を確認する。
      if (!isSoftRefresh) {
        setReportTypeVisibilityById(null);
        reportTypeAccessByIdRef.current = null;
        setReportTypeAccessById(null);
      }

      try {
        const rows = await fetchLatestWeeklyReportPublicSettings();
        if (cancelled) return;

        const nextAccess = buildWeeklyReportAccessMap(
          rows,
          rawReportTypeIdSet,
          state.data ?? {}
        );
        const previousAccess = reportTypeAccessByIdRef.current;
        const accessChanged =
          isSoftRefresh &&
          previousAccess !== null &&
          buildReportTypeAccessMapFingerprint(previousAccess) !==
            buildReportTypeAccessMapFingerprint(nextAccess);

        reportTypeAccessByIdRef.current = nextAccess;
        setReportTypeAccessById(nextAccess);
        setReportTypeAccessLoadFailed(false);

        if (accessChanged) {
          // 別ブラウザ等で公開範囲が変更されていた場合だけ、古い表示を破棄して再検索する。
          abortActiveSearch('public-scope-changed-after-focus');
          setLocalResults([]);
          onResults([]);
          lastAutoSearchKeyRef.current = null;
          setAutoSearchRequested(true);
        }
      } catch (err) {
        if (cancelled) return;

        console.error('公開範囲設定の取得に失敗しました:', err);

        // 公開範囲を確認できない場合は、古い権限の表示を残さない。
        // 通常の画面復帰では成功時に表示を維持するが、取得失敗時は fail-close とする。
        abortActiveSearch('public-scope-refresh-failed');
        setReportTypeVisibilityById(null);
        reportTypeAccessByIdRef.current = null;
        setReportTypeAccessById(null);
        setLocalResults([]);
        onResults([]);
        setReportTypeAccessLoadFailed(true);
        lastAutoSearchKeyRef.current = null;
        setAutoSearchRequested(true);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [
    abortActiveSearch,
    onResults,
    publicScopeRevision,
    rawReportTypeIdSet,
    state.data?.departments,
    state.data?.teams,
  ]);

  useEffect(() => subscribeWeeklyReportPublicScopeRefresh((reason) => {
    if (reason === 'focus' || reason === 'visibility') {
      // 画面へ戻っただけでは一覧を消さない。
      // 公開範囲だけをバックグラウンド再検証し、差分がある場合だけ再検索する。
      const now = Date.now();
      if (now - lastPassivePublicScopeRefreshAtRef.current < 500) return;
      lastPassivePublicScopeRefreshAtRef.current = now;
      softPublicScopeRefreshRef.current = true;
      setPublicScopeRevision((value) => value + 1);
      return;
    }

    // マスタ設定保存の明示通知は権限変更が確定しているため、
    // 既存表示を即時破棄して最新条件で再検索する。
    softPublicScopeRefreshRef.current = false;
    abortActiveSearch('public-scope-changed');
    setLocalResults([]);
    onResults([]);
    reportTypeAccessByIdRef.current = null;
    setReportTypeAccessById(null);
    setPublicScopeRevision((value) => value + 1);
    lastAutoSearchKeyRef.current = null;
    setAutoSearchRequested(true);
  }), [abortActiveSearch, onResults]);

  const getReportTypeIdsForSearch = useCallback(
    (selectedIds: string[]): string[] => {
      const uniqSelected = Array.from(
        new Set(
          (selectedIds ?? [])
            .map((id) => String(id ?? '').trim())
            .filter((id) => id.length > 0)
        )
      );

      if (uniqSelected.length === 0) {
        return [];
      }

      if (!Array.isArray(reportOptionsFiltered) || reportOptionsFiltered.length === 0) {
        return uniqSelected;
      }

      const visibleOptionIds = Array.from(
        new Set(
          reportOptionsFiltered
            .map((o: any) => String(o?.id ?? '').trim())
            .filter((id) => id.length > 0)
        )
      );
      const allowed = new Set(visibleOptionIds);
      const validSelected = uniqSelected.filter((id) => allowed.has(id));

      // 「すべてを選択」は、未選択と同じく report_types を送らない。
      // 全件を複数パラメータで送ると URL が肥大化し、分類マスタ追加時にもキャッシュ差分が増えるため。
      // 一部選択のときだけ、チーム/発行者と同じく選択値をすべて OR 条件として送る。
      const isAllVisibleSelected =
        visibleOptionIds.length > 0 &&
        validSelected.length === visibleOptionIds.length &&
        visibleOptionIds.every((id) => validSelected.includes(id));

      if (isAllVisibleSelected) {
        return [];
      }

      return validSelected;
    },
    [reportOptionsFiltered]
  );


/* =========================================================
 * ブラウザ再起動・別タブ対応の自動検索再実行ロジック
 * ======================================================= */

  // selectedReportTypes が「表示中の options に存在しないID」削除
  // localStorage 復元や、課フィルタ切替時の options 再生成で起きるため、
  // “表示中の週報分類 options” に存在するIDだけ残す。
  useEffect(() => {
    // 初期復元・URL正規化が終わる前に触ると、
    // URLの report_types（ラベル→ID変換中）を潰し得るので gate する
    if (!didHydrateFromLocalStorage) return;
    if (!initialSetupDone) return;

    // options が空の瞬間（ロード中など）の “一瞬 []” で全消ししない
    if (!Array.isArray(reportOptionsFiltered) || reportOptionsFiltered.length === 0) {
      return;
    }

    setSelectedReportTypes((prev) => {
      const next = sanitizeSelectedIdsByOptions(prev ?? [], reportOptionsFiltered);
      if (isSameStringArray(prev ?? [], next)) return prev;
      return next;
    });
  }, [
    didHydrateFromLocalStorage,
    initialSetupDone,
    reportOptionsFiltered,
  ]);


  // 日付を yyyy-mm-dd に
  const formatLocalDate = (d: Date): string => {
    return formatDateYmd(d);
  };

  const progressiveCreatorOptions = useProgressiveCreatorOptions({
    enabled:
      Boolean(startDate && endDate) &&
      !hasDateError &&
      didHydrateFromLocalStorage &&
      pendingDeptHydrationDone &&
      isViewerAccessContextReady,
    criteriaKey: creatorOptionsCriteriaKey,
    loginId: normalizedLoginId,
    startDate: startDate ? formatDateYmd(startDate) : '',
    endDate: endDate ? formatDateYmd(endDate) : '',
    departmentIds: creatorSyncDeptIds,
    teamIds: creatorSyncTeamIds,
    userDep: shouldApplyCreatorVisibilityScope ? creatorUserDepForScope : undefined,
    userTeamId: profile?.preferredTeamId,
    revalidateToken: creatorSyncTick,
    forceRevalidateRef: creatorSyncForceRef,
    latestUpdateMs: readLatestWeeklyDataInvalidationMs(),
    legacyCacheKey: creatorOptionsCriteriaKey
      ? `${creatorOptionsLocalStorageBaseKey}::${creatorOptionsCriteriaKey}`
      : null,
    legacyCacheVersion: WEEKLY_SEARCH_CREATOR_OPTIONS_CACHE_VERSION,
    setOptions: setCreatorOptionsByDate,
    setLoadingRemaining: setCreatorOptionsLoadingRemaining,
    setResolvedKey: setCreatorOptionsResolvedKey,
  });

  const creatorOptionsReadyForCurrentCriteria =
    pendingDeptHydrationDone &&
    creatorOptionsCriteriaKey != null &&
    creatorOptionsResolvedKey === creatorOptionsCriteriaKey &&
    !creatorOptionsLoadingRemaining &&
    progressiveCreatorOptions.allOptionsLoaded;

  // 課・チーム変更後の候補が0名で確定した場合、以前の条件で選択した発行者を解除する。
  // 読み込み途中や取得失敗は0名確定ではないため維持し、完成済み結果だけを根拠にする。
  // 表示側にも同じ正規化結果を同期的に渡すため、effect適用前の1描画も旧氏名を出さない。
  const selectedCreateByForDisplay = clearCreatorSelectionWhenResolvedEmpty(
    selectedCreateBy,
    creatorOptionsByDate,
    creatorOptionsReadyForCurrentCriteria,
    { preserveSelectedUserId: authenticatedUserId }
  );

  useEffect(() => {
    if (!creatorOptionsReadyForCurrentCriteria) return;

    setSelectedCreateBy((prev) =>
      clearCreatorSelectionWhenResolvedEmpty(
        prev,
        creatorOptionsByDate,
        creatorOptionsReadyForCurrentCriteria,
        { preserveSelectedUserId: authenticatedUserId }
      )
    );

    if (creatorOptionsByDate?.length === 0 && creatorOptionsCriteriaKey) {
      setCreatorSelectionNormalizedKey(creatorOptionsCriteriaKey);
    }
  }, [
    creatorOptionsByDate,
    creatorOptionsCriteriaKey,
    creatorOptionsReadyForCurrentCriteria,
    authenticatedUserId,
  ]);

  // ========== 検索画面 localStorage 復元（URLに条件がある場合はURL優先） ==========
  useEffect(() => {
    if (didHydrateFromLocalStorage) return;

    const clearWaitTimer = () => {
      if (hydrateScreenCacheWaitTimerRef.current != null) {
        window.clearTimeout(hydrateScreenCacheWaitTimerRef.current);
        hydrateScreenCacheWaitTimerRef.current = null;
      }
    };

    // URL に検索条件がある場合は、loginId/profileLoaded を待たずに先に完了させる
    // （ここで return してしまうと autoSearchRequested / didHydrateFromLocalStorage が立たず、自動検索が走らない）
    if (urlHasSearchCondition) {
      const hasUrlTitleBody = searchParams.get('search_title_body') !== null;
      const hasUrlTags = searchParams.get('search_tags') !== null;
      const keyCandidates = normalizedLoginId
        ? [localStorageKey, WEEKLY_SEARCH_SCREEN_CACHE_KEY_LATEST]
        : [WEEKLY_SEARCH_SCREEN_CACHE_KEY_LATEST];
      const cachedEntry = readFirstAvailableLocalStorage(keyCandidates);

      if (cachedEntry?.value) {
        const parsed = safeJsonParse<any>(cachedEntry.value);
        if (parsed && typeof parsed === 'object') {
          const fallbackMailFormatState = readMailFormatStateFromLocalStorage();
          setMailFormatState(
            (parsed as any)?.mailFormatState != null
              ? normalizeMailFormatState((parsed as any)?.mailFormatState)
              : fallbackMailFormatState ?? DEFAULT_MAIL_FORMAT_STATE
          );

          // URL にフラグが無い場合だけ localStorage の状態を反映
          if (!hasUrlTitleBody) {
            const cachedTitleBody = parseBooleanLike((parsed as any)?.searchTitleBody);
            if (typeof cachedTitleBody === 'boolean') {
              setSearchTitleBody(cachedTitleBody);
            }
          }
          if (!hasUrlTags) {
            const cachedTags = parseBooleanLike((parsed as any)?.searchTags);
            if (typeof cachedTags === 'boolean') {
              setSearchTags(cachedTags);
            }
          }

          // URL条件がある場合は、最後に検索されたURLの状態を優先する。
          const urlStartDatePreset = getValidRecentPresetFromParams(searchParams);
          if (urlStartDatePreset) {
            recentPresetRef.current = urlStartDatePreset;
            setStartDatePreset(urlStartDatePreset);
            setDateSearchMode('recent');
            applyStartDatePreset(urlStartDatePreset);
          } else if (
            searchParams.get('start_date') !== null ||
            searchParams.get('end_date') !== null ||
            searchParams.get(START_DATE_PRESET_PARAM) !== null
          ) {
            setStartDatePreset(null);
            setDateSearchMode('range');
          }
        }
      }

      clearWaitTimer();
      setAutoSearchRequested(true); // （URL条件なら自動検索したい）
      setPendingDeptHydrationDone(true);
      setDidHydrateFromLocalStorage(true);
      return;
    }

    // 貼り付け/ブラウザ再起動対策：
    // - loginId が取れたら userKey を優先
    // - loginId が未確定なら latest を読む
    // - latest が無い & loginId 未確定なら少し待って再トライ（無限待ちはしない）
    const keyCandidates = normalizedLoginId
      ? [localStorageKey, WEEKLY_SEARCH_SCREEN_CACHE_KEY_LATEST]
      : [WEEKLY_SEARCH_SCREEN_CACHE_KEY_LATEST];

    const cachedEntry = readFirstAvailableLocalStorage(keyCandidates);
    const raw = cachedEntry?.value ?? null;

    // latest が無い & loginId もまだ無い → ここで確定させず少し待つ（loginId確定後に再トライ）
    if (!raw && !normalizedLoginId) {
      if (hydrateScreenCacheWaitTimerRef.current == null) {
        hydrateScreenCacheWaitTimerRef.current = window.setTimeout(() => {
          hydrateScreenCacheWaitTimerRef.current = null;

          // ここまで待っても復元できない場合は「復元なし」で確定（無限待ち防止）
          setAutoSearchRequested(false);
          setPendingDeptHydrationDone(true);
          setDidHydrateFromLocalStorage(true);
        }, 1500);
      }

      return () => {
        clearWaitTimer();
      };
    }

    // loginId があるのに raw が無い（または latest も無い）→ 復元なしで確定
    if (!raw) {
      clearWaitTimer();
      setAutoSearchRequested(false);
      setPendingDeptHydrationDone(true);
      setDidHydrateFromLocalStorage(true);
      return () => {
        clearWaitTimer();
      };
    }

    clearWaitTimer();

    const parsed = safeJsonParse<any>(raw);
    if (!parsed || typeof parsed !== 'object') {
      setAutoSearchRequested(false);
      setPendingDeptHydrationDone(true);
      setDidHydrateFromLocalStorage(true);
      return () => {
        clearWaitTimer();
      };
    }

    const cache: WeeklySearchScreenCache = {
      version:
        typeof parsed.version === 'number'
          ? parsed.version
          : WEEKLY_SEARCH_SCREEN_CACHE_VERSION,
      savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : '',
      startDate: typeof parsed.startDate === 'string' ? parsed.startDate : null,
      endDate: typeof parsed.endDate === 'string' ? parsed.endDate : null,
      freeWord: typeof parsed.freeWord === 'string' ? parsed.freeWord : '',
      searchTitleBody: parseBooleanLike((parsed as any)?.searchTitleBody) ?? undefined,
      searchTags: parseBooleanLike((parsed as any)?.searchTags) ?? undefined,
      selectedReportTypes: normalizeStringArray(parsed.selectedReportTypes),
      selectedTeams: normalizeStringArray(parsed.selectedTeams),
      selectedCreateBy: normalizeStringArray(parsed.selectedCreateBy),
      selectedDeptIds: normalizeStringArray(parsed.selectedDeptIds),
      deptTouched: Boolean(parsed.deptTouched),
      startDatePreset:
        parsed?.startDatePreset === 'this' ||
        parsed?.startDatePreset === 'last' ||
        parsed?.startDatePreset === 'next'
          ? parsed.startDatePreset
          : null,

      lastSearchAt:
        typeof parsed.lastSearchAt === 'string' ? parsed.lastSearchAt : null,
      lastSearchQuery:
        typeof parsed.lastSearchQuery === 'string' ? parsed.lastSearchQuery : null,
      createByTouched:
        Boolean((parsed as any)?.createByTouched) ||
        normalizeStringArray(parsed.selectedCreateBy).length > 0,
      lastWrId: typeof parsed.lastWrId === 'string' ? parsed.lastWrId : null,
      mailFormatState:
        (parsed as any)?.mailFormatState != null
          ? normalizeMailFormatState((parsed as any)?.mailFormatState)
          : readMailFormatStateFromLocalStorage() ?? DEFAULT_MAIL_FORMAT_STATE,
    };

    // 最後の検索情報を復元（あれば meta は復元）
    if (cache.lastSearchAt && cache.lastSearchQuery) {
      const meta = { at: cache.lastSearchAt, query: cache.lastSearchQuery };
      lastSearchMetaRef.current = meta;
      setLastSearchMeta(meta);
    } else {
      lastSearchMetaRef.current = null;
      setLastSearchMeta(null);
    }

    // 検索ボタン未押下でも、保存済み条件があるなら起動時に自動検索したい
    // 「条件が全部空」でも lastSearchQuery があるなら（前回検索してるなら）自動検索したい
    const hasAnyConditionInCache =
      (cache.freeWord ?? '').trim().length > 0 ||
      (cache.selectedTeams ?? []).length > 0 ||
      (cache.selectedCreateBy ?? []).length > 0 ||
      (cache.selectedReportTypes ?? []).length > 0 ||
      (cache.selectedDeptIds ?? []).length > 0 ||
      Boolean(cache.deptTouched) ||
      Boolean(cache.createByTouched) ||
      Boolean(cache.startDatePreset) ||
      Boolean(cache.lastSearchQuery) ||
      Boolean(cache.lastWrId);

    setAutoSearchRequested(hasAnyConditionInCache);

    // その他
    setFreeWord(cache.freeWord ?? '');
    if (typeof cache.searchTitleBody === 'boolean') {
      setSearchTitleBody(cache.searchTitleBody);
    }
    if (typeof cache.searchTags === 'boolean') {
      setSearchTags(cache.searchTags);
    }
    setSelectedReportTypes(cache.selectedReportTypes ?? []);
    setSelectedTeams(cache.selectedTeams ?? []);
    setSelectedCreateBy(cache.selectedCreateBy ?? []);
    setMailFormatState(cache.mailFormatState ?? DEFAULT_MAIL_FORMAT_STATE);
    setDeptTouched(Boolean(cache.deptTouched));

    // createByTouched 復元
    const parsedCreateByTouched =
      Boolean((parsed as any)?.createByTouched) ||
      (cache.selectedCreateBy ?? []).length > 0;

    setCreateByTouched(parsedCreateByTouched);

    setLastWrId(cache.lastWrId ?? null);

    // 開始日付プリセット（チェック状態）を復元
    const presetRaw = (parsed as any)?.startDatePreset;
    const preset =
      presetRaw === 'this' || presetRaw === 'last' || presetRaw === 'next'
        ? (presetRaw as 'this' | 'last' | 'next')
        : null;

    const shouldUseCachedPreset = Boolean(
      preset &&
      datesMatchRecentPreset(
        preset,
        cache.startDate ? parseYmdToLocalDate(cache.startDate) : null,
        cache.endDate ? parseYmdToLocalDate(cache.endDate) : null
      )
    );

    if (preset && shouldUseCachedPreset) {
      setDateSearchMode('recent');
      setStartDatePreset(preset);
      // プリセットが有効なら日付はプリセットに寄せる
      applyStartDatePreset(preset);
    } else {
      setDateSearchMode('range');
      setStartDatePreset(null);
      if (cache.startDate) {
        setStartDate(new Date(cache.startDate));
      }
      if (cache.endDate) {
        setEndDate(new Date(cache.endDate));
      }
    }

    const hasAnyQuery = searchParams.toString().length > 0;
    if (!hasAnyQuery && cache.lastWrId && cache.lastWrId.trim() !== '') {
      const nextParams = new URLSearchParams();
      nextParams.set('wr_id', cache.lastWrId.trim());
      setWeeklySearchParams(nextParams, { replace: true });
    }

    // 課（deptOptions が揃っていない可能性があるので pending に）
    pendingDeptIdsRef.current = cache.selectedDeptIds ?? [];
    setRestoredDeptIdsForCreator(cache.selectedDeptIds ?? []);

    // pending があるなら「完了」を false にする（deptOptions に反映後に true にする）
    if ((cache.selectedDeptIds ?? []).length > 0) {
      setPendingDeptHydrationDone(false);
    } else {
      setPendingDeptHydrationDone(true);
    }

    setDidHydrateFromLocalStorage(true);

    return () => {
      clearWaitTimer();
    };
  }, [
    didHydrateFromLocalStorage,
    localStorageKey,
    urlHasSearchCondition,
    normalizedLoginId,
    profileLoaded,
    applyStartDatePreset,
    setWeeklySearchParams,
    searchParams,
  ]);


  // pendingDeptIdsRef を deptOptions に反映（deptOptions が揃った後）
  useEffect(() => {
    const pending = pendingDeptIdsRef.current;
    if (!pending) return;

    if (pending.length === 0) {
      pendingDeptIdsRef.current = null;
      setPendingDeptHydrationDone(true);
      return;
    }
    if (!deptOptions || deptOptions.length === 0) return;

    const picks = deptOptions.filter((d) => pending.includes(String(d.id)));
    if (picks.length > 0) {
      skipNextDeptChildSyncRef.current = true;
      setSelectedDepts(picks);
    }
    pendingDeptIdsRef.current = null;

  // picks が 0 でも完了にする（ref 依存で自動検索が止まるのを防ぐ）
    setPendingDeptHydrationDone(true);
  }, [deptOptions]);

  // 検索結果を localStorage から復元（戻った瞬間に前回結果を表示）
  useEffect(() => {
    if (didHydrateResultsFromLocalStorage) return;
    if (!didHydrateFromLocalStorage) return;
    if (!isViewerAccessContextReady || !viewerAccessContextKey) return;

    if (!ENABLE_PERSISTED_WEEKLY_RESULT_CACHE) {
      try {
        localStorage.removeItem(resultsLocalStorageKey);
      } catch {
        // The network search still proceeds even when storage is unavailable.
      }
      didRestoreResultsFromLocalStorageRef.current = false;
      swrRevalidatePendingRef.current = false;
      setDidHydrateResultsFromLocalStorage(true);
      return;
    }

    // タイマーの後始末
    if (hydrateResultsWaitTimerRef.current != null) {
      window.clearTimeout(hydrateResultsWaitTimerRef.current);
      hydrateResultsWaitTimerRef.current = null;
    }

    // 重要：normalizedLoginId が確定するまで "unknown" key で確定させない
    // ただし永遠に待たないように短い猶予でフォールバックする
    if (!normalizedLoginId) {
      hydrateResultsWaitTimerRef.current = window.setTimeout(() => {
        setDidHydrateResultsFromLocalStorage(true);
      }, 1500);
      return;
    }

    // ===== 画面の日付条件に一致するクエリのみ results-cache を復元対象にする =====
    const fmtYmd = (d: Date): string => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };

    const currentStartYmd = startDate ? fmtYmd(startDate) : null;
    const currentEndYmd = endDate ? fmtYmd(endDate) : null;

    const queryHasSameDates = (rawQuery: string): boolean => {
      try {
        const sp = new URLSearchParams(rawQuery);
        const qStart = sp.get('start_date');
        const qEnd = sp.get('end_date');

        // 日付が無いクエリは results-cache の復元対象にしない（不整合防止）
        if (!qStart || !qEnd) return false;

        if (currentStartYmd && qStart !== currentStartYmd) return false;
        if (currentEndYmd && qEnd !== currentEndYmd) return false;

        return true;
      } catch {
        return false;
      }
    };

    const expectedQueryRaw = urlHasSearchCondition
      ? searchParams.toString()
      : (() => {
          const q = lastSearchMetaRef.current?.query ?? null;
          if (!q) return null;

          // 日付が一致する場合だけ results-cache 復元に使う
          if (!queryHasSameDates(q)) return null;

          return q;
        })();

    // 復元すべきクエリが無いなら終了（無限待ちにしない）
    if (!expectedQueryRaw) {
      // 画面cacheの lastSearchQuery が欠けても、results-cache があれば戻った瞬間に表示する
      // ただし、日付が現在のUI日付と一致する場合だけ復元する（不整合防止）
      try {
        const cachedEntry =
          getResultsCacheEntries(readResultsCachePayload(resultsLocalStorageKey)).find(
            (entry) =>
              entry.accessContextKey === viewerAccessContextKey &&
              queryHasSameDates(entry.query) &&
              !isResultsCacheEntryStale(entry)
          ) ?? null;

        if (cachedEntry) {
          const cachedMeta = {
            at: cachedEntry.savedAt || new Date().toISOString(),
            query: cachedEntry.query,
          };
          didRestoreResultsFromLocalStorageRef.current = true;
          swrRevalidatePendingRef.current = true;

          lastSearchMetaRef.current = cachedMeta;
          setLastSearchMeta(cachedMeta);
          setLocalResults(cachedEntry.results);
          onResults(cachedEntry.results);
          onSearchPerformed('hydrate:results-cache:fallback');
        }
      } catch (e) {
        // 保険処理なのでアプリは止めない
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[SearchArea] results-cache fallback restore failed:', e);
        }
      }

      setDidHydrateResultsFromLocalStorage(true);
      return;
    }

    const expectedQuery = normalizeQueryString(expectedQueryRaw);

    const cachedEntry = findResultsCacheEntryByQuery(
      readResultsCachePayload(resultsLocalStorageKey),
      expectedQuery,
      viewerAccessContextKey
    );

    // 日付が一致しない results-cache は復元しない
    if (!cachedEntry || !queryHasSameDates(cachedEntry.query)) {
      setDidHydrateResultsFromLocalStorage(true);
      return;
    }

    const isCachedEntryStale = isResultsCacheEntryStale(cachedEntry);

    // 更新通知より古い results-cache は復元しない
    if (isCachedEntryStale) {
      setDidHydrateResultsFromLocalStorage(true);
      return;
    }

    // 復元成功：この後の auto search が 0 件で上書きしないようにする
    didRestoreResultsFromLocalStorageRef.current = true;
    swrRevalidatePendingRef.current = true; // （1回だけrevalidateしたい）

    const cachedMeta = {
      at: cachedEntry.savedAt || new Date().toISOString(),
      query: cachedEntry.query,
    };
    lastSearchMetaRef.current = cachedMeta;
    setLastSearchMeta(cachedMeta);
    setLocalResults(cachedEntry.results);
    onResults(cachedEntry.results);

    // Content 側の表示ゲート（searchPerformed）を満たす
    onSearchPerformed('hydrate:results-cache');

    setDidHydrateResultsFromLocalStorage(true);

    return () => {
      if (hydrateResultsWaitTimerRef.current != null) {
        window.clearTimeout(hydrateResultsWaitTimerRef.current);
        hydrateResultsWaitTimerRef.current = null;
      }
    };
  }, [
    didHydrateResultsFromLocalStorage,
    didHydrateFromLocalStorage,
    isViewerAccessContextReady,
    viewerAccessContextKey,
    resultsLocalStorageKey,
    urlHasSearchCondition,
    searchParams,
    onResults,
    onSearchPerformed,
    normalizedLoginId,
    startDate,
    endDate,
  ]);

  // ===== 画面の入力状態 → 共有用URL(検索条件付き) を生成 =====
  const buildMergedSearchParamsFromUi = useCallback(() => {
    const uniq = (arr: string[]) =>
      Array.from(new Set((arr ?? []).map((v) => String(v))));

    const wrIdParam = searchParams.get('wr_id');
    if (wrIdParam && String(wrIdParam).trim() !== '') {
      const merged = new URLSearchParams();
      merged.set('wr_id', String(wrIdParam).trim());
      return merged;
    }

    // report_types は有効候補へ正規化する。全選択は未選択と同じく送らず、一部選択は複数条件で送る。
    const validReportTypeIds = getReportTypeIdsForSearch(selectedReportTypes);
    const createByForSearch =
      createByTouched || hasInitialCreateByParam.current
        ? uniq(selectedCreateBy)
        : [];

    const params = buildWeeklySearchParams({
      startDate: startDate ? formatLocalDate(startDate) : undefined,
      endDate: endDate ? formatLocalDate(endDate) : undefined,
      freeWord,
      searchTitleBody,
      searchTags,
      selectedReportTypeIds: validReportTypeIds,
      allReportTypes: rawReportTypes,
      selectedTeamIds: uniq(selectedTeams),
      selectedDepartmentIds: uniq(effectiveDeptIds),
      selectedCreateByValues: createByForSearch,
      selectedCreateByUserIds: createByForSearch.filter((value) =>
        initialCreateByUserIdsRef.current.includes(String(value))
      ),
      createByOptions: createByOptionsForSearch,
      appendTemporaryFlg0: true,
    });

    // 既存のクエリ（mode等）を保持しつつ、検索条件だけ差し替え
    const merged = new URLSearchParams(searchParams);
    SEARCH_PARAM_KEYS.forEach((key) => merged.delete(key));
    params.forEach((value, key) => merged.append(key, value));

    // 記入者：条件なし を URL に残す
    const createByNone =
      Boolean(createByTouched) && uniq(selectedCreateBy).length === 0;
    if (createByNone) {
      merged.set(CREATE_BY_NONE_PARAM, '1');
    } else {
      merged.delete(CREATE_BY_NONE_PARAM);
    }

    // 開始日付プリセット（今週/先週/来週）を URL に残す
    if (dateSearchMode === 'recent' && startDatePreset) {
      merged.set(START_DATE_PRESET_PARAM, startDatePreset);
    } else {
      merged.delete(START_DATE_PRESET_PARAM);
    }

    return merged;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDate,
    endDate,
    freeWord,
    searchTitleBody,
    searchTags,
    dateSearchMode,
    startDatePreset,
    selectedReportTypes,
    reportOptionsFiltered,
    getReportTypeIdsForSearch,
    rawReportTypes,
    selectedTeams,
    effectiveDeptIds,
    selectedCreateBy,
    createByOptionsForSearch,
    createByTouched,
    searchParams,
  ]);

  // ========== 検索画面 localStorage 保存（変更があったら保持） ==========
  useEffect(() => {
    if (!didHydrateFromLocalStorage) return;

    // loginId 未確定の 'unknown' に保存しない（unknown 汚染を防ぐ）
    if (!normalizedLoginId) return;
    if (!initialSetupDone) return;
    if (!pendingDeptHydrationDone) return;
    if (isWrIdOnlySearchParams(searchParams)) return;

    const meta = lastSearchMetaRef.current ?? lastSearchMeta;

    const payload: WeeklySearchScreenCache = {
      version: WEEKLY_SEARCH_SCREEN_CACHE_VERSION,
      savedAt: new Date().toISOString(),
      startDate: startDate ? formatLocalDate(startDate) : null,
      endDate: endDate ? formatLocalDate(endDate) : null,
      // 解除を保存（null も保存される）
      startDatePreset: dateSearchMode === 'recent' ? startDatePreset : null,
      freeWord: freeWord ?? '',
      searchTitleBody,
      searchTags,
      selectedReportTypes: (selectedReportTypes ?? []).filter(
        (s) => typeof s === 'string' && s.trim().length > 0
      ),
      selectedTeams: (selectedTeams ?? []).filter(
        (s) => typeof s === 'string' && s.trim().length > 0
      ),
      selectedCreateBy: (selectedCreateBy ?? []).filter(
        (s) => typeof s === 'string' && s.trim().length > 0
      ),
      selectedDeptIds: (selectedDepts ?? [])
        .map((d) => String(d.id))
        .filter((s) => s.trim().length > 0),
      deptTouched: Boolean(deptTouched),
      createByTouched: Boolean(createByTouched),
      mailFormatState: normalizeMailFormatState(mailFormatState),

      lastSearchAt: meta?.at ?? null,
      lastSearchQuery: meta?.query ?? null,
      lastWrId: lastWrId ?? null,
    };

    try {
      const json = JSON.stringify(payload);

      localStorage.setItem(localStorageKey, json);

      // loginId 未確定の復元用（ブラウザ再起動時に効く）
      localStorage.setItem(WEEKLY_SEARCH_SCREEN_CACHE_KEY_LATEST, json);
    } catch (e) {
      console.warn('[SearchArea] localStorage save failed:', e);
    }

  }, [
    didHydrateFromLocalStorage,
    initialSetupDone,
    pendingDeptHydrationDone,
    localStorageKey,
    searchParams,
    startDate,
    endDate,
    dateSearchMode,
    startDatePreset,
    freeWord,
    searchTitleBody,
    searchTags,
    selectedReportTypes,
    selectedTeams,
    selectedCreateBy,
    selectedDepts,
    deptTouched,
    createByTouched,
    mailFormatState,
    lastSearchMeta,
    lastWrId,
    normalizedLoginId,
  ]);

  useEffect(() => {
    if (!didHydrateFromLocalStorage) return;
    try {
      const payload: WeeklySearchMailFormatCache = {
        version: MAIL_FORMAT_CACHE_VERSION,
        savedAt: new Date().toISOString(),
        mailFormatState: normalizeMailFormatState(mailFormatState),
      };
      localStorage.setItem(
        WEEKLY_SEARCH_MAIL_FORMAT_CACHE_KEY_LATEST,
        JSON.stringify(payload)
      );
    } catch {
      // ignore mail-format cache errors
    }
  }, [didHydrateFromLocalStorage, mailFormatState]);

  // ========== 復元された検索条件を URL に同期（戻った直後のURLコピー対策） ==========
  const didSyncUrlFromUiRef = useRef(false);

  useEffect(() => {
    if (didSyncUrlFromUiRef.current) return;

    // URLに条件がある場合はURL優先なので同期しない
    if (urlHasSearchCondition) return;

    // localStorage復元が終わってから
    if (!didHydrateFromLocalStorage) return;

    // dept / create_by / report_types の初期正規化が終わってから
    if (!initialSetupDone) return;

    // 発行者選択がある場合は、旧週報IDを新user_idへ移行してからURL化する。
    if (
      selectedCreateBy.length > 0 &&
      (creatorOptionsResolvedKey !== creatorOptionsCriteriaKey ||
        creatorSelectionNormalizedKey !== creatorOptionsCriteriaKey)
    ) return;

    // dept の pending 反映待ちがあるなら終わってから
    if (!pendingDeptHydrationDone) return;

    const merged = buildMergedSearchParamsFromUi();
    if (!merged.toString()) {
      didSyncUrlFromUiRef.current = true;
      return;
    }

    didSyncUrlFromUiRef.current = true;
    setWeeklySearchParams(merged, { replace: true });
  }, [
    urlHasSearchCondition,
    didHydrateFromLocalStorage,
    initialSetupDone,
    pendingDeptHydrationDone,
    selectedCreateBy,
    creatorOptionsResolvedKey,
    creatorOptionsCriteriaKey,
    creatorSelectionNormalizedKey,
    buildMergedSearchParamsFromUi,
    setWeeklySearchParams,
  ]);

  // 検索ボタン押下
  const handleSearch = async (arg?: any) => {
    const opts = pickHandleSearchOptions(arg);
    const reason = opts?.reason ?? 'manual';
    if (reason === 'manual') {
      creatorSyncForceRef.current = true;
      setCreatorSyncTick((value) => value + 1);
    }

    const seq = ++searchRequestSeqRef.current;
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
      searchAbortRef.current = null;
    }
    setSearchLoading(false);

    if (activeSearchAbortRef.current) {
      activeSearchAbortRef.current.abort();
    }

    const searchAbortController = new AbortController();
    activeSearchAbortRef.current = searchAbortController;
    const startedSearchParamsKey = normalizeQueryString(searchParams.toString());
    const startedViewerAccessContextKey = viewerAccessContextKey;

    const isSearchActive = () =>
      mountedRef.current &&
      seq === searchRequestSeqRef.current &&
      !searchAbortController.signal.aborted &&
      latestViewerAccessContextKeyRef.current ===
        startedViewerAccessContextKey &&
      normalizeQueryString(latestSearchParamsRef.current.toString()) === startedSearchParamsKey;

    const isManual = reason === 'manual';
    if (!isViewerAccessContextReady || !viewerAccessContextKey) {
      activeSearchAbortRef.current = null;
      setLocalResults([]);
      onResults([]);
      setAutoSearchRequested(true);

      if (isManual) {
        setSnackbarMsg(
          profileLoaded
            ? 'ユーザーの課・チーム情報を確認できないため、検索できません。設定内容をご確認ください。'
            : 'ユーザー情報を読み込み中です。しばらく待ってから再度お試しください。'
        );
        setSnackbarOpen(true);
        onSearchPerformed(reason);
      }
      return;
    }

    const wrIdParam = searchParams.get('wr_id');
    if (wrIdParam && String(wrIdParam).trim() !== '') {
      setLastWrId(String(wrIdParam).trim());
    } else {
      setLastWrId(null);
    }
    if (!isManual && wrIdParam && String(wrIdParam).trim() !== '') {
      const wrIdValue = String(wrIdParam).trim();
      const merged = new URLSearchParams();
      merged.set('wr_id', wrIdValue);

      if (!isReportTypeAccessReady) {
        setAutoSearchRequested(true);
        onSearchPerformed(reason);
        return;
      }

      const controller = new AbortController();
      searchAbortRef.current = controller;
      setSearchLoading(true);

      try {
        const currentAccessMap = buildWeeklyReportAccessMap(
          await fetchLatestWeeklyReportPublicSettings(), rawReportTypeIdSet, state.data ?? {},
        );
        if (!isSearchActive()) return;
        const apiParams = new URLSearchParams();
        apiParams.set('wr_id', wrIdValue);
        if (userDepForScopedSearch) apiParams.set('USER_DEP', userDepForScopedSearch);
        if (profile?.preferredTeamId) apiParams.set('USER_TEAM_ID', String(profile.preferredTeamId));

        throwIfAborted(searchAbortController.signal);

        const fetchedRows = await fetchWeeklyReports<Report>(
          apiParams,
          searchAbortController.signal
        );

        let recoveryRows: Report[] = [];
        if (fetchedRows.length === 0 && reportTypeAccessById != null) {
          try {
            recoveryRows = await fetchWeeklyReports<Report>(
              buildWeeklyReportVisibilityRecoveryParams(apiParams),
              searchAbortController.signal
            );
          } catch (error) {
            if (isAbortError(error)) throw error;
            console.warn('weekly-report visibility recovery failed', error);
          }
        }

        if (!isSearchActive()) {
          return;
        }

        const data =
          reportTypeAccessById != null
            ? mergeWeeklyReportRowsWithVisibilityRecovery({
                scopedRows: fetchedRows,
                recoveryRows,
                authenticatedUserId,
                decideVisibility: (row) => {
                  const resolution = resolveEffectiveAccessForRow(row, currentAccessMap);
                  return {
                    configured: resolution.configured,
                    allowed: isAccessAllowedByEffectiveAccess(
                      resolution,
                      (setting) =>
                        canAccessRowByUserDep(
                          row,
                          setting,
                          userDepForScopedSearch,
                          {
                            deptIdToLabelMap,
                            deptLabelToIdMap,
                            userDeptIdRaw: resolvedLoginUserDeptId,
                            userTeamIdRaw: profile?.preferredTeamId,
                          }
                        )
                    ),
                  };
                },
              })
            : [];

        const withKana = await attachKanaForWeeklyList<Report>(data, {
          userKanaMap,
        });

        if (!isSearchActive()) {
          return;
        }

        const sorted = [...withKana].sort(
          compareWeeklyReports({
            teamSortIndexMap,
            userSortIndexMap,
            sortMode: 'DEFAULT',
          })
        );

        if (!isSearchActive()) {
          return;
        }

        const nextUrlParams = mergeSearchConditionsIntoCurrentRouteParams(
          latestSearchParamsRef.current,
          merged
        );
        const shouldReplaceHistory = reason !== 'manual';
        setWeeklySearchParams(nextUrlParams, { replace: shouldReplaceHistory });

        onSearchPerformed(reason);
        setLocalResults(sorted);
        onResults(sorted);

        // wr_id 検索では「画面の検索条件キャッシュ」を上書きしない
        // （戻ったときの検索条件維持を優先）
      } catch (e: any) {
        if (isAbortError(e)) return;
        if (!isCurrentSearchRequest(seq, controller)) return;
        console.error('weekly-report-search error', e);
      } finally {
        if (isCurrentSearchRequest(seq, controller)) {
          setSearchLoading(false);
          searchAbortRef.current = null;
        }
      }

      return;
    }

    // ===== 日付の整合性チェック（念のため二重でガード） =====
    if (startDatePickerError || endDatePickerError) {
      setSnackbarMsg('日付の入力エラーを修正してください');
      setSnackbarOpen(true);
      onSearchPerformed(reason);
      return;
    }
    if (!startDate || !endDate) {
      setSnackbarMsg('開始日付と終了日付を指定してください');
      setSnackbarOpen(true);
      onSearchPerformed(reason);
      return;
    }
    if (startDate && endDate && stripTime(endDate) < stripTime(startDate)) {
      setSnackbarMsg('終了日付は開始日付以降を指定してください');
      setSnackbarOpen(true);
      onSearchPerformed(reason);
      return;
    }

    if (shouldApplyVisibilityScope && !isReportTypeAccessReady) {
      setAutoSearchRequested(true);
      setSnackbarMsg(
        reportTypeAccessLoadFailed
          ? '公開範囲設定を取得できないため、検索を停止しました。画面を再読み込みしてください。'
          : '公開範囲設定を読み込み中です。しばらく待ってから再度お試しください。'
      );
      setSnackbarOpen(true);
      onSearchPerformed(reason);
      return;
    }

    const uniq = (arr: string[]) => Array.from(new Set((arr ?? []).map(String)));

    // report_types は有効候補へ正規化する。全選択は未選択と同じく送らず、一部選択は複数条件で送る。
    const validReportTypeIds = getReportTypeIdsForSearch(selectedReportTypes);
    const createByForSearch =
      createByTouched || hasInitialCreateByParam.current
        ? uniq(selectedCreateBy)
        : [];

    const params = buildWeeklySearchParams({
      startDate: startDate ? formatLocalDate(startDate) : undefined,
      endDate: endDate ? formatLocalDate(endDate) : undefined,
      freeWord,
      searchTitleBody,
      searchTags,
      // 未選択なら [] が渡る（= report_types を append しない）
      selectedReportTypeIds: validReportTypeIds,
      allReportTypes: rawReportTypes,
      selectedTeamIds: uniq(selectedTeams),
      selectedDepartmentIds: uniq(effectiveDeptIds),
      selectedCreateByValues: createByForSearch,
      selectedCreateByUserIds: createByForSearch.filter((value) =>
        initialCreateByUserIdsRef.current.includes(String(value))
      ),
      createByOptions: createByOptionsForSearch,
      appendTemporaryFlg0: true,
    });

    // 既存のクエリパラメータ（mode など）を保持しつつ検索条件だけ差し替え
    const merged = new URLSearchParams(searchParams);

    const searchKeys = [
      'wr_id',
      'start_date',
      'end_date',
      'free_word',
      'search_title_body',
      'search_tags',
      'report_types',
      'teams',
      'create_by',
      'create_by_user_ids',
      'legacy_create_by',
      'departments',
      'temporary_flg',
      CREATE_BY_NONE_PARAM,
    ];
    searchKeys.forEach((key) => merged.delete(key));

    params.forEach((value, key) => {
      merged.append(key, value);
    });

    // 記入者を「条件なし」で検索した意図を URL に残す
    const createByNone =
      Boolean(createByTouched) && uniq(selectedCreateBy).length === 0;
    if (createByNone) {
      merged.set(CREATE_BY_NONE_PARAM, '1');
    } else {
      merged.delete(CREATE_BY_NONE_PARAM);
    }

    // 開始日付プリセット（今週/先週）を URL に残す
    if (dateSearchMode === 'recent' && startDatePreset) {
      merged.set(START_DATE_PRESET_PARAM, startDatePreset);
    } else {
      merged.delete(START_DATE_PRESET_PARAM);
    }

    const nextUrlParamsForCache = mergeSearchConditionsIntoCurrentRouteParams(
      latestSearchParamsRef.current,
      merged
    );
    const nextQueryForCache = nextUrlParamsForCache.toString();
    const shouldReadResultsCache =
      ENABLE_PERSISTED_WEEKLY_RESULT_CACHE &&
      !reason.startsWith('revalidate:') &&
      !reason.startsWith('auto-revalidate:');

    if (shouldReadResultsCache && normalizedLoginId) {
      const cachedEntry = findResultsCacheEntryByQuery(
        readResultsCachePayload(resultsLocalStorageKey),
        nextQueryForCache,
        viewerAccessContextKey ?? ''
      );

      if (cachedEntry && !isResultsCacheEntryStale(cachedEntry)) {
        const cachedMeta = {
          at: cachedEntry.savedAt || new Date().toISOString(),
          query: nextQueryForCache,
        };

        activeSearchAbortRef.current = null;
        onSearchPerformed(reason);
        setWeeklySearchParams(nextUrlParamsForCache, { replace: true });
        setLocalResults(cachedEntry.results);
        onResults(cachedEntry.results);
        lastSearchMetaRef.current = cachedMeta;
        setLastSearchMeta(cachedMeta);
        return;
      }
    }

    setSearchLoading(true);

    try {
      const apiParams = new URLSearchParams(merged);
      // 画面専用パラメータは API へ送らない
      apiParams.delete(CREATE_BY_NONE_PARAM);
      apiParams.delete(START_DATE_PRESET_PARAM);

      const currentAccessMap = buildWeeklyReportAccessMap(
        await fetchLatestWeeklyReportPublicSettings(), rawReportTypeIdSet, state.data ?? {},
      );
      if (!isSearchActive()) return;
      const userDep = shouldApplyVisibilityScope
        ? userDepForScopedSearch
        : '';

      // API 側の公開範囲判定に必要な USER_DEP は、
      // ログインユーザーの課がマスタ課IDへ解決できた場合だけ付与する。
      // 課IDが解決できない場合に USER_DEP や厳格なクライアント側フィルタを適用すると、
      // 同一課で見えるべき非公開週報まで落としてしまうため。
      const scopedUserDep = shouldApplyVisibilityScope && userDep ? userDep : undefined;
      if (scopedUserDep) {
        apiParams.set('USER_DEP', scopedUserDep);
      }
      if (profile?.preferredTeamId) {
        apiParams.set('USER_TEAM_ID', String(profile.preferredTeamId));
      }

      const deptScopeForAdaptiveFetch = uniq(effectiveDeptIds);
      const searchApiResultCapThreshold = 200;
      const searchApiInitialChunkDays = 62;
      const searchApiMaxConcurrency = 6;
      const fetchRowsAcrossDateRange = async (
        requestParams: URLSearchParams,
        viewerScope?: {
          userDep?: string;
          userTeamId?: string | number;
        }
      ): Promise<Report[]> => {
        const startYmd =
          requestParams.get('start_date') ??
          (startDate ? formatLocalDate(startDate) : '');
        const endYmd =
          requestParams.get('end_date') ??
          (endDate ? formatLocalDate(endDate) : '');

        if (!startYmd || !endYmd) {
          throwIfAborted(searchAbortController.signal);

          return fetchWeeklyReports<Report>(
            requestParams,
            searchAbortController.signal
          );
        }

        throwIfAborted(searchAbortController.signal);

        const result = await (
          fetchWeeklyReportsByDepartmentScopesAdaptively as unknown as (
            args: {
              startDate: string;
              endDate: string;
              baseParams: URLSearchParams;
              departmentScopes: string[][];
              userDep?: string;
              userTeamId?: string | number;
              appendTemporaryFlg0: boolean;
              splitThreshold: number;
              initialChunkDays: number;
              maxConcurrency: number;
              signal?: AbortSignal;
            }
          ) => Promise<{ rows: Report[] }>
        )({
          startDate: startYmd,
          endDate: endYmd,
          baseParams: requestParams,
          departmentScopes: [deptScopeForAdaptiveFetch],
          userDep: viewerScope?.userDep,
          userTeamId: viewerScope?.userTeamId,
          appendTemporaryFlg0: true,
          splitThreshold: searchApiResultCapThreshold,
          initialChunkDays: searchApiInitialChunkDays,
          maxConcurrency: searchApiMaxConcurrency,
          signal: searchAbortController.signal,
        });

        throwIfAborted(searchAbortController.signal);

        return result.rows;
      };

      // 自課だけの検索でも、発行者本人が公開先から外れている週報を回復する。
      // 日付・課・チーム・分類・記入者・フリーワードの検索条件は維持する。
      const shouldRecoverVisibilityRows =
        Boolean(scopedUserDep) &&
        reportTypeAccessById != null;
      const recoveryRowsPromise: Promise<Report[]> = shouldRecoverVisibilityRows
        ? fetchRowsAcrossDateRange(
            buildWeeklyReportVisibilityRecoveryParams(apiParams)
          ).catch((error) => {
            if (isAbortError(error)) throw error;
            console.warn('weekly-report visibility recovery failed', error);
            return [];
          })
        : Promise.resolve([]);
      const [scopedRows, recoveryRows] = await Promise.all([
        fetchRowsAcrossDateRange(apiParams, {
          userDep: scopedUserDep,
          userTeamId: profile?.preferredTeamId,
        }),
        recoveryRowsPromise,
      ]);
      let data = scopedRows;

      if (shouldApplyVisibilityScope && reportTypeAccessById != null) {
        data = mergeWeeklyReportRowsWithVisibilityRecovery({
          scopedRows,
          recoveryRows,
          authenticatedUserId,
          decideVisibility: (row) => {
            const resolution = resolveEffectiveAccessForRow(row, currentAccessMap);
            return {
              configured: resolution.configured,
              allowed: isAccessAllowedByEffectiveAccess(
                resolution,
                (setting) =>
                  canAccessRowByUserDep(
                    row,
                    setting,
                    userDep,
                    {
                      deptIdToLabelMap,
                      deptLabelToIdMap,
                      userDeptIdRaw: resolvedLoginUserDeptId,
                      userTeamIdRaw: profile?.preferredTeamId,
                    }
                  )
              ),
            };
          },
        });
      }

      // 後から走った検索がある、またはタブ遷移で中断済みなら、この結果は捨てる
      if (!isSearchActive()) {
        return;
      }

      throwIfAborted(searchAbortController.signal);

      // ==== 読み仮名の付与：DB かなを優先、なければ一般的な読み仮名 ====
      const withKana = await attachKanaForWeeklyList<Report>(data, {
        userKanaMap,
      });

      // 後から走った検索がある、またはタブ遷移で中断済みなら、この結果は捨てる
      if (!isSearchActive()) {
        return;
      }

      // (開始日付 → チーム → 人名(かな優先) → 作成日時）
      const sorted = [...withKana].sort(
        compareWeeklyReports({
          teamSortIndexMap,
          userSortIndexMap,
          // newestFirst を指定しない = 開始日付は昇順
          sortMode: 'DEFAULT',
        })
      );

      if (!isSearchActive()) {
        return;
      }

      const nextUrlParams = mergeSearchConditionsIntoCurrentRouteParams(
        latestSearchParamsRef.current,
        merged
      );
      const shouldReplaceHistory = reason !== 'manual';
      onSearchPerformed(reason);
      setWeeklySearchParams(nextUrlParams, { replace: shouldReplaceHistory });

      setLocalResults(sorted);
      onResults(sorted);

      // 最後の検索情報（戻ったときの自動検索/復元に使う）
      const nowIso = new Date().toISOString();
      const queryStr = nextUrlParams.toString();

      // ref も同期
      lastSearchMetaRef.current = { at: nowIso, query: queryStr };
      setLastSearchMeta({ at: nowIso, query: queryStr });

      // 検索条件（画面cache）も同期保存（ルート切替で useEffect が走らず meta が欠けるのを防ぐ）
      if (normalizedLoginId) {
        try {
          const payload: WeeklySearchScreenCache = {
            version: WEEKLY_SEARCH_SCREEN_CACHE_VERSION,
            savedAt: nowIso,
            startDate: startDate ? formatLocalDate(startDate) : null,
            endDate: endDate ? formatLocalDate(endDate) : null,
            startDatePreset: dateSearchMode === 'recent' ? startDatePreset : null,
            freeWord: freeWord ?? '',
            searchTitleBody,
            searchTags,
            selectedReportTypes: (selectedReportTypes ?? []).filter(
              (s) => typeof s === 'string' && s.trim().length > 0
            ),
            selectedTeams: (selectedTeams ?? []).filter(
              (s) => typeof s === 'string' && s.trim().length > 0
            ),
            selectedCreateBy: (selectedCreateBy ?? []).filter(
              (s) => typeof s === 'string' && s.trim().length > 0
            ),
            selectedDeptIds: (selectedDepts ?? [])
              .map((d) => String(d.id))
              .filter((s) => s.trim().length > 0),
            deptTouched: Boolean(deptTouched),
            createByTouched: Boolean(createByTouched),
            mailFormatState: normalizeMailFormatState(mailFormatState),

            lastSearchAt: nowIso,
            lastSearchQuery: queryStr,
            lastWrId: lastWrId ?? null,
          };

          const json = JSON.stringify(payload);
          localStorage.setItem(localStorageKey, json);
          localStorage.setItem(WEEKLY_SEARCH_SCREEN_CACHE_KEY_LATEST, json);
        } catch (e) {
          console.warn('[SearchArea] screen cache sync save failed:', e);
        }
      }

      // 権限付きの検索結果は永続化しない。将来サーバー側の権限バージョンを
      // 検証できる仕組みを導入した場合だけ、上のフラグと併せて再有効化する。
      if (normalizedLoginId && ENABLE_PERSISTED_WEEKLY_RESULT_CACHE) {
        try {
          writeResultsCacheEntry(resultsLocalStorageKey, {
            savedAt: nowIso,
            query: queryStr,
            accessContextKey: viewerAccessContextKey,
            results: sorted,
          });
        } catch (e) {
          console.warn('[SearchArea] results localStorage save failed:', e);
        }
      }

    } catch (err: any) {
      // 後から走った検索がある、またはタブ遷移で中断済みなら、このエラー結果は捨てる
      if (!isSearchActive()) {
        return;
      }

      if (err?.name === 'CanceledError' || err?.name === 'AbortError') {
        return;
      }

      console.error('検索エラー:', err);
      if (isApiError(err) && (err as any).response) {
        console.error('サーバ detail:', (err as any).response.data);
      }
      onSearchPerformed(reason);
    } finally {
      setSearchLoading(false);
      activeSearchAbortRef.current = null;
    }
  };

  const handleCopyUrl = () => {
    // URLコピーは「現在入力中の条件」ではなく、最後に検索した条件をコピーする。
    // ここで setSearchParams すると auto-search:url が走るため、URLコピーではアドレスバーを更新しない。
    const lastQuery =
      lastSearchMetaRef.current?.query ??
      lastSearchMeta?.query ??
      searchParams.toString();

    const copiedParams = new URLSearchParams(lastQuery);
    if (!getValidRecentPresetFromParams(copiedParams)) {
      copiedParams.delete(START_DATE_PRESET_PARAM);
    }

    const url = new URL(window.location.href);
    url.search = copiedParams.toString();

    const toCopy = decodeURI(url.toString());

    navigator.clipboard.writeText(toCopy).then(() => {
      setSnackbarMsg('URL\u3092\u30b3\u30d4\u30fc\u3057\u307e\u3057\u305f');
      setSnackbarOpen(true);
    });
  };
  /* =========================================================
   * 週報更新通知の購読（表示中なら即 revalidate）
   * ======================================================= */
  useEffect(() => {
    const tryRevalidate = (trigger: string) => {
      // まだ初期化が揃ってないなら “キュー” だけ入れる（auto useEffect が拾う）
      const ready =
        !state.loading &&
        isViewerAccessContextReady &&
        didHydrateFromLocalStorage &&
        didHydrateResultsFromLocalStorage &&
        pendingDeptHydrationDone &&
        initialSetupDone &&
        isReportTypeAccessReady &&
        !isDateRangeIncomplete &&
        !hasDateError;

      const {
        needs,
        updatedAtMs,
      } = computeNeedsRevalidateByUpdateSignal();

      if (!needs) return;

      if (updatedAtMs != null) {
        lastHandledUpdatedAtMsRef.current = updatedAtMs;
      }

      if (!ready) {
        // auto-search 側で拾わせる
        setAutoSearchRequested(true);
        return;
      }

      handleSearch({ reason: `revalidate:${trigger}` });
    };

    const invalidateAccessSensitiveResults = () => {
      // 権限変更直後に、以前のチームで見えていた内容を画面へ残さない。
      try {
        localStorage.removeItem(resultsLocalStorageKey);
      } catch {
        // キャッシュ削除に失敗しても、画面上の古い結果は必ず破棄する。
      }
      setLocalResults([]);
      onResults([]);
      creatorSyncForceRef.current = true;
      setCreatorSyncTick((value) => value + 1);
      lastAutoSearchKeyRef.current = null;
      setAutoSearchRequested(true);
    };

    const onCustom = () => tryRevalidate('custom-event');
    const onAccessContextUpdated = () => invalidateAccessSensitiveResults();

    const onStorage = (e: StorageEvent) => {
      if (e.key === ACCESS_CONTEXT_UPDATED_AT_KEY) {
        // 別タブでは次回表示時に最新プロフィールを取得するため、古い結果だけ破棄する。
        try {
          localStorage.removeItem(resultsLocalStorageKey);
        } catch {
          // 表示の破棄は継続する。
        }
        setLocalResults([]);
        onResults([]);
        return;
      }
      if (e.key !== WEEKLY_REPORT_UPDATED_AT_KEY) return;
      tryRevalidate('storage-event');
    };

    window.addEventListener(WEEKLY_REPORT_UPDATED_EVENT, onCustom);
    window.addEventListener(ACCESS_CONTEXT_UPDATED_EVENT, onAccessContextUpdated);
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener(WEEKLY_REPORT_UPDATED_EVENT, onCustom);
      window.removeEventListener(ACCESS_CONTEXT_UPDATED_EVENT, onAccessContextUpdated);
      window.removeEventListener('storage', onStorage);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.loading,
    isViewerAccessContextReady,
    didHydrateFromLocalStorage,
    didHydrateResultsFromLocalStorage,
    pendingDeptHydrationDone,
    initialSetupDone,
    isReportTypeAccessReady,
    isDateRangeIncomplete,
    hasDateError,
    computeNeedsRevalidateByUpdateSignal,
    resultsLocalStorageKey,
  ]);


  useEffect(() => {
    if (state.loading) return;
    if (!isViewerAccessContextReady) return;
    if (!didHydrateFromLocalStorage) return;
    if (!didHydrateResultsFromLocalStorage) return;
    if (!pendingDeptHydrationDone) return;
    if (!initialSetupDone) return;
    if (!isReportTypeAccessReady) return;
    if (
      selectedCreateBy.length > 0 &&
      (creatorOptionsResolvedKey !== creatorOptionsCriteriaKey ||
        creatorSelectionNormalizedKey !== creatorOptionsCriteriaKey)
    ) return;
    if (isDateRangeIncomplete) return;
    if (hasDateError) return;

    const needsSWRRevalidate = swrRevalidatePendingRef.current;

    const {
      needs: needsRevalidateBySignal,
      updatedAtMs,
      baseMs,
    } = computeNeedsRevalidateByUpdateSignal();

    const currentUrlKey = normalizeQueryString(searchParams.toString());

    const hasUrlCondition =
      currentUrlKey.length > 0 &&
      urlHasSearchCondition &&
      (
        !didInitialAutoSearch.current ||
        lastUrlSearchKeyRef.current !== currentUrlKey
      );

    const shouldAutoSearchNow =
      hasUrlCondition ||
      autoSearchRequested ||
      needsSWRRevalidate ||
      needsRevalidateBySignal;

    const uniq = (arr: string[]) => Array.from(new Set((arr ?? []).map(String)));

    const buildUiFingerprint = () => {
      const fmtYmd = (d: Date | null): string | null => {
        if (!d) return null;
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${dd}`;
      };

      const reportTypeIds = getReportTypeIdsForSearch(selectedReportTypes);
      const createByForSearch =
        createByTouched || hasInitialCreateByParam.current
          ? uniq(selectedCreateBy)
          : [];

      const createByNone =
        Boolean(createByTouched) && createByForSearch.length === 0;

      return JSON.stringify({
        start: fmtYmd(startDate),
        end: fmtYmd(endDate),
        freeWord: (freeWord ?? '').trim(),
        searchTitleBody,
        searchTags,
        teams: uniq(selectedTeams),
        depts: uniq(effectiveDeptIds),
        createBy: createByForSearch,
        reportTypes: reportTypeIds,
        createByNone,
        startDatePreset: dateSearchMode === 'recent' ? startDatePreset ?? null : null,
      });
    };

    const autoKey = hasUrlCondition
      ? `url::${currentUrlKey}`
      : `ui::${buildUiFingerprint()}`;

    if (!shouldAutoSearchNow) {
      return;
    }
    if (lastAutoSearchKeyRef.current === autoKey) {
      return;
    }

    lastAutoSearchKeyRef.current = autoKey;

    if (needsRevalidateBySignal && updatedAtMs != null) {
      lastHandledUpdatedAtMsRef.current = updatedAtMs;
    }

    if (hasUrlCondition) {
      didInitialAutoSearch.current = true;
      lastUrlSearchKeyRef.current = currentUrlKey;
    }

    if (needsSWRRevalidate) {
      swrRevalidatePendingRef.current = false;
    }

    if (autoSearchRequested) {
      setAutoSearchRequested(false);
    }

    const reason = needsRevalidateBySignal
      ? `auto-revalidate:update-signal(ms:${updatedAtMs},base:${baseMs})`
      : needsSWRRevalidate
        ? 'auto-revalidate:swr'
        : hasUrlCondition
          ? 'auto-search:url'
          : 'auto-search:cache';

    void handleSearch({ reason });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.loading,
    isViewerAccessContextReady,
    didHydrateFromLocalStorage,
    didHydrateResultsFromLocalStorage,
    pendingDeptHydrationDone,
    initialSetupDone,
    isReportTypeAccessReady,
    isDateRangeIncomplete,
    hasDateError,
    urlHasSearchCondition,
    searchParams,
    autoSearchRequested,
    startDate,
    endDate,
    freeWord,
    searchTitleBody,
    searchTags,
    selectedTeams,
    selectedCreateBy,
    creatorOptionsResolvedKey,
    creatorOptionsCriteriaKey,
    creatorSelectionNormalizedKey,
    selectedReportTypes,
    effectiveDeptIds,
    createByTouched,
    dateSearchMode,
    startDatePreset,
    computeNeedsRevalidateByUpdateSignal,
    getReportTypeIdsForSearch,
  ]);

  // マスタ取得
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const isActive = () => !cancelled && mountedRef.current && !controller.signal.aborted;

    apiClient
      .get<MasterResponse>('/weekly-required-items', {
        params: { include_creators: false },
        signal: controller.signal,
      })
      .then((r) => {
        if (!isActive()) return;
        setState({ loading: false, error: null, data: r.data });
      })
      .catch((e) => {
        if (!isActive() || isAbortError(e)) return;
        setState({
          loading: false,
          error: e instanceof Error ? e.message : 'Unknown error',
          data: null,
        });
      });

    apiClient
      .get<any[]>('/get_all_teams', { signal: controller.signal })
      .then((r) => {
        if (!isActive()) return;
        setTeamsMaster(Array.isArray(r.data) ? r.data : []);
        setTeamsLoaded(true);
      })
      .catch((e) => {
        if (!isActive() || isAbortError(e)) return;
        console.error('チーム取得エラー:', e);
        setTeamsMaster([]);
        setTeamsLoaded(true);
      });

    apiClient
      .get<any[]>('/get_all_team_classfications', { signal: controller.signal })
      .then((r) => {
        if (!isActive()) return;
        const data = r?.data;
        const list = Array.isArray(data)
          ? data
          : Array.isArray((data as any)?.team_classfications)
            ? (data as any).team_classfications
            : Array.isArray((data as any)?.team_classifications)
              ? (data as any).team_classifications
              : Array.isArray((data as any)?.list)
                ? (data as any).list
                : Array.isArray((data as any)?.data)
                  ? (data as any).data
                  : [];
        setTeamClassificationsMaster(list);
      })
      .catch((e) => {
        if (!isActive() || isAbortError(e)) return;
        console.error('チーム分類取得エラー:', e);
        setTeamClassificationsMaster([]);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  // 発行者候補の先行200件と全件取得を最優先する。
  // 全ユーザー情報は発行者のバックグラウンド取得が完了してから取得し、
  // 初回表示時に10,000件の通信・整形処理が競合しないようにする。
  const usersMasterRequestStartedRef = useRef(false);
  useEffect(() => {
    if (usersMasterRequestStartedRef.current) return;
    if (creatorOptionsByDate == null) return;
    if (creatorOptionsLoadingRemaining) return;

    usersMasterRequestStartedRef.current = true;
    let cancelled = false;
    let completed = false;

    shareInFlightRequest(
      'get-all-user-info:all',
      () => apiClient.get<any[]>('/get_all_user_info'),
    )
      .then((response) => {
        if (cancelled) return;
        completed = true;
        setUsersMaster(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        if (cancelled || isAbortError(error)) return;
        completed = true;
        console.error('ユーザー取得エラー:', error);
        setUsersMaster([]);
      });

    return () => {
      cancelled = true;
      if (!completed) usersMasterRequestStartedRef.current = false;
    };
  }, [creatorOptionsByDate, creatorOptionsLoadingRemaining]);

  // 初期セット（課の初期選択 / create_by / report_types の正規化）
  useEffect(() => {
    if (isInitialSetupDone.current) return;
    if (!didHydrateFromLocalStorage) return;
    if (state.loading || !state.data) return; // マスタ未準備ならスキップ

    let didInitDept = false;
    let didInitCreateBy = false;
    let didInitReportTypes = false;

    // 1) URL の departments があれば最優先で反映
    if (selectedDepts.length === 0 && !deptTouched) {
      const urlDeps = searchParams.getAll('departments').map(String);
      if (urlDeps.length > 0) {
        const picks = deptOptions.filter((d) => urlDeps.includes(String(d.id)));
        if (picks.length > 0) {
          setSelectedDepts(picks);
          // URL からの明示指定なので、以降のデフォルト適用を抑制する
          setDeptTouched(true);
          didInitDept = true;
        }
      }
    } else if (selectedDepts.length > 0) {
      didInitDept = true;
    }

    // 2) URL 指定がなく、selectedDepts も空なら「全課」をデフォルトにする
    if (!didInitDept && selectedDepts.length === 0 && !deptTouched) {
      didInitDept = true;
    } else if (!didInitDept && deptTouched) {
      // ユーザーが触った結果 selectedDepts が空の場合は「全課」にしたい意図なので、初期化完了扱い
      didInitDept = true;
    }

    // URLのuser_id/氏名は専用API到着前に旧wr_idへ推測変換しない。
    // これにより同姓同名の先勝ちと、旧候補が空の時の初期化待ちループを避ける。
    if (hasInitialCreateByParam.current) {
      didInitCreateBy = true;
    } else {
      // URL で「記入者：条件なし」を明示している場合は、
      // デフォルト（自分）を入れず、空を尊重する
      const urlCreateByNone = (() => {
        const v = searchParams.get('create_by_none');
        if (!v) return false;
        const s = String(v).trim().toLowerCase();
        return s === '1' || s === 'true' || s === 'yes';
      })();

      if (urlCreateByNone) {
        if (selectedCreateBy.length !== 0) {
          setSelectedCreateBy([]);
        }
        if (!createByTouched) {
          setCreateByTouched(true);
        }
        didInitCreateBy = true;
      } else if (!createByTouched && !hasInitialCreateByParam.current) {
        // デフォルトで create_by を自動適用しない。
        // 過去キャッシュ由来の暗黙選択が残っている場合はここで解除する。
        if (selectedCreateBy.length !== 0) {
          setSelectedCreateBy([]);
        }
        didInitCreateBy = true;
      } else if (selectedCreateBy.length > 0) {
        didInitCreateBy = true;
      } else {
        // ユーザーが「条件なし」などで触った後は、空を尊重する
        didInitCreateBy = true;
      }
    }

    // 3) report_types のURL補正（"?report_types=（ラベル）" → マスタの id に揃える）
    // URL の report_types は label で来る（buildWeeklySearchParams が label を送る）ため、
    // UI（MultiSelectCheckbox）は id でチェックされるように正規化する。
    if (hasInitialReportTypesParam.current && rawReportTypes.length > 0) {
      setSelectedReportTypes((prevValues) => {
        // すでにID（= masterの reportType.id）だけならそのまま
        const alreadyIds = prevValues.every((v) =>
          rawReportTypes.some((rt) => String(rt.id) === String(v))
        );
        if (alreadyIds) return prevValues;

        const picked = new Set<string>();

        prevValues.forEach((labelOrId) => {
          // id を直接渡されているケースにも一応対応
          const directIdHit = rawReportTypes.find(
            (rt) => String(rt.id) === String(labelOrId)
          );
          if (directIdHit) {
            picked.add(String(directIdHit.id));
            return;
          }

          // label 一致は複数ヒットし得る（部署差など）。一致分は全て選択に入れる。
          const hits = rawReportTypes.filter(
            (rt) => String(rt.label) === String(labelOrId)
          );
          if (hits.length > 0) {
            hits.forEach((rt) => picked.add(String(rt.id)));
          } else {
            // どうしても解決できない値は捨てずに残す（後方互換）
            picked.add(String(labelOrId));
          }
        });

        return Array.from(picked);
      });

      didInitReportTypes = true;
    } else {
      // URL に report_types が無い or マスタが空のケース
      didInitReportTypes = true;
    }

    // URLパラメータ正規化の完了フラグ（自動検索のゲート）
    if (!initialParamsReady) setInitialParamsReady(true);

    // Dept と CreateBy が両方「初期化完了」したら固定する
    if (didInitDept && didInitCreateBy && didInitReportTypes) {
      isInitialSetupDone.current = true;

      // state も更新（ref だけだと別 useEffect 側で検知しづらい）
      if (!initialSetupDone) {
        setInitialSetupDone(true);
      }
    }
  }, [
    state.loading,
    state.data,
    myDeptId,
    deptOptions,
    selectedDepts.length,
    selectedCreateBy.length,
    creatorOptionsRaw,
    rawReportTypes,
    searchParams,
    deptTouched,
    initialParamsReady,
    initialSetupDone,
    didHydrateFromLocalStorage,
    createByTouched,
  ]);

  // ==== usersMaster の取得が遅れても、未操作時は「全課」を維持 ====
  useEffect(() => {
    return;
  }, []);

  // メール関係
  const resolvedMailFormat = useMemo(
    () => resolveMailFormat(mailFormatState),
    [mailFormatState]
  );
  const selectedMailPresetLabel = useMemo(
    () => getMailPresetLabel(resolvedMailFormat.preset),
    [resolvedMailFormat.preset]
  );
  const selectedMailPresetHelper = useMemo(
    () =>
      MAIL_FORMAT_PRESET_OPTIONS.find((option) => option.value === resolvedMailFormat.preset)
        ?.helper ?? '',
    [resolvedMailFormat.preset]
  );
  const mailReports = useMemo(
    () => (Array.isArray(mailExportReports) ? mailExportReports : []),
    [mailExportReports]
  );
  const mailReportsOrderSignature = useMemo(
    () =>
      mailReports
        .map((r, idx) => String((r as any)?.wr_id ?? (r as any)?.WR_ID ?? idx))
        .join('|'),
    [mailReports]
  );
  const perItemEml = useMemo(() => {
    if (!dlgOpen || mailReports.length === 0) {
      return { items: [] };
    }
    const wrapConfig = resolveDefaultMailTextWrapConfig();
    return buildEmlPerItem(mailReports, {
      wrapConfig,
      resolvedFormat: resolvedMailFormat,
    });
  }, [dlgOpen, mailReports, resolvedMailFormat]);
  const hasMailItems = mailReports.length > 0;

  // ===== UIアクションの二重発火ガード（click一本化 + signature方式）=====
  const UI_ACTION_DEDUP_MS = 8000;

  const lastUiActionRef = useRef<{
    key: string;
    sig: string;
    at: number;
  } | null>(null);

  const stopEvent = (e: any) => {
    try { e.preventDefault?.(); } catch {}
    try { e.stopPropagation?.(); } catch {}
  };

  const runUiActionOnce = useCallback((key: string, sig: string, fn: () => void) => {
    const now = Date.now();
    const last = lastUiActionRef.current;

    if (last && last.key === key && last.sig === sig && now - last.at < UI_ACTION_DEDUP_MS) {
      return;
    }
    lastUiActionRef.current = { key, sig, at: now };
    fn();
  }, []);

  const handleOpenMailClick = useCallback((e: React.MouseEvent) => {
    stopEvent(e);
    if (!hasMailItems) return;

    runUiActionOnce(
      'open-mail-dialog',
      `count:${mailReports.length}|order:${mailReportsOrderSignature}`,
      () => {
        setDlgOpen(true);
      }
    );
  }, [
    hasMailItems,
    mailReports,
    mailReportsOrderSignature,
    runUiActionOnce,
  ]);

  const handleDownloadEmlClick = (e?: React.MouseEvent) => {
    if (e) stopEvent(e);
    if (!mailReports.length) return;

    const filename = buildEmlFilename();
    const wrapConfig = resolveDefaultMailTextWrapConfig();
    const eml = buildEmlText(mailReports, {
      deptLabelMap: deptIdToLabelMap,
      teamLabelMap: teamIdToLabelMap,
      mailFormat: resolvedMailFormat,
      wrapConfig,
    });
    const sig = `${filename}::${eml.length}::${eml.slice(0, 128)}`;

    runUiActionOnce('download-eml-all', sig, () => {
      void downloadEml(filename, eml, { preferFilePicker: false });
      setSnackbarMsg('.eml をダウンロードしました');
      setSnackbarOpen(true);
      setDlgOpen(false);
    });
  };

  const teamLabel = 'チーム';
  const createByLabel = (
    <>
      発行者
      <Box component="span" sx={{ fontSize: '75%' }}>
        （開始-終了日付）
      </Box>
    </>
  );
  const reportTypeLabel = CLASSIFICATION_MARKET_TARGET_ATTR_NAME;
  const deptPlaceholder = '条件なし';

  // MultiSelectWithCheckboxes は initialValue 変更を内部で同期するため、
  // 課選択のたびに key で強制リマウントしない（ドロップダウンを閉じないため）

  // ===== “onChange([])” で一瞬空になるケースのガード（検索画面用・軽め） =====
  const handleTeamsChange = (vals: string[]) => {
    setSelectedTeams((prev) => {
      if (vals.length === 0 && prev.length > 0 && teamOptionsFiltered.length === 0) {
        return prev;
      }
      return vals;
    });
  };

  const handleCreateByChange = (vals: string[]) => {
    const nextRaw = Array.isArray(vals) ? vals : [];

    setSelectedCreateBy((prev) => {
      // options が空の瞬間に「クリア扱い」になって prev を消す事故を防ぐ
      if (
        nextRaw.length === 0 &&
        (prev ?? []).length > 0 &&
        (!Array.isArray(createByOptionsFiltered) || createByOptionsFiltered.length === 0)
      ) {
        return prev;
      }

      // 「表示中の options に存在するものだけ」に正規化
      // （全選択/全解除が“表示中だけ”対象でも、隠れ選択を残さない）
      const next =
        Array.isArray(createByOptionsFiltered) && createByOptionsFiltered.length > 0
          ? sanitizeSelectedIdsByOptions(nextRaw, createByOptionsFiltered)
          : nextRaw;

      if (isSameStringArray(prev ?? [], next)) return prev;
      return next;
    });

    // ユーザーが記入者フィルタを触った（条件なしも含む）
    if (!createByTouched) {
      setCreateByTouched(true);
    }
  };

  const handleReportTypesChange = (vals: string[]) => {
    setSelectedReportTypes((prev) => {
      if (vals.length === 0 && prev.length > 0 && reportOptionsFiltered.length === 0) {
        return prev;
      }
      return vals;
    });
  };

  const handleMailIncludeFeedbackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setMailFormatState((prev) => ({
      ...prev,
      includeFeedback: checked,
    }));
  };

  const handleMailAppWrapChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setMailFormatState((prev) => ({
      ...prev,
      preset: checked ? MAIL_FORMAT_PRESET_APP : MAIL_FORMAT_PRESET_NO_WRAP,
    }));
  };

  const mailFormatSummary = `${selectedMailPresetLabel} / ${
    resolvedMailFormat.includeFeedback ? 'FBあり' : 'FBなし'
  }`;
  const isAppMailWrap = resolvedMailFormat.preset === MAIL_FORMAT_PRESET_APP;

  const dateSearchDesktopWidth = dateSearchMode === 'recent' ? 420 : 520;
  const secondaryFilterGridSx = {
    mt: '8px',
    '@media (min-width:1200px)': {
      flex: '1 1 190px',
      maxWidth: 'none',
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Box className="search-content">
        <Box
          sx={(theme) => ({
            p: 2,
            backgroundColor: theme.palette.mode === 'dark' ? '#0b1120' : '#f9f9fb',
            borderRadius: '12px',
            boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 4px 6px rgba(0,0,0,0.1)',
            mb: 3,
          })}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid
              size={{
                xs: 12,
                sm: 12,
                md: 4.5,
              }}
              sx={{
                '@media (min-width:1200px)': {
                  flex: `0 0 ${dateSearchDesktopWidth}px`,
                  maxWidth: `${dateSearchDesktopWidth}px`,
                },
              }}
            >
              <label className="label">
                検索期間
              </label>

              <Box
                sx={{
                  mt: 0.35,
                  display: 'flex',
                  alignItems: 'stretch',
                  gap: 1.2,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <FormGroup
                  sx={(theme) => ({
                    mb: 0,
                    px: 0.5,
                    py: 0.35,
                    borderRadius: '12px',
                    border: `1px solid ${theme.palette.mode === 'dark' ? '#26384f' : '#dbe3ee'}`,
                    backgroundColor: theme.palette.mode === 'dark' ? '#162235' : '#f8fafc',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    flexShrink: 0,
                    minWidth: { xs: '100%', sm: 92 },
                    '& .MuiFormControlLabel-root': {
                      margin: 0,
                      marginBottom: 0.25,
                      borderRadius: '8px',
                      paddingInline: 0.35,
                      transition: 'background-color 0.2s ease',
                    },
                    '& .MuiFormControlLabel-root:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? '#1c2b42' : '#eef2f7',
                    },
                    '& .MuiFormControlLabel-root:last-of-type': {
                      marginBottom: 0,
                    },
                    '& .MuiFormControlLabel-label': {
                      fontSize: 12,
                      lineHeight: 1,
                      fontWeight: theme.palette.mode === 'dark' ? 500 : 400,
                      color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#475569',
                    },
                    '& .MuiCheckbox-root': {
                      padding: 0.25,
                      color: theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b',
                      '&.Mui-checked': {
                        color: theme.palette.mode === 'dark' ? '#38bdf8' : '#2563eb',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: 20,
                      },
                    },
                  })}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={dateSearchMode === 'recent'}
                        onChange={handleDateSearchModeChange('recent')}
                      />
                    }
                    label="直近"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={dateSearchMode === 'range'}
                        onChange={handleDateSearchModeChange('range')}
                      />
                    }
                    label="期間検索"
                  />
                </FormGroup>

                {dateSearchMode === 'recent' ? (
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      p: { xs: 1, sm: 1.1 },
                      borderRadius: '14px',
                      border: '1px solid',
                      borderColor: muiTheme.palette.mode === 'dark' ? '#26384f' : recentPresetTone.border,
                      backgroundColor: muiTheme.palette.mode === 'dark' ? '#111a2b' : recentPresetTone.surface,
                      boxShadow: '0 12px 26px -22px rgba(15, 23, 42, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      transition: 'background-color 0.2s ease, border-color 0.2s ease',
                      '@media (min-width:1200px)': {
                        flex: '0 1 310px',
                        maxWidth: '310px',
                      },
                    }}
                  >
                    <Button
                      type="button"
                      size="small"
                      variant="outlined"
                      onClick={() => shiftRecentPreset(-1)}
                      disabled={currentRecentPreset === 'last'}
                      startIcon={<ChevronLeftIcon />}
                      sx={recentPresetButtonSx}
                    >
                      Prev
                    </Button>

                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        textAlign: 'center',
                        px: 0.35,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Box sx={{ fontSize: 14, fontWeight: 700, color: muiTheme.palette.mode === 'dark' ? '#e2e8f0' : '#0f172a' }}>
                        {recentPresetLabel}
                      </Box>
                      <Box sx={{ fontSize: 12, color: muiTheme.palette.mode === 'dark' ? '#94a3b8' : '#475569' }}>
                        {recentRangeLabel}
                      </Box>
                    </Box>

                    <Button
                      type="button"
                      size="small"
                      variant="outlined"
                      onClick={() => shiftRecentPreset(1)}
                      disabled={currentRecentPreset === 'next'}
                      endIcon={<ChevronRightIcon />}
                      sx={recentPresetButtonSx}
                    >
                      Next
                    </Button>
                  </Box>
                ) : (
                  <Box
                    sx={(theme) => ({
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      borderRadius: '14px',
                      border: `1px solid ${theme.palette.mode === 'dark' ? '#26384f' : '#dbe3ee'}`,
                      backgroundColor: theme.palette.mode === 'dark' ? '#162235' : '#f8fafc',
                      flexWrap: { xs: 'wrap', lg: 'nowrap' },
                    })}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: { xs: '100%', sm: 170 },
                        position: 'relative',
                      }}
                    >
                      <Box
                        component="span"
                        className="label"
                        sx={{
                          position: 'absolute',
                          top: -26,
                          left: 0,
                          pointerEvents: 'none',
                          whiteSpace: 'nowrap',
                          zIndex: 1,
                          lineHeight: 1,
                        }}
                      >
                        開始日付
                      </Box>
                      <DatePicker
                        value={startDate}
                        onChange={(newVal, context) => {
                          // validationError がある値は state に入れない（=無効な日付は保存されない）
                          if ((context as any)?.validationError) return;
                          setStartDate(newVal);

                          // 手動変更したらプリセットは外す
                          if (startDatePreset) setStartDatePreset(null);

                          if (!newVal) return;
                          if (endDate && stripTime(newVal) > stripTime(endDate)) {
                            setEndDate(null);
                            setEndDatePickerOpen(true);
                          }
                        }}
                        onError={(newError) => setStartDatePickerError(newError)}
                        slots={{ day: WeeklyHighlightDay }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            margin: 'none',
                            error: Boolean(startDatePickerError),
                            helperText: startDateHelperText || undefined,
                            sx: (theme: any) => ({
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                                backgroundColor: theme.palette.mode === 'dark' ? '#162235' : '#fff',
                                color: theme.palette.mode === 'dark' ? '#e2e8f0' : 'inherit',
                              },
                            }),
                          },
                          switchViewIcon: { sx: { display: 'none' } },
                          day: {
                            highlightedWeeks,
                            rangeStartDate: startDate,
                            rangeEndDate: endDate,
                            showRangeStartMarker: Boolean(startDate && endDate),
                            showRangeEndMarker: Boolean(startDate && endDate),
                          } as any,
                        }}
                      />
                    </Box>

                    <Box sx={{ color: 'text.secondary', px: 0.5 }}>～</Box>

                    <Box
                      sx={{
                        flex: 1,
                        minWidth: { xs: '100%', sm: 170 },
                        position: 'relative',
                      }}
                    >
                      <Box
                        component="span"
                        className="label"
                        sx={{
                          position: 'absolute',
                          top: -26,
                          left: 0,
                          pointerEvents: 'none',
                          whiteSpace: 'nowrap',
                          zIndex: 1,
                          lineHeight: 1,
                        }}
                      >
                        終了日付
                      </Box>
                      <DatePicker
                        value={endDate}
                        open={endDatePickerOpen}
                        onOpen={() => setEndDatePickerOpen(true)}
                        onClose={() => setEndDatePickerOpen(false)}
                        onChange={(newVal, context) => {
                          // validationError がある値は state に入れない（=無効な日付は保存されない）
                          if ((context as any)?.validationError) return;
                          setEndDate(newVal);
                          if (startDatePreset) setStartDatePreset(null);
                        }}
                        onError={(newError) => setEndDatePickerError(newError)}
                        minDate={startDate ?? undefined}
                        slots={{ day: WeeklyHighlightDay }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            margin: 'none',
                            error: Boolean(endDatePickerError) || Boolean(dateRangeErrorMsg),
                            helperText: endDateHelperText || undefined,
                            sx: (theme: any) => ({
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                                backgroundColor: theme.palette.mode === 'dark' ? '#162235' : '#fff',
                                color: theme.palette.mode === 'dark' ? '#e2e8f0' : 'inherit',
                              },
                            }),
                          },
                          switchViewIcon: { sx: { display: 'none' } },
                          day: {
                            highlightedWeeks,
                            rangeStartDate: startDate,
                            rangeEndDate: endDate,
                            showRangeStartMarker: true,
                          } as any,
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* 課（department） */}
            <Grid
              size={{ xs: 12, sm: 6, md: 1.5 }}
              sx={{
                mt: '6px',
                '@media (min-width:1200px)': {
                  flex: '1 1 150px',
                  maxWidth: 'none',
                },
              }}
            >
              <label className="label">課</label>
              <div className="adjustment" />
              <Box
                className="search-section-autocomplete"
                sx={(theme) => ({
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.mode === 'dark' ? '#111a2b' : '#fff',
                  },
                  '& .MuiOutlinedInput-root fieldset': {
                    borderColor: theme.palette.mode === 'dark' ? '#26384f' : '#e0e0e0',
                  },
                  '& .MuiOutlinedInput-root:hover fieldset': {
                    borderColor: theme.palette.mode === 'dark' ? '#475569' : '#bdbdbd',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                    borderColor: theme.palette.mode === 'dark' ? '#475569' : '#bdbdbd',
                    boxShadow: 'none',
                  },
                  '& .MuiInputBase-root:not(.MuiAutocomplete-hasClearIcon)': {
                    alignItems: 'center',
                  },
                  '& .MuiAutocomplete-hasClearIcon': {
                    alignItems: 'flex-start',
                  },
                  '& .MuiInputBase-root': { minHeight: 56 },
                })}
              >
                <MultiSelectWithCheckboxes
                  suggestions={deptOptions}
                  placeholder={deptPlaceholder}
                  initialValue={selectedDepts}
                  onChange={(vals) => {
                    setSelectedDepts(vals);
                    setDeptTouched(true); // ユーザーが課フィルタを触ったと記録
                  }}
                />
              </Box>
            </Grid>

            {/* チーム（入力フィルタ + 全てを選択＝表示中のみ） */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }} sx={secondaryFilterGridSx}>
              <MultiSelectCheckbox
                label={teamLabel}
                options={teamOptionsFiltered}
                selectedValues={selectedTeams}
                onChange={handleTeamsChange}
              />
            </Grid>

            {/* 記入者（入力フィルタ + 全てを選択＝表示中のみ） */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }} sx={secondaryFilterGridSx}>
              <MultiSelectCheckbox
                label={createByLabel}
                options={createByOptionsFiltered}
                selectedValues={selectedCreateByForDisplay}
                onChange={handleCreateByChange}
                identityMode="id"
                maxRenderedOptions={CREATOR_OPTION_DISPLAY_LIMIT}
                maxSearchRenderedOptions={CREATOR_SEARCH_RESULT_LIMIT}
                initialRenderedOptions={100}
                renderedOptionsStep={100}
                renderAllOptionsWhenLoaded
                maxAutoRenderedOptions={CREATOR_OPTION_DISPLAY_LIMIT}
                loadedOptionCount={createByOptionsFiltered.length}
                allOptionsLoaded={progressiveCreatorOptions.allOptionsLoaded}
                optionDisplayReady={creatorOptionsReadyForCurrentCriteria}
                remoteOptions={progressiveCreatorOptions.remoteSearchOptions}
                onFilterTextChange={progressiveCreatorOptions.onSearchTextChange}
                searchLoading={progressiveCreatorOptions.searchLoading}
                searchResultsComplete={progressiveCreatorOptions.searchResultsComplete}
                searchError={progressiveCreatorOptions.searchError}
                showSearchLoadingMessage={false}
                virtualizeOptions
                disableMenuTransition
                fixedVirtualRowHeight
                countUnit="名"
                onOpen={progressiveCreatorOptions.retry}
                loading={creatorOptionsLoadingRemaining}
                loadingText={progressiveCreatorOptions.loadingText}
                showLoadingMessage={false}
              />
            </Grid>

            {/* 週報分類（入力フィルタ + 全てを選択＝表示中のみ） */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }} sx={secondaryFilterGridSx}>
              <MultiSelectCheckbox
                label={reportTypeLabel}
                options={reportOptionsFiltered}
                selectedValues={selectedReportTypes}
                onChange={handleReportTypesChange}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="flex-end" sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <label className="label">フリーワード</label>
                <FormGroup
                  row
                  sx={(theme) => ({
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    '& .MuiFormControlLabel-root': {
                      margin: 0,
                      marginLeft: 1,
                    },
                    '& .MuiFormControlLabel-label': {
                      fontSize: 12,
                      lineHeight: 1,
                      color: theme.palette.mode === 'dark' ? '#e2e8f0' : 'inherit',
                    },
                    '& .MuiCheckbox-root': {
                      padding: 0.5,
                      color: theme.palette.mode === 'dark' ? '#94a3b8' : undefined,
                      '&.Mui-checked': {
                        color: theme.palette.mode === 'dark' ? '#38bdf8' : undefined,
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: 22,
                      },
                    },
                  })}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={searchTitleBody}
                        onChange={(e) => setSearchTitleBody(e.target.checked)}
                      />
                    }
                    label="タイトル&本文"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={searchTags}
                        onChange={(e) => setSearchTags(e.target.checked)}
                      />
                    }
                    label="タグ"
                  />
                </FormGroup>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 1,
                }}
              >
                <TextField
                  className="freeWord"
                  label="フリーワード"
                  fullWidth
                  margin="normal"
                  value={freeWord}
                  onChange={(e) => setFreeWord(e.target.value)}
                  slotProps={{
                    htmlInput: {
                      style: {
                        height: 40,
                        fontSize: 18,
                        padding: '8px',
                      },
                    },
                  }}
                  sx={{ flex: 1 }}
                />
                <Tooltip title="URLをコピー">
                  <IconButton
                    color="primary"
                    type="button"
                    onClick={handleCopyUrl}
                    sx={{ mb: 0.75 }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip
                  title={`メール作成（${mailFormatSummary}）`}
                  disableInteractive
                  disableTouchListener
                >
                  <span>
                    <IconButton
                      color="primary"
                      disabled={!hasMailItems}
                      type="button"
                      onClick={handleOpenMailClick}
                      sx={{ mb: 0.75 }}
                    >
                      <EmailIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Grid>

            <Grid
              size={{ xs: 12, md: 5.7 }}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                alignItems: 'flex-end',
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                type="button"
                onClick={handleSearch}
                disabled={hasDateError || isDateRangeIncomplete}
                sx={{ height: 40 }}
              >
                検索
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Dialog
          open={dlgOpen}
          onClose={() => setDlgOpen(false)}
          maxWidth="lg"
          fullWidth
          slotProps={{
            paper: {
              className: 'mail-create-dialog-paper',
            },
          }}
        >
          <DialogTitle className="mail-create-dialog-title">
            <span className="mail-create-dialog-heading">メール作成</span>
            <span className="mail-create-dialog-count">
              {perItemEml.items.length}件
            </span>
          </DialogTitle>
          <DialogContent className="mail-create-dialog-content">
            <Box className="mail-create-style-panel">
              <h2 className="mail-create-section-title">スタイル</h2>
              <div className="mail-create-toggle-group">
                <label className="mail-format-toggle-row mail-create-toggle-row">
                  <span className="mail-format-toggle-label">FBを記載</span>
                  <span className="mail-format-switch">
                    <input
                      type="checkbox"
                      className="mail-format-sr-only mail-format-switch-input"
                      checked={mailFormatState.includeFeedback}
                      onChange={handleMailIncludeFeedbackChange}
                    />
                    <span className="mail-format-switch-track" />
                  </span>
                </label>

                <label className="mail-format-toggle-row mail-create-toggle-row">
                  <span className="mail-format-toggle-label">APPを元に改行</span>
                  <span className="mail-format-switch">
                    <input
                      type="checkbox"
                      className="mail-format-sr-only mail-format-switch-input"
                      checked={isAppMailWrap}
                      onChange={handleMailAppWrapChange}
                    />
                    <span className="mail-format-switch-track" />
                  </span>
                </label>
              </div>
              <div className="mail-create-info-row">
                <InfoOutlinedIcon className="mail-format-info-icon" />
                <span>
                  {selectedMailPresetLabel}: {selectedMailPresetHelper}
                </span>
              </div>
            </Box>

            <Box className="mail-create-output-section">
              <h2 className="mail-create-section-title">出力対象</h2>
              <List dense className="mail-create-output-list">
                {perItemEml.items.map((item, idx) => (
                  <React.Fragment key={`mail-item-${idx}`}>
                    <ListItem className="mail-create-output-item">
                      <ListItemText
                        primary={`No.${item.index1based}（文字数: ${item.rawLength}）`}
                        secondary={
                          item.preview.length > 140
                            ? item.preview.substring(0, 140) + '...'
                            : item.preview || '(本文なし)'
                        }
                        slotProps={{
                          primary: {
                            className: 'mail-create-output-primary',
                          },
                          secondary: {
                            className: 'mail-create-output-secondary',
                          },
                        }}
                      />
                    </ListItem>
                    {idx < perItemEml.items.length - 1 && (
                      <Divider component="li" />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          </DialogContent>
          <DialogActions className="mail-create-dialog-actions">
            <Button
              type="button"
              variant="contained"
              className="mail-create-cancel-button"
              onClick={() => setDlgOpen(false)}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="contained"
              className="mail-create-download-button"
              onClick={handleDownloadEmlClick}
              disabled={!mailReports.length}
            >
              ダウンロード
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMsg}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default SearchArea;
